import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { api, chatFileBlob } from '../../utils/api';
import { fmtDate, fmtDateTime, todayISO } from '../../utils/region';
import { TAB_GROUPS, DOC_GROUP, EMAIL_CAL_GROUP } from '../../tabPalette';
import { ACCOUNT_COLORS } from '../../utils/accountColors';
import { docNoSplitter, useDocSeries } from '../../utils/docSeries';
import { displayClientName } from '../../utils/displayName';   // a client's name as the lists show it (report 101b5c69)
import { cleanNo, fitsNo, noBoxW, noRuleFor, readNoFor, retypeNo } from '../../utils/docNumber';   // the wizard's Number rules (report 7dc8999f)
import { useInstallOffer, wantInstallHow, takeInstallHow, runningAsApp } from '../../utils/installApp';
import { useChatPush, pushHere } from '../../utils/chatPush';
import type { ChatPushButton } from '../../utils/chatPush';
import { CHAT_PATH, EMAIL_PATH, FILES_PATH } from '../App';
// The chat's own tab, opened by name so there is one of it (report 14dc81ac, over the machinery of report c2846304),
// and the note that brings a reduced window back up on the tab it was expanded from.
import { openChatTab, useChatBack } from '../../services/popupHome';
import DocNoLink from '../DocNoLink';
// Email's own Rectify (AI) window, for a photo sent here (report 6cc0de58): one window, handed the chat's own
// three server calls. canRectify says which pictures the AI reads — a JPEG or a PNG.
import RectifyModal, { canRectify } from '../EmailDetail/RectifyModal';
// The crop window for a picture still on its way (report 1e817873): a picture waiting in the strip above the typing
// box, or the photo waiting to be named in the document-upload group, is cut down in the browser before it is sent.
import ImageCrop, { canCrop } from './ImageCrop';
// The chats' pictures (report 42100db4): drawn in the list and at the head of the thread, set with My picture and Group picture.
import { ChatAvatar, PicButton, keepPic, usePic } from './ChatAvatar';
// The pane the Filing page draws a document in, and the one page-drawing call behind it — straight from the module
// it lives in now, so a chat does not pull the whole File Library in to draw a PDF (report 6cc0de58, opening #3).
import { PdfPreview, pdfPageDataUrl } from '../PdfPreview';
import { SimResults } from '../DocStorage/UploadWizard';   // the simulation checks' Results list, the wizard's own
import type { ChatConversation, ChatHit, ChatJobs, ChatLang, ChatMessage, ChatPassports, ChatPerson, ChatQuotes, ChatReacts, ChatTaxDocPage, ChatTaxIds } from '../../types';

// ── Internal chat, all three steps: text, files, photos and voice notes, notices (report 32621dcf) ───────────────
// "Make a WhatsApp/Telegram-like messaging system for any/all internal communications, where we can also create
// groups. Allow the users to add this to their dashboard without using one of their 12 alowable tabs." Answered by
// the reporter: text, files, photos and voice notes; admins alone make a group and add or remove people; one-to-one
// chats alongside groups; "chat button can be in the menu dock (top, side, or bottom) and opens a pop-up over the
// current page so that it doesn't use one of the 12 pages."
// This is that pop-up: no page of the system's own and nothing in the My Dashboard(s) picker. The chats are down the
// left — New chat for a one-to-one with anybody on staff, and for an admin New group and a member editor — and the
// thread of the one clicked fills the right. While it is open it asks the server again every few seconds, so a
// colleague's message arrives without anyone pressing anything.
// It is also the whole of the app a phone installs (report 8c5f3514): the one address of its own, /chat, hands this
// same window `app` and it fills the screen there instead of sitting over a page. See ChatApp beside this file, and
// `phone` below for the two-screen shape a narrow screen is given.
// Step 2 is the paperclip and the microphone at the foot of the thread: a file or a photo picked from the computer
// or pasted straight in — which waits above the typing box to be looked at and goes on Send (takeShots, sendShots)
// — or a voice note recorded here and then, each stored through the existing file
// storage and drawn in the thread where it was sent — a photo as a picture, which opens large over the window when
// it is pressed (ChatAttachment), a voice note as a player, a file as a line to press.
// Step 3 is the on-screen notices (ChatToast, beside this file) and the bell in this pop-up's header that turns them
// on and off. Two things here serve them: `startId`, so a card opens the pop-up on its own chat, and the count of
// open pop-ups below, so the cards keep quiet while the chat window itself is being read.
// Whose colour is whose went one step further after that (reports c05bc176 and b4806261): no words in this window
// are coloured for who wrote them any more — every name sits in a bubble of its own colour instead, and the chat
// rows down the left wear the last writer's bubble beside their unread count. See PersonBubble.
// Step 4 is what a message may now be written AS (report 67084d4c): bold, underlined, struck through, italic and
// the two kinds of list, written as light markers by the toolbar above the typing box and drawn by ChatWords; and
// a colleague named, offered by '@' in that box and drawn bold in their own colour wherever the words name them.
// Step 5 is FORWARDING (report 96e28b88): Forward beside Reply on a message, which ticks that message and opens a
// picker of the chats the reader is in; while the ticking is on, every message in the thread wears a tick box, so
// one forward can carry a whole run of them. It goes no further than this system — see ForwardTo below.
const POLL_MS = 5_000;        // how often an open pop-up asks again
const UNREAD_POLL_MS = 30_000;   // how often the button's red bubble asks
const MAX_REC_MS = 300_000;   // a voice note stops itself after five minutes, well under the 200 MB a message may carry
const MAX_BODY = 4000;        // one message, as the server holds it

// ── Emojis, and answering one message in particular (report 32621dcf, Calem: "Allow emojis and direct replies to
// messages" — asked which of the two was meant, the answer was "(c) both") ─────────────────────────────────────
// The emojis the picker offers, and no library for them: an emoji is only text, so pressing one puts that character
// into the message being typed — at the cursor, not at the end — or onto a colleague's message as an answer to it,
// and the browser's own font draws it. Typing one with the computer's own emoji keyboard still works exactly as it
// did; this is for the people who do not know that keyboard.
const EMOJI = [
  '👍', '👎', '👏', '🙏', '❤️', '🔥', '🎉', '✅',
  '❌', '⚠️', '❓', '❗', '😀', '😁', '😂', '🙂',
  '😉', '😍', '😅', '🤔', '😢', '😮', '😡', '😴',
  '💪', '👀', '🤝', '⭐', '📌', '📎', '⏰', '📄',
];
// The face on the two buttons that open the picker. It is a character, not one of the Tabler glyphs the rest of
// this file uses, for the same reason as the list above: the emoji font is the browser's own and always there,
// while the icon font is fetched from a CDN and nothing in this repo can say which faces it carries.
const SMILE = '🙂';
const EMOJI_COLS = 8;         // the picker's grid, so its width is EMOJI_COLS wide and the rest wraps
const QUOTE_CHARS = 120;      // of a quoted message's words — all a quote line shows, as the server also cuts it
const FLASH_MS = 1_600;       // how long a message stays marked after a quote has taken the reader to it

// ── A chat's row menu (report e307a908, Calem: "allow messages to be marked unread if the user has not yet responded
// but obviously the two blue ticks still show at the sender's side") ─────────────────────────────────────────────
// A right-click on a chat's row — a press held on a phone, which has no right button — opens Mark as unread, or Mark
// as read on a chat with something unread. See the block beside `back` inside ChatPanel.
const LONG_PRESS_MS = 500;    // how long a finger holds a row before its menu opens
const PRESS_SLACK = 8;        // pixels a held finger may drift; further is a scroll, not a press
const PRESS_CLICK_MS = 500;   // how long after the finger lifts the click a phone sends for it is still that press
const ROW_MENU_W = 170;       // the menu's least width, and how far it is kept inside the window's edges
const ROW_MENU_H = 44;

// ── Finding words said in a chat (report f57cc430, Calem: "Allow passport numbers to be found in chat as well") ──
// The search box over the chat list: it searches as you type, 300ms after the last key as every other search box in
// the system does since report 0c04879b, and what it finds takes the list's place until the box is emptied again.
// A press on a hit opens that chat ON the message — however far back it was said, since each hit comes with the
// window and the page the thread has to be asked for (server routes/chat.ts, /search) — and marks it there for a
// moment, exactly as a press on a quote does (goTo).
const FIND_MS = 300;          // after the last key, as every other search box in the system waits
const FIND_MIN = 2;           // one letter would match nearly every chat; the server holds the same line

// ── Changing what you have just said (report d5b1d1de, Calem: "Allow users to edit their messages for up to 15
// minutes after they submit/send") ───────────────────────────────────────────────────────────────────────────────
// Fifteen minutes from the send, and the sender's own typed words alone — the server holds the same line by its own
// clock, so this is only what the reader is shown: Edit stands in the ⋮ menu on their own message with the minutes
// still left on it, and goes from the menu as the last of them runs out. The count comes off a clock that ticks
// once a MINUTE while the window is open, not off each render, so a thread at rest redraws nothing.
const EDIT_WINDOW_S = 900;    // the fifteen minutes, as the server allows them
const MINUTE_MS = 60_000;
// The box the words are changed in (report 2fa9001f: "allow the edit window to be double its current width (as well
// as full height) for ease of editing"). It was 230px wide, 180 on a phone, and stopped growing at six lines, so
// a long message was changed inside a narrow box it scrolled in. It is now twice as wide at the least — the bubble
// round it let out past its usual 78% of the thread to hold it, but never wider than the thread itself — and as tall
// as every line of the words, until the bubble fills what the thread shows; only past that do they scroll inside it.
const EDIT_W = 460;           // the box's least width, twice its old 230…
const EDIT_W_PHONE = 360;     // …and on a phone twice its old 180

// ── A message read in another language (report 9b579ee4: "Allow users to translate a chat message from/to: English
// to Thai, Thai to English, English to Chinese, Chinese to English, Chinese to Thai, Thai to Chinese") ──────────────
// Translate stands in a message's ⋮ menu with the two of the three languages its words are not already in, and the
// answer is drawn inside the bubble under the words themselves, for this reader alone: nothing is stored and the
// message is never changed (server routes/chat.ts, the translate door, through Email's own translation). Which
// language the words are IN is read off their script by exactly the test the server makes (langOfWords there): Thai
// letters make them Thai, Chinese characters Chinese, anything else English. Any of either counts, as a Thai or Chinese
// message here routinely carries English names, numbers and terms; the baht sign and Thai digits are no letters and
// count for nothing.
const CHAT_LANGS: ChatLang[] = ['en', 'th', 'zh'];
const THAI_LETTER = /[\u{0E01}-\u{0E3A}\u{0E40}-\u{0E4E}]/gu;
const CJK_CHAR = /[\u{3400}-\u{4DBF}\u{4E00}-\u{9FFF}]/gu;
const langOfWords = (s: string): ChatLang => {
  const th = (String(s || '').match(THAI_LETTER) || []).length;
  const zh = (String(s || '').match(CJK_CHAR) || []).length;
  return !th && !zh ? 'en' : th >= zh ? 'th' : 'zh';
};
/** Words to translate at all: a letter somewhere outside a web address — so an emoji, a number, a bare link, and a
 *  photo, a file or a voice note sent with no caption offer no Translate. The server holds the same line. */
const hasWords = (s: string): boolean => /\p{L}/u.test(String(s || '').replace(/https?:\/\/\S+/g, ' '));
/** Each translation made this session, by message and language, with the words it was made from: a message changed
 *  since (report d5b1d1de) no longer matches them and is asked for afresh. Held outside the window, so closing the
 *  chat and opening it again costs no second translation. */
const TRANSLATED = new Map<string, { src: string; text: string; from: ChatLang }>();
/** One bubble's translation as it stands — being made, made, or not to be had — and the words and language it is for. */
type Translation = { to: ChatLang; src: string; from: ChatLang; text: string; state: 'busy' | 'done' | 'failed' };
/** The room the ⋮ menu needs above its button at its tallest — Translate with its two languages, Edit and Delete. A
 *  message nearer the top of the thread than that has its menu open downwards, where the thread does not cut it off. */
const MENU_ROOM = 160;

// ── Is a chat pop-up on screen? (step 3) ─────────────────────────────────────────────────────────────────────────
// The notices keep quiet while the chat window is open — whatever arrives is already being read in it, and a card
// over it would be noise — so each open pop-up counts itself here while it is mounted and tells whoever is watching.
// Closing one also sends CHAT_READ_EVENT, so every red bubble on the page asks again at once: a chat read in a
// pop-up opened from a card empties the count without waiting for the next 30-second ask.
export const CHAT_READ_EVENT = 'slf-chat-read';
let panelsOpen = 0;
const panelWatchers = new Set<(n: number) => void>();
function countPanels(delta: number): void {
  panelsOpen += delta;
  panelWatchers.forEach(f => f(panelsOpen));
}
/** True while any chat pop-up is open in this browser tab. */
export function useChatPanelOpen(): boolean {
  const [n, setN] = useState(panelsOpen);
  useEffect(() => {
    panelWatchers.add(setN);
    setN(panelsOpen);                       // one may have opened between the first render and here
    return () => { panelWatchers.delete(setN); };
  }, []);
  return n > 0;
}

/** The chat button's red bubble: everything unread, asked for at once, every 30 seconds after, and the moment any
 *  pop-up closes. `refresh` is for the owner of a pop-up to ask as it closes, which does the same. */
export function useChatUnread(): { unread: number; refresh: () => void } {
  const [unread, setUnread] = useState(0);
  const stopped = useRef(false);
  const ask = useCallback(async () => {
    try { const r = await api.chatUnread(); if (!stopped.current) setUnread(Number(r?.total) || 0); }
    catch { /* the next ask retries */ }
  }, []);
  useEffect(() => {
    stopped.current = false;
    ask();
    const iv = setInterval(ask, UNREAD_POLL_MS);
    window.addEventListener(CHAT_READ_EVENT, ask);
    return () => { stopped.current = true; clearInterval(iv); window.removeEventListener(CHAT_READ_EVENT, ask); };
  }, [ask]);
  return { unread, refresh: ask };
}

// ── A phone-shaped screen (report 8c5f3514) ──────────────────────────────────────────────────────────────────────
// Chat is now also an app a phone installs (ChatApp, beside this file), and ONE window serves both: this pop-up is
// 1081px wide with the chats down the left of the thread, which on a phone would be two columns of about 200px each.
// So every measurement below that reads `phone` lays the very same parts out as two screens instead — the chat list,
// then the thread of the one chosen, with a back arrow at the head of the window.
// The test is the width of the screen alone, 680px, the breakpoint global.css already turns the Due Diligence form
// at — never whether this is the app or the pop-up. So the desktop pop-up is exactly what it always was, and a
// narrow window is a narrow window wherever it is opened.
const PHONE_W = '(max-width: 680px)';
export function usePhone(): boolean {
  const [on, setOn] = useState(() => !!window.matchMedia?.(PHONE_W).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(PHONE_W);
    if (!mq) return;
    const read = () => setOn(mq.matches);
    read();                                   // the phone may have been turned between the first render and here
    mq.addEventListener('change', read);
    return () => mq.removeEventListener('change', read);
  }, []);
  return on;
}
// ── A phone's names and words, one size up (report 999f2fe7: "Increase the username and message text on a phone by
// 1 pt") ─────────────────────────────────────────────────────────────────────────────────────────────────────────
// A phone's two screens were drawn at the desk's own sizes. There, and only there, four things are PHONE_UP larger:
// the words of a message (12.5 → 13.5) and the name bubble at the start of them (10.5 → 11.5), and in the chat list
// each chat's name (12.5 → 13.5) and the last thing said in it (11.5 → 12.5). This window's sizes are pixels, so the
// "1 pt" asked for is read as one pixel. The room round them is left as it was — the padding, the name bubble's own
// 20px height — and a wider screen sees exactly what it always did.
const PHONE_UP = 1;
// ── The words inside a chat, one size up on every screen (report d4dbcbe8: "make the text size inside the chat 1pt
// larger") ────────────────────────────────────────────────────────────────────────────────────────────────────────
// The same pixel for a point as PHONE_UP, but on a desk, a laptop and an iPad as well, with a phone's pixel still
// on top. Three things in the open chat take it: the words of a message (12.5 → 13.5, a phone's 14.5) — and with
// them a caption and a file's name, which take their size from the words — the name bubble at the start of them
// (10.5 → 11.5, a phone's 12.5), and the typing box at the foot of the chat (12.5 → 13.5, a phone's 16 → 17).
// Everything else keeps its size: the head of the chat, the chat list, the menus and the icons, and the smaller
// lines in and under a bubble — a quote, who a forward came from, the time. The room round them is left as it was,
// as PHONE_UP left it: the bubble's padding, the name bubble's 20px height.
const TEXT_UP = 1;

// ── How wide the chat list is (report 8e638d38: "Allow users on PC, laptop, and iPad to move the left column to be
// larger (by up to 15%), which in turn increases the text size in that column") ────────────────────────────────────
// The chats down the left were 290px and nothing could change that. The column's right-hand edge is now a handle that
// a mouse, a finger or a pen drags out to 15% wider, 333.5px, and every size in the column grows by that same width ÷
// 290: the chats' names and last messages, their icons, the bubble and the red count at the end of each row, New chat
// and New group, and the room around them. The sizes are multiplied one by one rather than the column being given a
// CSS zoom, which would widen the column a second time over. The thread keeps whatever width is left.
// The width is the PERSON's own (opening #2: "Keep that size for the next time the user logs in as well"): kept on the
// server against their login (routes/chat.ts /list-width), so it follows them to another computer or the iPad and
// outlives cleared storage. This browser keeps a copy as well, which draws the window's first paint until the server's
// width arrives and wins. A double-click on the handle puts the list back to 290px. A phone has no columns (usePhone,
// above): its list is the whole screen, exactly as before.
const LIST_W = 290;                   // the list's own width, and the narrowest it goes
const LIST_W_MAX = LIST_W * 1.15;     // 333.5 — "by up to 15%"
const LIST_W_KEY = 'mf_chat_list_w';
/** A width the list may be given: whole pixels, from its own 290 up to 15% more. */
const listWidth = (w: number): number => Math.min(LIST_W_MAX, Math.max(LIST_W, Math.round(w)));
function readListW(): number {
  try { const w = Number(localStorage.getItem(LIST_W_KEY)); return w ? listWidth(w) : LIST_W; } catch { return LIST_W; }
}
/** This browser's copy, for the next window's first paint. */
function keepListW(w: number): void {
  try { if (w === LIST_W) localStorage.removeItem(LIST_W_KEY); else localStorage.setItem(LIST_W_KEY, String(w)); }
  catch { /* a private window: this visit keeps the width, the next starts at the server's */ }
}
/** A width the hand chose: this browser's copy and the person's own on the server, where 290 empties what was kept. */
function saveListW(w: number): void {
  keepListW(w);
  api.chatSetListWidth(w).catch(() => { /* this browser keeps it; the next drag tries the server again */ });
}

/** What a person is called in a list: their nickname, else their name, else their address. */
const who = (p: ChatPerson | null | undefined): string => (p ? (p.nickname || p.name || p.email) : '');
/** A person's own colour, as it is everywhere else in SLF (report 32621dcf, Calem: "Give each user the same
 *  color as their username in the SLF system") — their Team calendar's colour, which comes down with them on
 *  every chat answer. It draws a colleague's NAME: the name of a one-to-one chat, in the left-hand list and at
 *  the head of the thread (report 2ad9e77c, Calem: "The user's name in their user color, just not the chat
 *  messages themselves"), and the name over a quoted message, with the bar down that quote's side. It never
 *  draws the WORDS of a message — those are black, and whose they are is the bubble at the start of the line
 *  (PersonBubble, below). Nobody to colour — a colleague removed from the system — keeps the ordinary colour
 *  of the line it is on, `base`. */
const hue = (p: ChatPerson | null | undefined, base = 'var(--text-primary)'): string => p?.color || base;

// ── A person's colour, in a bubble at the start of the line (reports c05bc176 and b4806261, Calem:
// "Remove the user colors from the chat window and instead change all text to black. However add the user's
// bubble and color to the beginning of the line of text", and "In the chat window when a new message comes in
// … in the left-hand column, show how many notifications there are in a round bubble and show the last
// username's bubble") ────────────────────────────────────────────────────────────────────────────────────────
// Every name in this pop-up was drawn in the person's own colour, and since report 32621dcf so were the words
// of their messages — which left a thread with a colour on every line and nothing in it black, and the chat
// list reading as a colour chart rather than as a list. The colour has one place of its own now: this bubble,
// and nothing else in the window is coloured for whose it is — the words are the ordinary black of the rest of
// the system, and only a document link keeps a colour of its own, because a quotation, invoice or receipt
// number is blue and a job number pink wherever in SLF it is written.
// The bubble is the one Calem already reads on Active and Closed Jobs (components/Jobs ResourceChips, his
// "resource bubble"): the person's colour for the name, that same colour washed to '18' for the fill, a '55'
// edge, 20px high and fully round. Jobs gives every chip one width so the columns after them line up down the
// page; a chat line has no column to meet, so a bubble here is only as wide as the name inside it.
// Nobody to colour — a colleague taken off the system, whose name still stands on what they wrote — gets the
// plain grey bubble Jobs' "+N" chip wears, so no line ever loses who wrote it.
const BUBBLE_H = 20;
// What a bubble may take of a chat row in the left column, so a long name cannot push that chat's own name out
// of the row. A name past it ends in "…" inside its bubble, and the whole name is in the hover.
const ROW_BUBBLE_MAX = 92;
// A chat's picture (report 42100db4): its side in a row of the list (grown with the column, as the rest of the row is)
// and at the head of the open thread.
const PIC_ROW = 28;
const PIC_HEAD = 32;
// `scale` grows a bubble in the chat list with its widened column (report 8e638d38, LIST_W); 1 everywhere else.
// `up` is pixels added to the name alone — the thread's, on every screen (report d4dbcbe8, TEXT_UP) and a phone's on
// top (report 999f2fe7, PHONE_UP) — so the bubble keeps its height and padding; 0 everywhere else.
function PersonBubble({ p, name, scale = 1, up = 0, style }: { p?: ChatPerson | null; name?: string; scale?: number; up?: number; style?: React.CSSProperties }) {
  const label = who(p) || String(name || '');
  if (!label) return null;
  const c = p?.color || '';
  return (
    <span title={label}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: BUBBLE_H * scale, maxWidth: '100%',
        padding: `0 ${7 * scale}px`, borderRadius: 999, fontSize: 10.5 * scale + up, fontWeight: 700, whiteSpace: 'nowrap', verticalAlign: 'middle',
        color: c || 'var(--text-secondary)', background: c ? `${c}18` : 'var(--bg-app)',
        border: `1px solid ${c ? `${c}55` : 'var(--border-strong)'}`, ...style,
      }}>
      {/* A name too long for the room it has ends in "…" in a span of its own: text-overflow works on a block,
          not on the flex box around it, and that span keeps the font's own line height because it clips. */}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </span>
  );
}
// ── Sent, and read (report 29486dfd) ────────────────────────────────────────────────────────────────────────────
// Calem: "If the recipient in a one-on-one chat has received, opened, and read the messages, show two blue ticks -
// same as WhatsApp." So: one grey tick on my own message says it has gone, and two blue ones say the person on the
// other side has opened the chat and read it. Nothing is asked of them and nothing new is stored — their own read
// watermark, which has always emptied their unread count when they open the chat, is simply told to the sender now
// (server routes/chat.ts otherRead) — and the thread already asks again every few seconds, so a tick turns blue by
// itself while the window is open.
// On the reader's OWN messages only, and in a ONE-TO-ONE chat only: a group has many readers and no single answer
// to give, so a group's bubbles are left exactly as they are.
// The blue is the app's own blue — the blue of the quotation pages and of a blue mailbox — and not this file's
// --accent, which is the app's green: green ticks would say nothing about having been read.
const READ_BLUE = ACCOUNT_COLORS.blue.accent;
// `scale`: the chat list's widened column (report 8e638d38), as PersonBubble's.
function ChatTicks({ read, scale = 1, style }: { read: boolean; scale?: number; style?: React.CSSProperties }) {
  const { t } = useTranslation();
  const label = t(read ? 'chat_read' : 'chat_sent');
  return (
    <span title={label} aria-label={label} role="img"
      style={{ flexShrink: 0, whiteSpace: 'nowrap', color: read ? READ_BLUE : 'var(--text-tertiary)', ...style }}>
      {/* Tabler's own double tick, the two checks overlapping, against the single one for a message not yet read. */}
      <i className={`ti ti-${read ? 'checks' : 'check'}`} style={{ fontSize: 12 * scale, verticalAlign: -1 }} aria-hidden />
    </span>
  );
}
/** The i18n key for a message that is not plain text — step 2's three kinds. The notices name them the same way. */
export const kindKey = (k: ChatMessage['kind']): string =>
  k === 'image' ? 'chat_kind_image' : k === 'voice' ? 'chat_kind_voice' : 'chat_kind_file';
/** A file's size, as the File Library writes it. */
const fmtSize = (n?: number) => { if (!n) return ''; if (n < 1024) return `${n} B`; if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`; return `${(n / 1048576).toFixed(1)} MB`; };
/** A recording's length while it is being made: minutes and seconds. */
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
/** What to call a picture off the clipboard (report 32621dcf, Calem: "Allow images and screenshots to be copied
 *  and pasted directly into the chat window"). A copied screenshot arrives with no name of its own, so one is
 *  made for it from the moment it was pasted and from what it is — pasted-image-1762500000000.png — and that is
 *  the name the chat shows and the name it saves under. The four kinds a browser can draw are named properly;
 *  anything else keeps its own sort, and the server calls such a one a file, as it does for a picked one. */
const PASTE_EXT: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp' };
const pastedName = (mime: string): string => {
  const kind = String(mime || '').toLowerCase().split(';')[0].trim();
  const ext = PASTE_EXT[kind] || (kind.split('/')[1] || '').replace(/[^a-z0-9]/g, '') || 'png';
  return `pasted-image-${Date.now()}.${ext}`;
};
/** One pasted picture, held above the typing box until Send (report 32621dcf, Calem: "Show the screenshot or
 *  copy and paste image in the chat window before posting it directly to the chat"): the bytes themselves, the
 *  name made for them and an object URL, so the strip and the large preview draw it from the computer's own
 *  memory — nothing has been to the server yet, and nobody else in the chat can see it.
 *  Report 1e817873 ("An image that is loaded via paperclip or copy and paste to any group or chat should have a crop
 *  tool"): a picture picked with the paperclip now waits here too, under its own file name, and any of them can be
 *  cropped before Send (ImageCrop). A crop takes the place of the bytes, and so of the object URL drawn from them. */
type Shot = { id: string; blob: Blob; name: string; url: string };
const SHOT_THUMB = 56;        // the side of a thumbnail in the strip above the typing box
// The small dark buttons on a thumbnail: the × at its top right and, on a picture that can be cropped, Crop under it
// at the bottom right (report 1e817873).
const SHOT_DOT: React.CSSProperties = { position: 'absolute', right: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, padding: 0, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11, lineHeight: 1, cursor: 'pointer' };

// ── A document waiting to be named, in the document-upload group (report 13fbee78) ──────────────────────────────
// Calem: "allow users to upload documents, then use AI to analyze them and suggest their name in keeping with the
// naming protocol in our documents. Make a suggestion below the upload and ask them to verify and/or edit that
// suggestion. Then once the suggestion is confirmed, upload that document to the drop folder."
// So in the ONE group an administrator has marked (Admin → Chat), an attached image or PDF does not go straight
// into the thread: it waits here, under the typing box, with the AI's name in a box to be checked or changed, and
// Confirm both sends the message and files a copy into the Drop Folder under that name. Nothing has left this
// computer at that point but a copy sent to be read.
// `cropped` (report 1e817873): a photo cut down by Crop since it was picked — `blob` is then the cut-out, and the strip
// says so beside the name.
type DocHold = { blob: Blob; fileName: string; cropped?: boolean };
// "<Type> (<Person>) <Company> <YYYY-MM-DD>.<ext>" — the Type before the first bracket, a person inside it, an
// optional company, and the date closing the name with nothing after it but the extension. A MIRROR of the
// server's own reading of the protocol (server/src/services/dropFile.ts PROTOCOL + protocolType), which stays the
// authority and refuses a name this lets through; it is here so a hand-edit that breaks the shape is said so
// under the box at once, before the document goes anywhere. Keep the two the same.
const DOC_PROTOCOL = /^([^()]+)\(([^()]*)\)(.*)\s(\d{4}-\d{2}-\d{2})(\.[A-Za-z0-9]{1,10})?$/;
const protocolOk = (name: string): boolean => {
  const m = DOC_PROTOCOL.exec(String(name || '').trim());
  if (!m || !m[2].trim()) return false;
  const at = Date.parse(`${m[4]}T00:00:00Z`);
  if (isNaN(at) || new Date(at).toISOString().slice(0, 10) !== m[4]) return false;   // 2026-02-30 is no date
  const type = m[1].replace(/\s+/g, ' ').replace(/[\s\-–—_.,;:]+$/, '').trim();      // dropFile's own tidy-up
  return !!type && /\p{L}/u.test(type);
};
// Calem (report 595aad91): "Change the naming box to show four separate boxes … rather than one text box".
// One box for the whole name asked the sender to carry the protocol's punctuation in their head — the brackets
// round the client, the order of the parts, the date last. The name is now held as its FOUR parts, each in its
// own labelled box, and the protocol's shape is put back round them on Confirm (docFileName). The extension is
// never one of the boxes: it belongs to the file that was picked and is carried along untouched.
// Report 7dc8999f added the document's own number, the Upload Document wizard's Number box (report c8ad7a51): held
// here as typed or read, and used only as far as the Type then in its box allows (retypeNo).
type DocParts = { type: string; person: string; company: string; date: string; number: string };
const NO_PARTS: DocParts = { type: '', person: '', company: '', date: '', number: '' };
/** The '.pdf' of a file's name, '' when it has none. */
const extOf = (fileName: string): string => {
  const m = /(\.[A-Za-z0-9]{1,10})$/.exec(String(fileName || '').trim());
  return m ? m[1] : '';
};
/** The AI's suggestion taken apart into the four boxes, by the very reading the name is judged by. A suggestion
 *  that does not follow the protocol is not thrown away: the whole of it goes into Type, where it can be read
 *  and moved into the right boxes by hand. */
const splitDocName = (name: string, fallbackExt: string): { parts: DocParts; ext: string } => {
  const whole = String(name || '').trim();
  const m = DOC_PROTOCOL.exec(whole);
  if (!m) return { parts: { ...NO_PARTS, type: whole }, ext: fallbackExt };
  return { parts: { ...NO_PARTS, type: m[1].trim(), person: m[2].trim(), company: m[3].trim(), date: m[4] }, ext: m[5] || fallbackExt };
};
/** The four boxes written back out as the protocol's one name — the company left out when its box is empty,
 *  which is what the protocol says of a document belonging to a person alone. A type that carries a number of
 *  its own has it straight after the type word (report 7dc8999f), as the Upload Document wizard names a file:
 *  "Chanote 53658 (First Last) 2017-08-31.pdf". */
const docFileName = (p: DocParts, ext: string): string => {
  const company = p.company.trim();
  const no = retypeNo(p.number, p.type);
  return `${p.type.trim()}${no ? ` ${no}` : ''} (${p.person.trim()})${company ? ` ${company}` : ''} ${p.date.trim()}${ext}`;
};
/** Confirm is offered once the three required boxes are filled; the company's is the one that may stay empty. */
const docPartsReady = (p: DocParts): boolean => !!(p.type.trim() && p.person.trim() && p.date.trim());
// The reporter (report 972661d6): "if you aren't sure, highlight the border of that selection in orange and ask the
// user to check but it is better to fill in something rather than nothing (where possible)." A box the server filled
// with a guess — off the file's own name, or today's date, rather than off the document itself, or off the document by
// an AI read that was not sure of it (2nd opening) — is edged in orange with a line under it asking for it to be
// checked, until it is changed by hand. A guess never holds Confirm back: only an empty Type, Client Name or date does.
type DocPart = keyof DocParts;
const DOC_PART_KEYS: DocPart[] = ['type', 'person', 'company', 'date'];   // the four, as a simulation check compares them
const DOC_GUESS = '#EA580C';
const DOC_GUESS_EDGE: React.CSSProperties = { border: `1px solid ${DOC_GUESS}`, boxShadow: `0 0 0 1px ${DOC_GUESS}` };
const DOC_GUESS_HINT: React.CSSProperties = { display: 'block', marginTop: 2, fontSize: 10, color: '#B45309' };
// The reporter (report 98453bac, opening #2): "Make the company field a drop-down box (populated with all of our
// slf system companies) with an 'Other' text box inclusion". Typed free-hand, the same client's company reached
// the name a different way every time — the bare name, the name with its legal suffix, a mis-type, another case —
// and Our Documents then held one company under several names. The Company box is now PICKED from the very list
// the Upload Document wizard picks from (GET /doc-storage/companies: the client master's own companies, bare
// names, A→Z), so a document named in the chat carries the very name it would in the wizard. The first entry is
// blank — the protocol lets a document belong to a person alone — and the last is 'Other…', which brings the
// typing box back for a company the client master has not got yet. Nothing of the name itself changes: what is
// picked goes into the same box of the name as what used to be typed there.
// Opening #3 ("Allow users to search for the company name") kept all of that and made the list searchable, since
// 130-odd companies cannot be scrolled towards: DocCompanyPicker, below.
/** What the AI read lined up with that list, by the wizard's own reading (UploadWizard's matchToList): the
 *  list's own entry whatever case or extra words the document printed it with, '' when the list has nothing
 *  like it — which drops the read into the Other box as it was read. Keep the two readings the same. */
const matchCompany = (raw: string, list: string[]): string => {
  const v = String(raw || '').trim().toLowerCase();
  if (!v) return '';
  return list.find(c => c.toLowerCase() === v)
    || list.find(c => c.toLowerCase().includes(v) || v.includes(c.toLowerCase()))
    || '';
};
/** Which files the AI can be asked to name: the three sorts the reader takes (services/vision.ts). Anything else
 *  is sent as it always was, with a line saying no name could be suggested for it. */
const DOC_READABLE = /^(application\/pdf|image\/png|image\/jpe?g)$/i;
const canReadDoc = (f: File): boolean =>
  DOC_READABLE.test(String(f.type || '').split(';')[0].trim()) || /\.(pdf|png|jpe?g)$/i.test(f.name || '');

// ── The numbers inside a message, made live (report 32621dcf, Calem: "When a user shares a job or a quotation,
// invoice, or receipt number - allow other users to click on this information and go directly to that
// job/quotation/invoice/receipt") ───────────────────────────────────────────────────────────────────────────────
// A message used to be drawn as the plain words it is, so a number shared in a chat led nowhere. ChatWords walks
// those words once and draws the kinds of number the office shares as each is drawn everywhere else in SLF:
// · a quotation, an invoice or a receipt — handed to DocNoLink, which knows the workspace's own prefixes
//   (utils/docSeries.ts, Admin → Companies), draws the number in the Quotation / Invoice / Receipt blue and on a
//   click opens that document's own form over the chat (report b4cccf02; the forms are drawn by DocNoHost, which
//   is mounted in App above this pop-up, so closing the chat never takes one with it). The number keeps that blue
//   where the words around it are black: every QO / IV / RE number in the system is blue and clickable, and it is
//   a document that is being named, not a person (reports c05bc176, b4806261 — see PersonBubble). Without the
//   Quotation / Invoice / Receipt module it is blue and opens nothing, as it is everywhere else.
// · a job — bare digits, so the server says which of them are jobs at all (`jobs`, from the chat's own answers,
//   its own world and nothing deleted) and those alone are drawn in the Active / Closed Jobs pink and open the
//   job in a new browser tab. Anything else stays words, so 60000 written as a sum is not a link. In the installed
//   chat app a new browser tab would mean leaving the app for the phone's browser, so there `onJob` is handed in
//   and the job opens OVER the chat instead (report 8c5f3514; ChatApp draws it).
// · a tax ID — thirteen bare digits, so again only the server can say which of them are a client's at all
//   (`taxIds`, the chat's own answers; report 32621dcf, Calem: "Allow users to paste or put in a tax ID number for
//   company documents and for those documents to come up automatically. For example 0845557000162 (Samui Law
//   Firm)"). One that names a client is drawn in the amber-brown of the Document top-menu button — the colour of
//   the archive it opens — and a press lists that client's company documents from Our Documents over the Chat
//   window (TaxDocs below). Every staff reader, not management alone: browsing and downloading Our Documents is
//   open to every login (routes/files.ts), and an "Admin only" document is left out of the list by the server.
// · a passport number — letters and digits, PB2713441 (report f57cc430, opening #2, Calem: "I want you to find
//   the passport number in our documents under Identification - Passport as well do the same as tax ID, quotation
//   number, etc. Highlight it in the filing/tracking colour and allow a quick launch of that passport in a new
//   tab"). No shape can tell one from a reference number, so again the server says which of them is a passport in
//   Our Documents at all (`passports`, the chat's own answers) and those alone are drawn — in the same amber-brown
//   a tax ID wears, the colour of the archive they both come out of — with the person's name in the hover. A press
//   opens that passport in a browser tab of its own. Every staff reader, as a tax ID is for every staff reader;
//   an "Admin only" passport is not in the answers at all, so it is never even named.
// Whose number it is used to be in the hover alone (a passport, a tax ID) or nowhere at all (a job), so a reader
// had to point at a number to learn what it was. Now, on the message itself, each of those three is followed by
// whose it is, in the plain --text-primary of the words around it and pressing nothing — the number alone keeps
// its colour and its press (report 9c266d45: "include some basic information with that number … only the number
// (not the name etc) needs to be highlighted in the color related to the top menu"): "(Passport - <the person>)",
// "(Company Documents - <the client>)", "(Job - <the client>)", the client as the lists show one and a job's as
// the Active Jobs list's Client – Company column names it (`client`, routes/chat.ts jobsIn). It is drawn, never
// stored: the message keeps the words exactly as they were typed, so one sent before this, an edited one and a
// translation carry it too. The one-line quote strip above an answer has no room for it and goes without, and a
// QO / IV / RE number names its own kind and stays as it was.
// A click stops where it is: the pop-up closes on a press outside its window, and a number is inside it.
// Used for a text message's words and for the caption under a photo, a voice note or a file — the two places a
// message's words are drawn. The chat list's last-message line and the notices' cards stay plain text: a link in
// one of those would fight the card's own press, which opens the chat.
// ⛔ The thirteen-digit test comes FIRST in this one regex: #?\d{5,6} would otherwise eat the head of a tax ID
// (084555 of 0845557000162) and the rest of it would never be looked at. The passport token comes ahead of
// #?\d{5,6} for the same reason — the digits of PB2713441 would otherwise be read on their own, and the letters
// that make it a passport number left outside the link. It is the SAME shape the server looks for (routes/chat.ts
// PASSPORT_RX), so the token drawn here is the token that was asked about: one to three letters and the five to
// nine digits behind them, ten characters at the most.
const NUM_IN_TEXT = /\d{13}|\b[A-Za-z]{1,3}\d{5,9}\b|#?\d{5,6}/g;
const PASSPORT_CHARS = 10;
const isDigit = (c: string): boolean => c >= '0' && c <= '9';

// ── Light formatting, and a colleague named in a message (report 67084d4c, Calem: "If anyone mentions a user's
// name in the chat, highlight their name in bold and their username in color. Also allow the following functions
// on all chat text: Bold Underline Strikethrough Italic Bullet points Numbered points") ─────────────────────────
// A message is the plain text it always was, start to finish: the toolbar above the typing box writes a few light
// markers round what is selected, and the server keeps the words exactly as they were typed, markers and all.
// Nothing here sends or stores HTML, so a message can never draw anything its sender did not type.
//   **bold**   __underlined__   ~~struck through~~   _italic_   '- ' a bullet point   '1. ' a numbered point
// ChatWords draws those as <strong>, <u>, <s>, <em>, <ul> and <ol> around the very same walk that makes a job, a
// document or a tax number live, so a number inside formatted words is still the link it is everywhere else.
// The two underscore markers count only at the edge of a word, so a file name written in a message
// (my_file_name.pdf) stays the name it is instead of going half into italics.
// A colleague NAMED is the one coloured thing a message's own words carry: '@' in the typing box offers the staff
// list and puts "@Name" in, and the thread draws that name — typed with the '@' or simply written out — bold in
// that person's own colour, the colour their bubble wears. Every other word stays --text-primary, as reports
// c05bc176 and b4806261 asked (PersonBubble above); a name is the one exception the reporter asked for.
type MarkTag = 'strong' | 'u' | 's' | 'em';
const MARKS: { mark: string; tag: MarkTag; word: boolean }[] = [
  { mark: '**', tag: 'strong', word: false },
  { mark: '__', tag: 'u', word: true },       // '__' before '_', so the longer marker is the one meant
  { mark: '~~', tag: 's', word: false },
  { mark: '_', tag: 'em', word: true },
];
const LIST: React.CSSProperties = { margin: '3px 0', paddingLeft: 20 };   // as every other list in SLF (global.css)
const BULLET = /^[ \t]*-[ \t]+(.*)$/;             // '- a bullet point'
const NUMBERED = /^[ \t]*\d{1,3}\.[ \t]+(.*)$/;   // '1. a numbered point'
const isWordChar = (c?: string): boolean => !!c && /[\p{L}\p{N}]/u.test(c);
/** The marker that opens at `i`, and where it closes; null when none does, so `i` is an ordinary character. */
function markAt(s: string, i: number): { mark: string; tag: MarkTag; close: number } | null {
  for (const m of MARKS) {
    if (!s.startsWith(m.mark, i)) continue;
    if (m.word && isWordChar(s[i - 1])) continue;
    const close = s.indexOf(m.mark, i + m.mark.length);
    if (close < 0 || close === i + m.mark.length) continue;             // never closed, or nothing inside it
    if (m.word && isWordChar(s[close + m.mark.length])) continue;
    return { mark: m.mark, tag: m.tag, close };
  }
  return null;
}
const inTag = (tag: MarkTag, key: string, kids: React.ReactNode[]): React.ReactNode =>
  tag === 'strong' ? <strong key={key}>{kids}</strong>
    : tag === 'u' ? <u key={key}>{kids}</u>
      : tag === 's' ? <s key={key}>{kids}</s>
        : <em key={key}>{kids}</em>;
/** One stretch of words with its markers drawn, nested ones and all; `leaf` draws each plain bit between them. */
function markedUp(s: string, leaf: (text: string, key: string) => React.ReactNode[], key: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let at = 0;
  for (let i = 0; i < s.length; i++) {
    const m = markAt(s, i);
    if (!m) continue;
    if (i > at) out.push(...leaf(s.slice(at, i), `${key}-${at}`));
    out.push(inTag(m.tag, `${key}-m${i}`, markedUp(s.slice(i + m.mark.length, m.close), leaf, `${key}-m${i}`)));
    i = m.close + m.mark.length - 1;   // the loop's own ++ steps past the closing marker
    at = i + 1;
  }
  if (at < s.length) out.push(...leaf(s.slice(at), `${key}-${at}`));
  return out;
}
/** A message as plain words, the markers taken off: for the one line under a chat's name in the left column,
 *  which shows words rather than drawing them. What is stored and sent is untouched, as ever. */
const plainWords = (s: string): string => String(s || '')
  .replace(/\*\*(.+?)\*\*/g, '$1')
  .replace(/~~(.+?)~~/g, '$1')
  .replace(/(^|[^\p{L}\p{N}_])__?([^\s_][^_]*?)__?(?=[^\p{L}\p{N}_]|$)/gu, '$1$2')
  .replace(/^[ \t]*(?:-|\d{1,3}\.)[ \t]+/gm, '');
/** Which names a message may carry and in what colour: one regex of every staff name, the longest first, and
 *  their colours keyed by the name in lower case. null = the lists have yet to land, so nothing is coloured. */
type Mentions = { re: RegExp; colors: Record<string, string> } | null;
// The toolbar over the typing box: what each button writes round what is selected, the Tabler face it wears and
// the key of its hover. Bold, Underline and Italic are ⌘/Ctrl+B, U and I in the box as well.
const TOOLS: { icon: string; key: string; mark?: string; list?: 'ul' | 'ol' }[] = [
  { icon: 'bold', key: 'chat_bold', mark: '**' },
  { icon: 'underline', key: 'chat_underline', mark: '__' },
  { icon: 'strikethrough', key: 'chat_strike', mark: '~~' },
  { icon: 'italic', key: 'chat_italic', mark: '_' },
  { icon: 'list', key: 'chat_bullets', list: 'ul' },
  { icon: 'list-numbers', key: 'chat_numbers', list: 'ol' },
];
// An '@' being typed: one at the start of the message or after a space, and what has been typed since. A second
// '@' or a space ends it, so a name chosen from the list — "@Ann Smith " — stops offering the list after it.
const AT_TOKEN = /(?:^|\s)@([^\s@]{0,24})$/;
const AT_MAX = 8;             // how many of the staff the list offers at once; typing narrows it

// ── How large an emoji is drawn (report ffe39a2e, Calem: "When somebody posts an emoji message by itself, make it
// 120% larger than the current reply to or include in message emoji. Also increase the current reply-to or
// include-in-message emoji size by a further 15%") ──────────────────────────────────────────────────────────────
// An emoji was drawn at exactly the size of the words around it, so one sent on its own — which is the whole of
// what that message says — read no larger than one tucked inside a sentence. Now, in size alone:
// · an emoji among words is 15% past those words (EMOJI_EM), wherever a message's words are drawn — the thread, the
//   caption under a photo, a voice note or a file, a quote of a message and a translation — and the words
//   themselves keep the size they have always had;
// · an emoji put ON a message, in the chip under the bubble, is that same 15% larger (CHIP_EMOJI);
// · a message that is nothing but emoji is 120% larger again (EMOJI_SOLO_EM = EMOJI_EM × 2.2): there are no words
//   beside it for the size to fight with, so the picture IS the message. The bubble keeps the padding it has and
//   grows around the taller line, as it does around any tall line.
// Nothing about the message itself changes — it is stored and sent exactly as it was typed, and this is only its
// size on the reader's screen. The chat pop-up and the installed chat app are one and the same window, so both
// follow from here.
// A RUN of emoji is kept whole inside ONE span: the zero-width joiner that binds a family or a profession into a
// single picture, a skin tone, a flag's two halves and the FE0F that turns a text character into an emoji all
// belong to the run, and a span around each piece on its own would break such a sequence back into its halves.
const EMOJI_EM = 1.15;
const EMOJI_SOLO_EM = 2.53;   // EMOJI_EM × 2.2, the 120% larger again
const CHIP_EMOJI = 13.8;      // the chip's own 12, × EMOJI_EM
// What counts as an emoji at all: a character the fonts draw as one all by itself (👍 ⭐ ✅ ⏰ — every emoji the
// picker offers and every one an emoji keyboard types), or one that draws as plain text until an FE0F follows it
// (❤️ ⚠️). So © ® ™ and the arrows, which only ever stand as text in the office's messages, keep the size of the
// words they sit in.
const EMOJI_RUN = /(?:\p{Emoji_Presentation}|\p{Emoji}\u{FE0F})(?:[\u{200D}\u{FE0F}\u{20E3}\u{E0020}-\u{E007F}]|\p{Emoji_Presentation}|\p{Emoji}\u{FE0F})*/gu;
/** Is this the whole of a message — emoji and nothing else but the spaces and line breaks between them? */
const emojiOnly = (s: string): boolean => {
  const txt = String(s || '');
  return !!txt.trim() && !txt.replace(EMOJI_RUN, '').trim();
};
/** One plain stretch of a message's words, each run of emoji in it drawn at `em` times the size of those words.
 *  A stretch carrying no emoji is handed back as the words it is, with no span wrapped round it. */
function emojiSized(s: string, key: string, em: number): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let at = 0;
  let m: RegExpExecArray | null;
  EMOJI_RUN.lastIndex = 0;
  while ((m = EMOJI_RUN.exec(s))) {
    if (m.index > at) out.push(s.slice(at, m.index));
    out.push(<span key={`${key}e${m.index}`} style={{ fontSize: `${em}em` }}>{m[0]}</span>);
    at = m.index + m[0].length;
  }
  if (!out.length) return [s];
  if (at < s.length) out.push(s.slice(at));
  return out;
}

function ChatWords({ text, jobs, taxIds, passports, onTaxId, onJob, mentions, inline }: {
  text: string; jobs: ChatJobs; taxIds: ChatTaxIds; passports: ChatPassports; onTaxId: (taxId: string) => void;
  onJob?: (page: string, jobId: string) => void; mentions?: Mentions; inline?: boolean;
}) {
  const { t } = useTranslation();
  const me = useStore(s => s.me);
  useDocSeries();   // draws again when the workspace's document prefixes land
  // Which Jobs page a number opens: Active Jobs for an admin and for anybody granted it — that list's ?open=
  // fetches the job by its id, so one that is not on the page, a closed one included, still opens — and Closed
  // Jobs for somebody granted that alone. Neither module: the number stays words, never a link into a page that
  // would turn the reader away.
  const mods = me?.modules || [];
  const page = me?.role === 'admin' || mods.includes('active_jobs') ? 'active_jobs'
    : mods.includes('closed_jobs') ? 'closed_jobs' : '';
  // A tax ID needs no such test of its own: every staff login may browse and download Our Documents, so a
  // matched one is drawn and pressable for all of them (report 32621dcf — it used to ask for management here,
  // and so answered for the reporter and for nobody else on staff). A passport number is the same: the file
  // comes back through that same door, and an "Admin only" one is not in the answers to begin with.
  const split = docNoSplitter();
  // How large the emoji in these words are drawn (report ffe39a2e): 15% past the words, and far larger when the
  // whole of the message is emoji. A quoted line is left at the ordinary size however little it carries — it is one
  // line of the strip above an answer, not the message, and the strip holds one line and cuts the rest off.
  const em = !inline && emojiOnly(text) ? EMOJI_SOLO_EM : EMOJI_EM;

  // A press on a passport number: its bytes through the File Library's own door — api.filesBlob, the very door
  // the tax-ID panel reads a document back through — straight into a browser tab of its own, which is the
  // "quick launch of that passport in a new tab" the report asked for. The object URL is let go of a minute
  // later, as every other blob this app opens in a tab is. A file that has gone, or that has been made Admin
  // only since the answer, simply does not open: nothing is said, as nothing is said when a number is nobody's.
  const openPassport = (fileId: string) => {
    if (!fileId) return;
    api.filesBlob(fileId).then(url => {
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }, () => { /* the number stays where it is */ });
  };

  // The numbers in one piece of plain words. The next candidate is found by the test itself, so passing one over
  // leaves the rest of the piece intact.
  const nums = (part: string, key: string): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    let at = 0;                      // how far through this piece the words have been drawn
    let m: RegExpExecArray | null;
    NUM_IN_TEXT.lastIndex = 0;
    while ((m = NUM_IN_TEXT.exec(part))) {
      const hit = m[0], end = m.index + hit.length;
      // Part of a longer run of digits is neither a job nor a tax ID (a telephone number, a year and a sum run
      // together). A '#' in front is a boundary of its own. A passport token needs no test of its own here:
      // its own two word boundaries already keep it out of a longer word and a longer number.
      if ((hit[0] !== '#' && m.index > 0 && isDigit(part[m.index - 1])) || (end < part.length && isDigit(part[end]))) continue;
      // A token carrying a LETTER can only be a passport number, and bare digits can never be one — so each
      // kind is looked up in its own answers alone and the three can never be mistaken for one another.
      const letters = /[A-Za-z]/.test(hit);
      const pass = letters && hit.length <= PASSPORT_CHARS ? passports[hit] : undefined;
      const passFile = pass ? pass.fileId : '';
      const tax = !letters && hit.length === 13 ? taxIds[hit] : undefined;
      const no = hit[0] === '#' ? hit.slice(1) : hit;
      const job = letters || hit.length === 13 ? undefined : jobs[no];
      // Letters stuck to digits that are NOBODY'S passport: the digits themselves are handed back to the test,
      // so a job number written straight after a word ("job12345") is still the job link it has always been.
      // The test is moved past the letters alone, never back, so this can only step forwards.
      if (!pass && letters) {
        const lead = /^[A-Za-z]+/.exec(hit);
        NUM_IN_TEXT.lastIndex = m.index + (lead ? lead[0].length : 1);
        continue;
      }
      // No passport, no client and no job of that number, or nowhere for this reader to open the job: words,
      // as before.
      if (!pass && !tax && (!job || !page)) continue;
      if (m.index > at) out.push(...emojiSized(part.slice(at, m.index), `${key}x${at}`, em));
      out.push(pass ? (
        // The archive's own amber-brown, the colour a tax ID is drawn in, with whose passport it is in the hover
        // — and, for the rare protocol name carrying no person at all, the same hover without a name in it.
        <span key={`${key}p${m.index}`} role="button"
          title={pass.person ? t('chat_pass_tip', { name: pass.person }) : t('chat_pass_tip_any')}
          onClick={e => { e.stopPropagation(); openPassport(passFile); }}
          style={{ color: DOC_GROUP.color, cursor: 'pointer' }}>{hit}</span>
      ) : tax ? (
        <span key={`${key}t${m.index}`} role="button" title={t('chat_tax_tip', { name: tax.name })}
          onClick={e => { e.stopPropagation(); onTaxId(hit); }}
          style={{ color: DOC_GROUP.color, cursor: 'pointer' }}>{hit}</span>
      ) : (
        <span key={`${key}j${m.index}`} role="button"
          title={t(`chat_job${job && job.closed ? '_closed' : ''}${onJob ? '_over' : ''}_tip`, { no })}
          onClick={e => {
            e.stopPropagation();
            if (!job) return;
            // In the app the job is drawn over the chat; on a page it is a browser tab of its own, as it was.
            if (onJob) onJob(page, job.id);
            else window.open(`/m/${page}?open=${encodeURIComponent(job.id)}`, '_blank');
          }}
          style={{ color: TAB_GROUPS.jobs.color, cursor: 'pointer' }}>{hit}</span>
      ));
      // …and whose it is, after it in plain words (report 9c266d45), on the message line alone. Already written
      // there — typed, or copied out of a thread with its label — it is not written a second time.
      if (!inline) {
        const taxName = tax ? displayClientName(tax.name) : '';
        const who = pass ? (pass.person ? t('chat_pass_label', { name: pass.person }) : t('chat_pass_label_any'))
          : tax ? (taxName ? t('chat_tax_label', { name: taxName }) : t('chat_tax_label_any'))
            : job && job.client ? t('chat_job_label', { name: job.client }) : t('chat_job_label_any');
        if (!part.slice(end).trimStart().startsWith(who)) {
          out.push(<span key={`${key}l${m.index}`} style={{ color: 'var(--text-primary)' }}>{` ${who}`}</span>);
        }
      }
      at = end;
    }
    if (at < part.length) out.push(...emojiSized(part.slice(at), `${key}x${at}`, em));
    return out;
  };
  // A colleague named in those words (report 67084d4c): bold, in their own colour, written with the '@' the
  // typing box's own list puts in or simply written out. Part of a longer word is no name — "Announcement" does
  // not name Ann — so what stands either side of it has to be something other than a letter or a digit.
  const named = (part: string, key: string): React.ReactNode[] => {
    const ment = mentions;
    if (!ment) return nums(part, key);
    const out: React.ReactNode[] = [];
    let at = 0;
    let m: RegExpExecArray | null;
    ment.re.lastIndex = 0;
    while ((m = ment.re.exec(part))) {
      const hit = m[0], end = m.index + hit.length;
      if (isWordChar(part[m.index - 1]) || isWordChar(part[end])) continue;
      const nm = hit[0] === '@' ? hit.slice(1) : hit;
      if (m.index > at) out.push(...nums(part.slice(at, m.index), `${key}w${at}`));
      out.push(
        <strong key={`${key}n${m.index}`} style={{ color: ment.colors[nm.toLowerCase()] || 'var(--text-primary)' }}>{hit}</strong>,
      );
      at = end;
    }
    if (at < part.length) out.push(...nums(part.slice(at), `${key}w${at}`));
    return out;
  };
  // One plain stretch of a message: the odd pieces are the document numbers the splitter matched; the even ones
  // are the rest of the words, where a name, a job number or a tax ID may be.
  const leaf = (s: string, key: string): React.ReactNode[] => (split ? s.split(split) : [s])
    .flatMap<React.ReactNode>((part, i) => (i % 2 === 1 ? [<DocNoLink key={`${key}d${i}`} no={part} />] : named(part, `${key}p${i}`)));

  // A quoted line is one line in a strip of its own, with no room for a list: words and markers alone there.
  if (inline) return <>{markedUp(String(text), leaf, 'q')}</>;

  // The lines of a message: each one that begins '- ' or '1. ' becomes a point of a list, and everything else is
  // the words themselves, line breaks and all (the bubble keeps them — white-space: pre-wrap). A line taken into
  // a list takes its own line break with it, so a list leaves no empty line above or below it.
  const blocks: React.ReactNode[] = [];
  let plain: string[] = [];
  let items: string[] = [];
  let tag: 'ul' | 'ol' = 'ul';
  let seq = 0;
  const putPlain = () => {
    if (!plain.length) return;
    const k = `b${seq++}`;
    blocks.push(<React.Fragment key={k}>{markedUp(plain.join('\n'), leaf, k)}</React.Fragment>);
    plain = [];
  };
  const putList = () => {
    if (!items.length) return;
    const k = `b${seq++}`;
    const points = items.map((it, i) => <li key={i}>{markedUp(it, leaf, `${k}-${i}`)}</li>);
    blocks.push(tag === 'ol' ? <ol key={k} style={LIST}>{points}</ol> : <ul key={k} style={LIST}>{points}</ul>);
    items = [];
  };
  String(text).split('\n').forEach(ln => {
    const b = BULLET.exec(ln);
    const n = b ? null : NUMBERED.exec(ln);
    const want: '' | 'ul' | 'ol' = b ? 'ul' : n ? 'ol' : '';
    if (!want) { putList(); plain.push(ln); return; }
    putPlain();
    if (tag !== want) putList();   // a numbered run after a bulleted one is a list of its own
    tag = want;
    items.push(((b || n) as RegExpExecArray)[1]);
  });
  putPlain();
  putList();
  return <>{blocks}</>;
}

/** The pills that sit on the dark of a photo or a document opened over the whole screen — Save, Rotate, Rectify (AI)
 *  — and the one line they ever have to say, a turn the server would not save. One copy, so the photo overlay and
 *  the document overlay below cannot drift apart. */
const bigPill = (wait: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 34, padding: '0 14px', borderRadius: 999,
  border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: wait ? 'wait' : 'pointer',
});
const turnNote: React.CSSProperties = {
  maxWidth: 260, padding: '5px 9px', borderRadius: 8, background: 'rgba(180,35,24,0.88)',
  color: '#fff', fontSize: 11.5, fontWeight: 700, lineHeight: 1.45,
};

/** A photo shown large, over the Chat window (report 32621dcf, Calem: "…when the user clicks on them, just
 *  enlarge them like a preview on the screen"): the dark behind it, the picture at its own size, and the × at the
 *  top right to go back to the chat. The dark, the × and Escape all close it. One and the same overlay serves a
 *  photo already sent — which carries Save, so a picture pressed to be looked at can still be kept — and a
 *  pasted one still waiting above the typing box, which is on nobody's computer but the sender's own and so
 *  shows no Save.
 *  Escape is caught on the way DOWN and stopped there: the Chat window listens for Escape too, and without this
 *  the whole window would close under the photo instead of the photo closing. The one exception is the Rectify (AI)
 *  window opened from here (report 6cc0de58): it stands over this one and answers Escape itself, so the photo stays
 *  where it is until that window has gone.
 *  A photo already sent also carries Rectify (AI) beside Save, so a client's photo of a document can be straightened
 *  from the picture being looked at. On a phone it is the wand alone — two named pills and the × would not fit the
 *  width — and the window it opens says its name at the head. A picture still waiting to be sent has neither: it is
 *  on nobody's computer but the sender's own, and there is no message for the AI to be asked about yet.
 *  Rotate stands with them (report 6cc0de58, opening #3): a quarter-turn that is SAVED, so the picture comes back
 *  the new way up for everybody in the chat, not only on the screen it was turned on.
 *  A picture still waiting to be sent carries Crop instead (report 1e817873), the one tool it has: the crop window
 *  (ImageCrop) stands over this one, and this one stands down on Escape while it is open, as it does for Rectify (AI). */
function PhotoBig({ url, name, onClose, onSave, onRectify, onRotate, onCrop, busy, turning, turnSays }:
  { url: string; name: string; onClose: () => void; onSave?: () => void; onRectify?: () => void;
    onRotate?: () => void; onCrop?: () => void; busy?: boolean; turning?: boolean; turnSays?: string }) {
  const { t } = useTranslation();
  const phone = usePhone();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[data-rectify], [data-crop]')) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    // Over the Chat window itself, which sits at 10200: this is a child of it, so it is above everything in it.
    // The picture keeps its own size and is only ever made smaller, never blown up past what was sent.
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true" aria-label={name}
      style={{ position: 'fixed', inset: 0, zIndex: 10300, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}>
      {/* The picture itself, pressed or not, stays where it is — only the dark around it closes. */}
      <img src={url} alt={name}
        style={{ display: 'block', maxWidth: '92vw', maxHeight: '84vh', borderRadius: 8, boxShadow: '0 8px 40px #0007', cursor: 'default' }} />
      {(onSave || onRectify || onRotate || onCrop) && (
        // A column, so the one line this row ever has to say — a turn that would not save — sits under the pills
        // instead of pushing them along.
        <div style={{ position: 'fixed', top: 14, left: 16, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, zIndex: 10301 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onSave && (
              <button onClick={onSave} disabled={busy} title={t('chat_save')} style={bigPill(!!busy)}>
                <i className={`ti ti-${busy ? 'loader' : 'download'}`} style={{ fontSize: 15, animation: busy ? 'spin 1s linear infinite' : undefined }} aria-hidden />{t('chat_save')}
              </button>
            )}
            {/* The quarter-turn, which SAVES the picture that way up (report 6cc0de58, opening #3) — icon-only on a
                phone, as Rectify (AI) beside it already is: three named pills and the × would not fit the width. */}
            {onRotate && (
              <button onClick={onRotate} disabled={turning} title={t('chat_rotate_btn')} aria-label={t('chat_rotate_btn')}
                style={{ ...bigPill(!!turning), ...(phone ? { width: 34, padding: 0, gap: 0 } : null) }}>
                <i className={`ti ti-${turning ? 'loader' : 'rotate-clockwise'}`} style={{ fontSize: 16, animation: turning ? 'spin 1s linear infinite' : undefined }} aria-hidden />{phone ? '' : t('chat_rotate_btn')}
              </button>
            )}
            {onRectify && (
              <button onClick={onRectify} title={t('chat_rect_btn')} aria-label={t('chat_rect_btn')}
                style={{ ...bigPill(false), ...(phone ? { width: 34, padding: 0, gap: 0 } : null) }}>
                <i className="ti ti-wand" style={{ fontSize: 16 }} aria-hidden />{phone ? '' : t('chat_rect_btn')}
              </button>
            )}
            {/* Named on a phone as well: it only ever stands alone, on a picture not yet sent. */}
            {onCrop && (
              <button onClick={onCrop} title={t('chat_crop')} aria-label={t('chat_crop')} style={bigPill(false)}>
                <i className="ti ti-crop" style={{ fontSize: 16 }} aria-hidden />{t('chat_crop')}
              </button>
            )}
          </div>
          {turnSays && <div style={turnNote}>{turnSays}</div>}
        </div>
      )}
      <button onClick={onClose} title={t('chat_preview_close')} aria-label={t('chat_preview_close')}
        style={{ position: 'fixed', top: 14, right: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 17, cursor: 'pointer', zIndex: 10301 }}>
        <i className="ti ti-x" aria-hidden />
      </button>
    </div>
  );
}

// How big a PDF sent in a chat is still drawn in its bubble. A chat takes a file of up to 200 MB, and a thread of
// those pulled whole into the browser to draw their first pages would cost more than it is worth: a bigger one keeps
// the line it has always had, which saves it to the computer.
const PDF_PEEK_MAX = 12 * 1024 * 1024;

/** A document sent in a chat, READ over the whole screen (report 6cc0de58, opening #3 — Calem: "I want the PDF to
 *  show a preview in the chat rather than just a link and you should be able to rotate this as well. It also saves
 *  the file, by the way."). Before this, a PDF in a chat was a line that saved it to the computer and nothing more:
 *  there was no document on screen that a turn could even be asked of.
 *  Everything about it mirrors a photo shown large (PhotoBig above): the same dark at the same height over the Chat
 *  window, Save and Rotate as the same pills at the top left, the × at the top right, and the dark, the × and Escape
 *  all closing it — Escape caught on the way DOWN and stopped, or the whole Chat window would close under the
 *  document, and standing down while the Rectify (AI) window is on screen, which answers the key itself.
 *  The pages themselves are the Filing page's own pane (PdfPreview): every page drawn down the screen, no viewer
 *  toolbar and no thumbnail rail, scrolled like a document. */
function DocBig({ url, name, onClose, onSave, onRotate, busy, turning, turnSays }:
  { url: string; name: string; onClose: () => void; onSave?: () => void; onRotate?: () => void;
    busy?: boolean; turning?: boolean; turnSays?: string }) {
  const { t } = useTranslation();
  const phone = usePhone();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[data-rectify]')) return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    // Over the Chat window itself, which sits at 10200: this is a child of it, so it is above everything in it.
    // This one scrolls, which a photo never had to: a press on its own scroll bar is not a press on the dark
    // around the document, so it is told apart by where in the box it landed and left to scroll.
    <div onMouseDown={e => {
      if (e.target !== e.currentTarget) return;
      const box = e.currentTarget as HTMLDivElement;
      if (e.clientX > box.clientWidth || e.clientY > box.clientHeight) return;
      onClose();
    }}
      role="dialog" aria-modal="true" aria-label={name}
      style={{ position: 'fixed', inset: 0, zIndex: 10300, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: phone ? '56px 8px 16px' : '56px 24px 24px', overflow: 'auto' }}>
      {/* The pages fill a reader's width, and the whole of a phone's screen: 820 is about an A4 page on a laptop. */}
      <div style={{ width: phone ? '100%' : 820, maxWidth: '100%', flexShrink: 0 }}>
        <PdfPreview src={url} />
      </div>
      <div style={{ position: 'fixed', top: 14, left: 16, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, zIndex: 10301 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onSave && (
            <button onClick={onSave} disabled={busy} title={t('chat_save')} style={bigPill(!!busy)}>
              <i className={`ti ti-${busy ? 'loader' : 'download'}`} style={{ fontSize: 15, animation: busy ? 'spin 1s linear infinite' : undefined }} aria-hidden />{t('chat_save')}
            </button>
          )}
          {/* The quarter-turn, saved into the document itself — every page of it, so a scan that came in sideways
              reads the right way up for everybody in the chat from then on. Icon-only on a phone. */}
          {onRotate && (
            <button onClick={onRotate} disabled={turning} title={t('chat_rotate_btn')} aria-label={t('chat_rotate_btn')}
              style={{ ...bigPill(!!turning), ...(phone ? { width: 34, padding: 0, gap: 0 } : null) }}>
              <i className={`ti ti-${turning ? 'loader' : 'rotate-clockwise'}`} style={{ fontSize: 16, animation: turning ? 'spin 1s linear infinite' : undefined }} aria-hidden />{phone ? '' : t('chat_rotate_btn')}
            </button>
          )}
        </div>
        {turnSays && <div style={turnNote}>{turnSays}</div>}
      </div>
      <button onClick={onClose} title={t('chat_doc_close')} aria-label={t('chat_doc_close')}
        style={{ position: 'fixed', top: 14, right: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 17, cursor: 'pointer', zIndex: 10301 }}>
        <i className="ti ti-x" aria-hidden />
      </button>
    </div>
  );
}

// ── A client's company documents, opened by their tax ID (report 32621dcf, Calem: "Tax ID is written or pasted
// into a chat message, so that 0845557000162 becomes clickable in the chat the way a job or quotation number
// already [is] … Samui Law Firm's most recent company documents stored in our documents pop up in preview
// mode … As long as those documents are stored in our archive under our documents") ──────────────────────────
// A panel over the Chat window: the client's name and tax ID at its head, their documents from the Our Documents
// archive down the left — the newest first, each showing its Document type with the person and the date out of
// the file's own name — and the one chosen drawn beside them. The NEWEST is chosen the moment the list lands, so
// a press on a tax ID comes up showing the most recent document, which is what was asked for.
// The dark, the × and Escape all close it, exactly as they close a photo shown large (PhotoBig above), and
// Escape is caught on the way DOWN and stopped there: the Chat window listens for it too, and without this the
// whole window would close under the panel instead of the panel closing.
// The documents are read back through the File Library's own door (api.filesBlob), which every staff login may
// download from, and drawn with the very pane the Filing page draws them in (PdfPreview).
const TAX_PREVIEW_EXT = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp'];   // what the pane can draw, as the File Library's own canPreview
const extOfName = (name: string): string => (String(name || '').split('.').pop() || '').toLowerCase();
const canShowDoc = (name: string): boolean => TAX_PREVIEW_EXT.includes(extOfName(name));
const TAX_LIST_W = 300;       // the documents' column; the chosen one has the rest of the panel

function TaxDocs({ taxId, onClose }: { taxId: string; onClose: () => void }) {
  const { t } = useTranslation();
  // On a phone the two columns stack: the documents across the top, the one chosen under them (report 8c5f3514).
  const phone = usePhone();
  const [found, setFound] = useState<ChatTaxDocPage | null>(null);   // null = still being asked for
  const [failed, setFailed] = useState(false);
  const [pickedId, setPickedId] = useState('');
  const [shown, setShown] = useState<{ id: string; url: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key !== 'Escape') return; e.stopPropagation(); onClose(); };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => {
    let live = true;
    setFound(null); setFailed(false); setPickedId('');
    api.chatTaxDocs(taxId).then(r => { if (live) setFound(r); }, () => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [taxId]);

  const rows = found?.rows || [];
  // Nothing chosen yet means the newest, which is the first of the list: the panel opens on the most recent
  // document rather than on an empty pane.
  const picked = rows.filter(d => d.id === pickedId)[0] || rows[0] || null;
  const showId = picked && canShowDoc(picked.name) ? picked.id : '';

  // The chosen document's bytes, fetched with the login token and drawn as a blob — the same way the Filing page
  // and Drop File's pop-up show one. The object URL is let go of as soon as another document is chosen or the
  // panel closes, so nothing of it is left held in the browser.
  useEffect(() => {
    if (!showId) { setShown(null); return; }
    let live = true, made = '';
    setLoading(true);
    api.filesBlob(showId).then(url => {
      if (!live) { URL.revokeObjectURL(url); return; }
      made = url;
      setShown({ id: showId, url });
      setLoading(false);
    }, () => { if (live) { setShown(null); setLoading(false); } });
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [showId]);

  const spin = <i className="ti ti-loader" style={{ fontSize: 20, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />;
  const docRow = (on: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', marginBottom: 2, padding: '8px 10px', borderRadius: 9,
    border: '1px solid', borderColor: on ? DOC_GROUP.border : 'transparent', background: on ? DOC_GROUP.tint : 'transparent',
    cursor: 'pointer', textAlign: 'left', fontSize: 12.5, color: 'var(--text-primary)', minWidth: 0,
  });
  return (
    // Over the Chat window itself, which sits at 10200: this is a child of it, so it is above everything in it.
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true" aria-label={t('chat_tax_title')}
      style={{ position: 'fixed', inset: 0, zIndex: 10300, background: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: phone ? 0 : 24 }}>
      {/* 860 × 580 on a screen with the room for it; the whole of a phone's screen otherwise, where that would be
          wider and taller than the screen itself and the × at its corner would be off the edge. */}
      <div style={{ display: 'flex', flexDirection: 'column', width: phone ? '100%' : 860, maxWidth: phone ? '100%' : '94vw', height: phone ? '100%' : 580, maxHeight: phone ? '100%' : '86vh', background: 'var(--bg-surface)', borderRadius: phone ? 0 : 14, boxShadow: phone ? undefined : 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 14px', borderBottom: '1px solid var(--border-strong)', flexShrink: 0 }}>
          <i className="ti ti-folder-open" style={{ fontSize: 19, color: DOC_GROUP.color }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('chat_tax_title')}</div>
            {/* Whose documents these are: the client the tax ID names, and the tax ID itself. */}
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {found?.name ? `${found.name} · ${taxId}` : taxId}
            </div>
          </div>
          <button onClick={onClose} title={t('chat_tax_close')} aria-label={t('chat_tax_close')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>

        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: phone ? 'column' : 'row' }}>
          {/* Stacked on a phone: the list takes the top two-fifths and the document the rest, so both are there
              at once rather than the list taking a 300px column off a 390px screen. */}
          <aside style={{ width: phone ? '100%' : TAX_LIST_W, flexShrink: 0, maxHeight: phone ? '40%' : undefined, overflowY: 'auto', borderRight: phone ? undefined : '1px solid var(--border-strong)', borderBottom: phone ? '1px solid var(--border-strong)' : undefined, padding: '8px 8px 10px' }}>
            {!found && !failed ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 14 }}>{spin}</div>
            ) : failed ? (
              <div style={{ fontSize: 12, lineHeight: 1.5, color: '#B42318', padding: '6px 4px' }}>{t('chat_tax_failed')}</div>
            ) : !rows.length ? (
              <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-tertiary)', padding: '6px 4px' }}>{t('chat_tax_none')}</div>
            ) : (
              <>
                {rows.map(d => (
                  // The whole file name is in the hover, so a reader can always see which document a row is.
                  <button key={d.id} onClick={() => setPickedId(d.id)} title={d.name} style={docRow(!!picked && d.id === picked.id)}>
                    <i className="ti ti-file-text" style={{ fontSize: 15, flexShrink: 0, marginTop: 1, color: DOC_GROUP.color }} aria-hidden />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.type || d.name}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {[d.person, d.date ? fmtDate(d.date) : ''].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </button>
                ))}
                {/* More of them than one panel lists: said plainly rather than quietly left out. */}
                {found && found.total > rows.length && (
                  <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-tertiary)', padding: '6px 6px 0' }}>
                    {t('chat_tax_more', { n: found.total - rows.length })}
                  </div>
                )}
              </>
            )}
          </aside>

          <section style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {!picked ? null : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{picked.name}</span>
                </div>
                <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', alignItems: showId && shown ? 'flex-start' : 'center', justifyContent: 'center', padding: 10, background: 'var(--bg-panel)' }}>
                  {/* A Word file or a spreadsheet is listed like any other document of that client, and says so
                      rather than being left out of the list. */}
                  {!showId ? (
                    <div style={{ fontSize: 12.5, lineHeight: 1.6, textAlign: 'center', color: 'var(--text-tertiary)' }}>{t('chat_tax_no_preview')}</div>
                  ) : loading || !shown || shown.id !== showId ? (
                    spin
                  ) : extOfName(picked.name) === 'pdf' ? (
                    <PdfPreview src={shown.url} />
                  ) : (
                    <img src={shown.url} alt={picked.name} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 6 }} />
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

/** What a message CARRIES, drawn in its own bubble: a photo as a picture, a voice note as a player, a PDF as its
 *  first page, and anything else as a line to press that saves it. The bytes are fetched with the login token
 *  (chatFileBlob) — a plain <img src> or <audio src> cannot carry one — and the blob is what the picture, the
 *  player and the document pane are given. The server hands them to the members of that chat and nobody else, so
 *  nothing about the sender's own files opens.
 *  A photo carries the wand as well (report 6cc0de58): Rectify (AI), the email's own window, for a client's photo
 *  of a document that came in here. The message keeps its picture whatever the AI makes of it.
 *  A photo and a PDF each carry the turn too (opening #3): one press saves the file a quarter round, and the bytes
 *  are fetched again so what is on screen is the file as it now stands. */
function ChatAttachment({ convId, m }: { convId: string; m: ChatMessage }) {
  const { t } = useTranslation();
  const phone = usePhone();
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [big, setBig] = useState(false);   // the photo shown large over the Chat window
  const [doc, setDoc] = useState(false);   // …or the PDF read over it (DocBig)
  const [rect, setRect] = useState(false); // …and the AI's straightened version of a photo, over either
  const [page1, setPage1] = useState(''); // the PDF's first page, drawn in the bubble
  const [turning, setTurning] = useState(false);
  const [turnSays, setTurnSays] = useState('');   // the words for a turn that did not save; '' while all is well
  const [turned, setTurned] = useState(0);   // bumped by a saved turn, which fetches the file again
  // A PDF small enough to be worth fetching whole to draw (report 6cc0de58, opening #3: "I want the PDF to show a
  // preview in the chat rather than just a link"). A bigger one stays the line it has always been — a chat takes a
  // file of up to 200 MB, and nobody wants a thread of them pulled into the browser to draw their first pages.
  const pdf = m.kind === 'file' && String(m.fileMime || '').toLowerCase().split(';')[0].trim() === 'application/pdf';
  const peek = pdf && m.fileSize > 0 && m.fileSize <= PDF_PEEK_MAX;
  const drawn = m.kind === 'image' || m.kind === 'voice' || peek;   // anything else is never fetched until it is asked for

  // The bytes, fetched once and used by the bubble and by whatever is opened over the chat. `turned` goes up when a
  // turn has been SAVED, which fetches them again: what is then on screen is the file as it now stands. The object
  // URL that one replaces is let go of only once the new one is in hand, so nothing ever points at nothing.
  const held = useRef('');
  useEffect(() => {
    if (!drawn) return;
    let live = true;
    chatFileBlob(convId, m.id).then(b => {
      if (!live) return;
      if (!b) { setFailed(true); return; }
      const made = URL.createObjectURL(b);
      if (held.current) URL.revokeObjectURL(held.current);
      held.current = made;
      setUrl(made);
    }, () => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [convId, m.id, drawn, turned]);
  // …and let go of on the way out, so nothing of it is left held in the browser.
  useEffect(() => () => { if (held.current) URL.revokeObjectURL(held.current); }, []);

  // The PDF's first page as a picture for the bubble, drawn by the very call the Filing page's pane draws a page
  // with (PdfPreview's pdfPageDataUrl). A document the browser cannot read leaves the bubble as it was: the line
  // with its name, which still saves it.
  useEffect(() => {
    if (!peek || !url) { setPage1(''); return; }
    let live = true;
    (async () => {
      try {
        const buf = await (await fetch(url)).arrayBuffer();
        const first = await pdfPageDataUrl(buf, 1, 240);
        if (live) setPage1(first.url);
      } catch { if (live) setPage1(''); }
    })();
    return () => { live = false; };
  }, [peek, url]);

  // Save it to the computer, under the name it was sent with. A picture already on screen keeps its
  // place if this fails — only a file, which has nothing drawn of its own, says so.
  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const b = await chatFileBlob(convId, m.id);
      if (!b) { if (!url) setFailed(true); return; }
      const u = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = u;
      a.download = m.fileName || 'file';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 10_000);
    } catch { if (!url) setFailed(true); } finally { setBusy(false); }
  };

  // ── A quarter-turn that sticks (report 6cc0de58, opening #3: "Yes save the file the new way up") ─────
  // One press turns the file a quarter clockwise — the STORED file, not the screen: the server rewrites it,
  // so it comes back the new way up for everybody in the chat and in every download of it from now on. The
  // bytes are then fetched again here, which is what redraws the picture (or the PDF's first page) turned.
  // A file past the server's own ceiling for a turn is told so plainly — "please try again" would be a lie, since
  // trying again cannot make it smaller.
  const turn = async () => {
    if (turning) return;
    setTurning(true); setTurnSays('');
    try {
      const r = await api.rotateChatFile(convId, m.id, 90);
      if (r && r.ok) setTurned(n => n + 1);
      else setTurnSays(t(r && r.code === 'too_large' ? 'chat_rotate_too_big' : 'chat_rotate_fail'));
    } catch { setTurnSays(t('chat_rotate_fail')); } finally { setTurning(false); }
  };

  // ── A photo shown large, over the Chat window (report 32621dcf, Calem: "…when the user clicks on them,
  // just enlarge them like a preview on the screen") ──────────────────────────────────────────────────
  // Pressing a photo used to save it to the computer, which is not what a press on a picture means
  // anywhere else; it now opens it over the whole screen at its own size (PhotoBig), as a payment slip
  // opens on a Bank Statement row. The picture is the very same one already drawn in the bubble — the
  // bytes fetched once for the thumbnail serve both — so opening it fetches nothing.
  // The photo has gone from the server, or the message is no longer drawn: nothing left to show large.
  useEffect(() => { if (!url || failed) { setBig(false); setDoc(false); } }, [url, failed]);

  const line: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 7, maxWidth: '100%', padding: 0, border: 'none', background: 'transparent', color: 'inherit', font: 'inherit', textAlign: 'left', cursor: busy ? 'wait' : 'pointer' };
  if (failed) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-tertiary)' }}>
        <i className="ti ti-file-off" style={{ fontSize: 15 }} aria-hidden />{t('chat_file_gone')}
      </div>
    );
  }
  if (drawn && !url) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-tertiary)' }}>
        <i className="ti ti-loader" style={{ fontSize: 15, animation: 'spin 1s linear infinite' }} aria-hidden />{t(kindKey(m.kind))}
      </div>
    );
  }
  if (m.kind === 'image') {
    // ── Rectify (AI) on a photo sent here (report 6cc0de58) ─────────────────────────────────────────────
    // The reporter: "allow users to rectify images in the chat app - on desktop and phone". The wand sits in the
    // corner of the picture in the bubble, and again beside Save in the photo shown large, so it is one press
    // away whichever way the picture is being looked at. A JPEG and a PNG carry it — the two the AI reads
    // (canRectify) — so a GIF or a WebP has no wand that could only answer "cannot be rectified".
    // The window is the email's (RectifyModal), handed this message's own server calls: it straightens the
    // document, lets the four corners be dragged onto it by hand (on a phone too, with a finger), names it by
    // the archive's protocol and offers to file it in the Drop Folder. It stands at
    // 10310, over the photo (10300) and its buttons (10301), because the Chat pop-up is itself over the page.
    const wand = canRectify(m.fileMime, m.fileName);
    const tap = phone ? 32 : 28;   // a thumb's target on a phone, where this picture may well have been taken
    const circle: React.CSSProperties = {
      display: 'flex', alignItems: 'center', justifyContent: 'center', width: tap, height: tap, padding: 0,
      borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer',
    };
    return (
      <>
        {/* The wand is held in the corner OF THE PICTURE, so this wrapper is as wide as the picture and no wider;
            aligned to the top, which keeps the bubble exactly as tall as it was with no strip of empty space
            under the photo. */}
        <span style={{ position: 'relative', display: 'inline-block', verticalAlign: 'top', maxWidth: '100%' }}>
          <button onClick={() => setBig(true)} title={t('chat_preview')} style={{ ...line, display: 'block', maxWidth: '100%', cursor: 'zoom-in' }}>
            <img src={url} alt={m.fileName || t('chat_kind_image')}
              style={{ display: 'block', maxWidth: 240, maxHeight: 240, width: 'auto', height: 'auto', borderRadius: 8, border: '1px solid var(--border)' }} />
          </button>
          {/* Rotate stands beside the wand, which keeps the very corner it has always had: a quarter-turn that is
              SAVED into the picture (report 6cc0de58, opening #3). Every photo carries it — a GIF and a WebP too,
              which the AI cannot read and so have no wand. */}
          <span style={{ position: 'absolute', top: 5, right: 5, display: 'flex', alignItems: 'center', gap: 4 }}>
            <button onClick={turn} disabled={turning} title={t('chat_rotate_btn')} aria-label={t('chat_rotate_btn')}
              style={{ ...circle, cursor: turning ? 'wait' : 'pointer' }}>
              <i className={`ti ti-${turning ? 'loader' : 'rotate-clockwise'}`}
                style={{ fontSize: phone ? 17 : 15, animation: turning ? 'spin 1s linear infinite' : undefined }} aria-hidden />
            </button>
            {wand && (
              <button onClick={() => setRect(true)} title={t('chat_rect_btn')} aria-label={t('chat_rect_btn')} style={circle}>
                <i className="ti ti-wand" style={{ fontSize: phone ? 17 : 15 }} aria-hidden />
              </button>
            )}
          </span>
        </span>
        {/* A turn the server would not save is said plainly, under the picture. */}
        {turnSays && <div style={{ ...turnNote, marginTop: 5 }}>{turnSays}</div>}
        {/* Save goes with it, so a photo pressed to be looked at can still be kept — and the turn and the wand
            beside it, so the picture can be set the right way up without closing it again. */}
        {big && <PhotoBig url={url} name={m.fileName || t('chat_kind_image')} onClose={() => setBig(false)} onSave={save}
          onRectify={wand ? () => setRect(true) : undefined} onRotate={turn} busy={busy} turning={turning} turnSays={turnSays} />}
        {rect && (
          <RectifyModal key={`${convId}:${m.id}`} z={10310} fileName={m.fileName || ''}
            doors={{
              rectify: lang => api.rectifyChatImage(convId, m.id, lang),
              restraighten: (token, corners) => api.restraightenChatImage(convId, m.id, token, corners),
              store: (token, name) => api.storeRectifiedChatImage(convId, m.id, token, name),
              discard: token => api.discardRectifiedChatImage(convId, m.id, token),
            }}
            onClose={() => setRect(false)} />
        )}
      </>
    );
  }
  // The browser's own player, so pausing and scrubbing work as they do everywhere else. No caption track:
  // a colleague's spoken note has no transcript to show.
  if (m.kind === 'voice') return <audio controls src={url} style={{ display: 'block', width: 230, maxWidth: '100%', height: 36 }} />;
  // A file's line is as wide as its bubble, and its whole name is shown: a long one wraps onto more lines instead of
  // being cut short with "…" (report 4eb34eff: "Name of the file to the length of the chat bubble"), the way the
  // "Filed in the Drop Folder as …" line under it already does. The press on it saves the file, as it always has —
  // which is why it stays under the PDF's first page below, where the picture itself opens the document instead.
  const nameLine = (
    <button onClick={save} title={t('chat_save')} style={line}>
      <i className={`ti ti-${busy ? 'loader' : 'paperclip'}`} style={{ fontSize: 15, flexShrink: 0, color: 'var(--accent-text)', animation: busy ? 'spin 1s linear infinite' : undefined }} aria-hidden />
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontWeight: 700, whiteSpace: 'normal', overflowWrap: 'anywhere', textDecoration: 'underline' }}>{m.fileName || t('chat_kind_file')}</span>
        {m.fileSize > 0 && <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{fmtSize(m.fileSize)}</span>}
      </span>
    </button>
  );
  // ── A PDF read in the chat, not only saved out of it (report 6cc0de58, opening #3) ───────────────────
  // Calem: "I want the PDF to show a preview in the chat rather than just a link and you should be able to rotate
  // this as well." The bubble now shows the document's FIRST PAGE, drawn by the same call the Filing page draws a
  // page with, with its name under it; pressing the page opens the whole document over the chat (DocBig) and the
  // turn in its corner saves it a quarter round, exactly as a photo's does. A document the browser cannot read, and
  // one too big to be worth fetching whole, keep the plain line they have always had.
  if (page1) {
    const tap = phone ? 32 : 28;
    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
          <span style={{ position: 'relative', display: 'inline-block', maxWidth: '100%' }}>
            <button onClick={() => setDoc(true)} title={t('chat_doc_open')} style={{ ...line, display: 'block', maxWidth: '100%', cursor: 'zoom-in' }}>
              <img src={page1} alt={m.fileName || t('chat_kind_file')}
                style={{ display: 'block', maxWidth: 240, maxHeight: 300, width: 'auto', height: 'auto', borderRadius: 8, border: '1px solid var(--border)', background: '#fff' }} />
            </button>
            <button onClick={turn} disabled={turning} title={t('chat_rotate_btn')} aria-label={t('chat_rotate_btn')}
              style={{ position: 'absolute', top: 5, right: 5, display: 'flex', alignItems: 'center', justifyContent: 'center', width: tap, height: tap, padding: 0, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: turning ? 'wait' : 'pointer' }}>
              <i className={`ti ti-${turning ? 'loader' : 'rotate-clockwise'}`}
                style={{ fontSize: phone ? 17 : 15, animation: turning ? 'spin 1s linear infinite' : undefined }} aria-hidden />
            </button>
          </span>
          {nameLine}
          {turnSays && <div style={turnNote}>{turnSays}</div>}
        </div>
        {doc && <DocBig url={url} name={m.fileName || t('chat_kind_file')} onClose={() => setDoc(false)} onSave={save}
          onRotate={turn} busy={busy} turning={turning} turnSays={turnSays} />}
      </>
    );
  }
  return nameLine;
}

// ── How to install, for a phone whose browser offers no dialog of its own (report 8c5f3514) ─────────────────────
// Calem: "Cannot find where the page tells the phone what to install." Chat's top row now carries an "Install app"
// button whenever there is still something to install (utils/installApp.ts). On Android Chrome that button opens
// the browser's OWN install dialog, because the browser handed the page an offer to open it with. Where it handed
// over nothing — every iPhone, where "Add to Home Screen" sits in the Share sheet and no page may open it, and a
// Chrome whose one offer has already been used — the button opens this instead: the few words that say which of the
// phone's own buttons to press. Both phones are named and both lines are always shown, rather than guessing from
// what the browser calls itself: an iPad answers that question as a Mac, and a reader shown the wrong line would be
// hunting for a menu that is not there.
// Wherever this sheet is read, the page under it is the chat app's own (report 96e28b88, opening #3): pressed in the
// pop-up, the button takes the phone to that address before this opens. That is what makes the words below true —
// "Add to Home Screen" saves the page it is tapped on, so tapped here it gives an icon that opens Chat, and tapped
// on the page the pop-up was over it would have given one that opens the portal, which is what the reporter got.
// Escape is caught on the way DOWN and stopped, as it is for the tax-ID panel and a photo shown large: the Chat
// window listens for Escape too, and without this the whole window would close under this sheet.
function InstallHow({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const phone = usePhone();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key !== 'Escape') return; e.stopPropagation(); onClose(); };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const step: React.CSSProperties = { padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-app)' };
  return (
    // Over the Chat window (10200), as the tax-ID panel is: this is a child of it.
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true" aria-label={t('chat_install_how_title')}
      style={{ position: 'fixed', inset: 0, zIndex: 10300, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div style={{ width: phone ? '100%' : 420, maxWidth: '100%', background: 'var(--bg-surface)', borderRadius: 14, boxShadow: 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 14px', borderBottom: '1px solid var(--border-strong)' }}>
          <i className="ti ti-download" style={{ fontSize: 19, color: 'var(--accent)' }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('chat_install_how_title')}</div>
          <button onClick={onClose} title={t('chat_install_close')} aria-label={t('chat_install_close')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 14 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('chat_install_how_intro')}</div>
          <div style={{ ...step, fontSize: 12.5, color: 'var(--text-primary)' }}>
            <div style={{ marginBottom: 3, fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('chat_install_ios_head')}</div>
            {t('chat_install_ios')}
          </div>
          <div style={{ ...step, fontSize: 12.5, color: 'var(--text-primary)' }}>
            <div style={{ marginBottom: 3, fontSize: 11.5, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('chat_install_android_head')}</div>
            {t('chat_install_android')}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── "Turn on notifications", on a phone (report ec0c5a76, opening #2: "Allow pop-up notifications on the user's
// phone for the chat app") ────────────────────────────────────────────────────────────────────────────────────────
// The banners themselves were already built (report d5b1d1de), but on a phone their one way in was a bare 28px icon
// in the top row that nobody read as "notifications". So a phone-shaped screen has this instead, over the chat list,
// saying it in words. It asks the browser nothing until it is pressed — a browser asked out of nowhere refuses for
// good (utils/chatPush.ts) — and it goes once this device has the banners, or for good once its × is pressed.
// An iPhone tab cannot be given them at all, only Chat opened from its Home Screen icon can, so there this is one
// line saying what to tap instead — and only on the chat page itself: "Add to Home Screen" saves the page it is
// tapped on, and in the pop-up that is whatever page the chat sits over (the Install app button takes a phone to
// the chat page first). The desk keeps its icon beside the bell, exactly as it was.
function PushOffer({ push, app }: { push: ChatPushButton; app: boolean }) {
  const { t } = useTranslation();
  const ask = push.show && push.on === false;
  const tell = !push.show && app && push.homeScreen;
  if (push.dismissed || (!ask && !tell)) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '10px 10px 0', padding: '8px 6px 8px 10px', borderRadius: 10, border: '1px solid var(--accent)', background: 'var(--accent-bg)', flexShrink: 0 }}>
      {ask ? (
        <button onClick={() => { if (!push.busy) void push.allow(); }}
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 9, padding: 0, border: 'none', background: 'transparent', color: 'var(--accent-text)', textAlign: 'left', cursor: push.busy ? 'wait' : 'pointer' }}>
          <i className={`ti ti-${push.busy ? 'loader' : 'bell-ringing'}`} style={{ fontSize: 20, flexShrink: 0, animation: push.busy ? 'spin 1s linear infinite' : undefined }} aria-hidden />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 800 }}>{t('chat_push_turn_on')}</span>
            <span style={{ display: 'block', fontSize: 12, lineHeight: 1.4, color: 'var(--text-secondary)' }}>{t('chat_push_turn_on_sub')}</span>
          </span>
        </button>
      ) : (
        <>
          <i className="ti ti-bell-ringing" style={{ fontSize: 20, flexShrink: 0, color: 'var(--accent-text)' }} aria-hidden />
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45, color: 'var(--text-primary)' }}>{t('chat_push_iphone')}</span>
        </>
      )}
      <button onClick={push.dismiss} title={t('chat_push_dismiss')} aria-label={t('chat_push_dismiss')}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', flexShrink: 0 }}>
        <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
      </button>
    </div>
  );
}

// ── Where forwarded messages go (report 96e28b88, Calem: "Allow users to forward (messages, images, documents,
// etc) as well as reply", then — asked how far it should reach and whether one message at a time was enough —
// "Forward within the internal chat. Give users the ability to forward either one or more than one message.") ───
// The picker over the Chat window: every chat the signed-in person is IN, which is the very list down the left of
// the window and nothing else — forwarding here never leaves the system, so there is no mailbox and no client to
// pick. Each chat has a tick, there is a box to find one by name, and the comment under them goes as an ordinary
// message of the forwarder's own straight after the copies.
// Escape is caught on the way DOWN and stopped, as it is for the tax-ID panel and a photo shown large: the Chat
// window listens for Escape too, and without this the ticking would be dropped under the picker.
const FWD_MAX_MSGS = 30;      // messages one forward carries, as the server allows
const FWD_MAX_CONVS = 20;     // chats it goes to at once, likewise
const OK_MS = 4_000;          // how long "Forwarded to N chat(s)" stands at the foot of the thread
// One chat's row in the picker — the window's own list row (`row` inside ChatPanel), which is inside that
// component, so this is its copy, exactly as TaxDocs keeps its own docRow.
const pickRow = (on: boolean): React.CSSProperties => ({
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', marginBottom: 2, padding: '8px 10px', borderRadius: 9,
  border: '1px solid', borderColor: on ? 'var(--accent)' : 'transparent', background: on ? 'var(--bg-active)' : 'transparent',
  cursor: 'pointer', textAlign: 'left', fontSize: 12.5, color: 'var(--text-primary)', minWidth: 0,
});

function ForwardTo({ convs, count, busy, err, onSend, onClose }: {
  convs: ChatConversation[]; count: number; busy: boolean; err: string;
  onSend: (to: string[], comment: string) => void; onClose: () => void;
}) {
  const { t } = useTranslation();
  const phone = usePhone();
  const [to, setTo] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [find, setFind] = useState('');
  const [capped, setCapped] = useState(false);   // the most chats at once has been ticked

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key !== 'Escape') return; e.stopPropagation(); onClose(); };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  /** A chat's name here is the name the window's own list gives it: a group's own, "You" for a chat with yourself
   *  (report e307a908), or the other person. */
  const nameOf = (c: ChatConversation) => (c.kind === 'group' ? (c.name || t('chat_group')) : c.self ? t('chat_you') : (who(c.other) || t('chat_gone')));
  const q = find.trim().toLowerCase();
  const shown = q ? convs.filter(c => nameOf(c).toLowerCase().includes(q)) : convs;
  const tick = (id: string) => {
    if (to.includes(id)) { setTo(v => v.filter(x => x !== id)); setCapped(false); return; }
    if (to.length >= FWD_MAX_CONVS) { setCapped(true); return; }
    setCapped(false);
    setTo(v => [...v, id]);
  };
  // 16px on a phone, as every other box in this window: anything smaller zooms an iPhone's whole page in.
  const box: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: phone ? 16 : 12.5, boxSizing: 'border-box' };
  const off = busy || !to.length;
  return (
    // Over the Chat window itself, which sits at 10200: this is a child of it, so it is above everything in it.
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true" aria-label={t('chat_fwd_title')}
      style={{ position: 'fixed', inset: 0, zIndex: 10300, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: phone ? 0 : 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', width: phone ? '100%' : 440, maxWidth: phone ? '100%' : '94vw', height: phone ? '100%' : 540, maxHeight: phone ? '100%' : '86vh', background: 'var(--bg-surface)', borderRadius: phone ? 0 : 14, boxShadow: phone ? undefined : 'var(--shadow-md)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 14px', borderBottom: '1px solid var(--border-strong)', flexShrink: 0 }}>
          <i className="ti ti-corner-up-right" style={{ fontSize: 19, color: 'var(--accent)' }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('chat_fwd_title')}</div>
            {/* How many messages are going, said plainly, since the picker covers the thread they were ticked in. */}
            <div style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-tertiary)' }}>{t('chat_fwd_sub', { n: count })}</div>
          </div>
          <button onClick={onClose} title={t('chat_fwd_close')} aria-label={t('chat_fwd_close')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', flexShrink: 0 }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>

        <div style={{ padding: '10px 12px 0', flexShrink: 0 }}>
          <input autoFocus value={find} onChange={e => setFind(e.target.value)} placeholder={t('chat_fwd_find')} style={box} />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '8px 10px 10px' }}>
          {!shown.length ? (
            <div style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-tertiary)', padding: '6px 4px' }}>{t('chat_fwd_no_chats')}</div>
          ) : shown.map(c => {
            const on = to.includes(c.id);
            return (
              // A one-to-one chat IS a colleague, so its name is drawn in their own colour here as it is in the
              // window's list (report 2ad9e77c); a group's name is nobody's and stays black.
              <button key={c.id} onClick={() => tick(c.id)} aria-pressed={on} title={nameOf(c)} style={pickRow(on)}>
                <i className={`ti ti-${on ? 'square-check' : 'square'}`} style={{ fontSize: 15, flexShrink: 0, color: on ? 'var(--accent)' : 'var(--text-tertiary)' }} aria-hidden />
                <i className={`ti ti-${c.kind === 'group' ? 'users' : 'user'}`} style={{ fontSize: 15, flexShrink: 0, color: 'var(--text-tertiary)' }} aria-hidden />
                <span style={{ flex: 1, minWidth: 0, fontWeight: 700, color: c.kind === 'group' ? 'var(--text-primary)' : hue(c.other), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOf(c)}</span>
              </button>
            );
          })}
        </div>

        {/* On a phone this foot is the foot of the screen, so it keeps clear of the home bar as the typing box does
            — which the chat app's viewport, reaching the screen's edges, makes real (report ec0c5a76). */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, padding: phone ? '10px 12px calc(10px + env(safe-area-inset-bottom))' : '10px 12px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={MAX_BODY} placeholder={t('chat_fwd_note')}
            style={{ ...box, minHeight: 42, maxHeight: 90, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }} />
          {capped && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('chat_fwd_cap_chats', { n: FWD_MAX_CONVS })}</div>}
          {/* A forward that was refused says so here, under the ticks, rather than at the foot of the thread
              behind this picker — and the ticks stay, so it can simply be pressed again. */}
          {err && <div role="alert" style={{ fontSize: 12, lineHeight: 1.5, color: '#B42318' }}>{err}</div>}
          <button onClick={() => onSend(to, note)} disabled={off}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 38, padding: '0 14px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1 }}>
            <i className={`ti ti-${busy ? 'loader' : 'corner-up-right'}`} style={{ fontSize: 15, animation: busy ? 'spin 1s linear infinite' : undefined }} aria-hidden />
            {t('chat_fwd_send', { n: to.length })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── The Company box's list, searched rather than scrolled (report 98453bac opening #3, Calem: "Allow users to
// search for the company name") ──────────────────────────────────────────────────────────────────────────────
// The drop-down that opening settled holds the whole client master — 130-odd companies — and a drop-down cannot
// be typed towards: the screenshot shows "Sa" typed into it and nothing happening. This is the very picker the
// Upload Document wizard offers for the same list (UploadWizard's CompanyPicker): the box shows what is picked,
// a press into it empties it for typing, and the list under it narrows to the companies whose name holds what
// was typed — "No match" when none do. Leaving the box without picking anything puts back what it held, so a
// half-typed search never changes the name.
// The order that opening settled stands whatever is typed: "— No company —" first, the client master A→Z,
// "Other…" last. Only the master's own names are narrowed; those two are always offered.
// The list opens UPWARDS, over the thread: the box sits a few rows above the foot of the window, and a list
// hanging below it would be cut off at the window's own edge.
// Escape is caught on the way DOWN and stopped there while the list is open, as the forward picker and a photo
// shown large catch it: the Chat window listens for Escape too, and without this the whole pop-up would close
// instead of the list.
function DocCompanyPicker({ value, other, companies, guess = false, onPick, onOther, onType, onEnter }: {
  value: string;                 // the master's entry the box stands on; '' = — No company —
  other: boolean;                // the box stands on "Other…", and the typing box is under it
  companies: string[];
  guess?: boolean;               // that entry is a guess, not the document's own reading: edged in orange (report 972661d6)
  onPick: (v: string) => void;   // a company of the master's, or '' for no company
  onOther: () => void;
  onType: () => void;            // a keystroke takes the note under the boxes away, as one in the other three does
  onEnter: () => void;           // Enter is Confirm, as it is in the other three
}) {
  const { t } = useTranslation();
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key !== 'Escape') return; e.stopPropagation(); setOpen(false); };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open]);
  // 16px on a phone, as every other box in this window: anything smaller zooms an iPhone's whole page in.
  const box: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: phone ? 16 : 12.5, boxSizing: 'border-box' };
  const row: React.CSSProperties = { padding: '8px 10px', fontSize: 12.5, cursor: 'pointer', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const lit = (on: boolean) => (ev: React.MouseEvent<HTMLDivElement>) => { ev.currentTarget.style.background = on ? 'var(--bg-active)' : 'transparent'; };
  const ql = q.trim().toLowerCase();
  const shown = ql ? companies.filter(c => c.toLowerCase().includes(ql)) : companies;
  const label = other ? t('chat_doc_company_other') : (value || t('chat_doc_company_none'));
  const show = () => { setOpen(true); setQ(''); };                  // a press into the box clears it for typing
  const take = (pick: () => void) => { pick(); setQ(''); setOpen(false); };
  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      <input value={open ? q : label} placeholder={t('chat_doc_company_find')} aria-label={t('chat_doc_company')} autoComplete="off"
        onFocus={show} onClick={() => { if (!open) show(); }} onBlur={() => setOpen(false)}
        onChange={e => {
          onType();
          const v = e.target.value;
          // With the list shut the box is showing the company that is picked, not a search — the box keeps the
          // cursor through a pick and through Escape. Anything typed into it then STARTS a search: the list opens
          // on what was typed (taken from the end, where the cursor stands after a pick), so a keystroke is never
          // swallowed by the name the box was showing and the box never looks frozen.
          if (!open) { setOpen(true); setQ(v.startsWith(label) ? v.slice(label.length) : ''); return; }
          setQ(v);
        }}
        onKeyDown={e => {
          if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
          e.preventDefault();
          // With words in the box Enter takes the first company found, which is what Enter means in a box being
          // typed into; with nothing being searched it is Confirm, as in the three boxes beside this one.
          if (open && ql && shown.length) { take(() => onPick(shown[0])); return; }
          onEnter();
        }}
        style={{ ...box, paddingRight: 26, cursor: open ? 'text' : 'pointer', ...(guess ? DOC_GUESS_EDGE : null) }} />
      <i className="ti ti-selector" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: 'var(--text-tertiary)', pointerEvents: 'none' }} aria-hidden />
      {open && (
        // The mouse is kept off the box itself (mousedown is stopped), as it is on the '@' list: the box keeps the
        // focus through a pick, so the list can be opened again with a press and Enter still reaches Confirm.
        <div role="listbox" aria-label={t('chat_doc_company')} onMouseDown={e => e.preventDefault()}
          style={{ position: 'absolute', left: 0, right: 0, bottom: 'calc(100% + 4px)', zIndex: 5, maxHeight: 220, overflowY: 'auto', padding: 2, borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-md)', boxSizing: 'border-box' }}>
          <div role="option" aria-selected={!other && !value} onClick={() => take(() => onPick(''))}
            onMouseEnter={lit(true)} onMouseLeave={lit(false)} style={{ ...row, color: 'var(--text-secondary)' }}>
            {t('chat_doc_company_none')}
          </div>
          {shown.map(c => (
            <div key={c} role="option" aria-selected={!other && c === value} title={c} onClick={() => take(() => onPick(c))}
              onMouseEnter={lit(true)} onMouseLeave={lit(false)} style={row}>
              {c}
            </div>
          ))}
          {!shown.length && <div style={{ ...row, cursor: 'default', color: 'var(--text-tertiary)' }}>{t('chat_doc_company_no_match')}</div>}
          <div role="option" aria-selected={other} onClick={() => take(onOther)}
            onMouseEnter={lit(true)} onMouseLeave={lit(false)} style={{ ...row, color: 'var(--text-secondary)' }}>
            {t('chat_doc_company_other')}
          </div>
        </div>
      )}
    </div>
  );
}

// ── The thread held at its foot (report bd101132) ────────────────────────────────────────────────────────────────
// How far the thread's foot stands below the bottom of what the thread shows, in px: 0 at the foot — exactly where
// scrollIntoView({ block: 'end' }) puts it — and less than that past it, in the room under the last message.
function belowFoot(el: HTMLElement, f: HTMLElement): number {
  return f.getBoundingClientRect().bottom - (el.getBoundingClientRect().top + el.clientTop + el.clientHeight);
}
const FOOT_SLACK = 24;        // px short of its foot the thread may stand and still count as standing at it

// ── How far back the thread reads (report d0f07722) ──────────────────────────────────────────────────────────────
// A thread opens on its last four weeks, and a button at its head names the month before them: each press brings
// that month back and names the one before it that anybody wrote in (server routes/chat.ts, above the messages
// door). Which month that is comes from the server; the words the button says it in are worked out here.
/** A 'YYYY-MM' month as the button names it, in the screen's own language: the month's name, its number and its
 *  year — each language's words take the ones it writes a month with. The year is the one every date here is
 *  written in, never a local calendar's. */
function monthWords(ym: string, lang: string): { month: string; num: number; year: number } {
  const [year, num] = ym.split('-').map(Number);
  const loc = lang.startsWith('zh') ? 'zh-CN' : lang.startsWith('th') ? 'th-TH' : 'en-GB';
  let month = ym;
  try { month = new Intl.DateTimeFormat(loc, { month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(year, num - 1, 1))); }
  catch { /* the month as the server wrote it */ }
  return { month, num, year };
}

/** `startId` (step 3): the chat to open on, for a pop-up opened from an on-screen notice rather than from a button.
 *  `app` (report 8c5f3514): this window IS the page — the installed chat app (ChatApp) — so it fills the screen and
 *  has neither a dark behind it nor a × to close it, there being nothing to close it to. `onJob`, from the same
 *  report: how a job number shared in a message is opened, for the app, which draws it over the chat rather than
 *  letting a new browser tab take the reader out of the app. */
export default function ChatPanel({ onClose, startId = '', app = false, onJob, onReduce, updateBar }: {
  onClose: () => void; startId?: string; app?: boolean; onJob?: (page: string, jobId: string) => void;
  // The way back down from the full-screen page to the floating window (report 14dc81ac): given only where the page
  // has a window to go back to, and never in the window itself — see the two buttons in the top row below.
  onReduce?: () => void;
  // The app's "a new version is ready" bar (ChatApp, report 0e8dd0cb): this window covers the whole screen, so the bar
  // has to stand inside it to move the top row down rather than cover its buttons.
  updateBar?: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const phone = usePhone();   // the chat list and the thread as two screens rather than two columns
  /** A chat's name: a group's own, "You" for a chat with yourself (report e307a908), or the other person in a one-to-one. */
  const titleOf = (c: ChatConversation) =>
    c.kind === 'group' ? (c.name || t('chat_group')) : c.self ? t('chat_you') : (who(c.other) || t('chat_gone'));
  /** The last message under a chat's name, on one line — words only, so no formatting marker shows there. */
  const snippet = (m: ChatMessage | null) =>
    !m ? t('chat_no_messages') : m.kind === 'text' ? plainWords(m.body).replace(/\s+/g, ' ') : t(kindKey(m.kind));
  const me = useStore(s => s.me);
  const admin = me?.role === 'admin';
  const [convs, setConvs] = useState<ChatConversation[] | null>(null);   // null = still being asked for
  const [failed, setFailed] = useState(false);
  const [openId, setOpenId] = useState('');
  const [msgs, setMsgs] = useState<ChatMessage[] | null>(null);
  const [jobNos, setJobNos] = useState<ChatJobs>({});                    // which numbers in the words are jobs (ChatWords)
  const [taxNos, setTaxNos] = useState<ChatTaxIds>({});                  // …and which are a client's tax ID (ChatWords)
  const [passNos, setPassNos] = useState<ChatPassports>({});             // …and which are a passport in Our Documents
  const [taxOpen, setTaxOpen] = useState('');                            // the tax ID whose documents are showing, '' for none
  // One and the same close, so the few-second ask behind the panel does not re-hang its Escape listener.
  const closeTax = useCallback(() => setTaxOpen(''), []);
  const [quotes, setQuotes] = useState<ChatQuotes>({});                  // the quoted messages older than this page
  const [reacts, setReacts] = useState<ChatReacts>({});                  // the emojis on this page's messages
  const [replyTo, setReplyTo] = useState('');                            // the message being answered, '' for none
  const [pasted, setPasted] = useState<Shot[]>([]);                      // pictures pasted in and waiting to be sent
  const [bigShot, setBigShot] = useState('');                            // which of those is being shown large, by its id
  const [cropShot, setCropShot] = useState('');                          // …and which is in the crop window (report 1e817873)
  const [docCrop, setDocCrop] = useState(false);                         // the document-upload group's photo in the crop window
  // The document waiting to be named in the document-upload group (report 13fbee78): the file itself, the name
  // the AI suggested as the four boxes now have it (report 595aad91), whether the read is still running, and what
  // to say under the boxes — the reason no name could be suggested, or that what they hold makes no name the
  // protocol takes.
  const [docHold, setDocHold] = useState<DocHold | null>(null);
  const [docParts, setDocParts] = useState<DocParts>(NO_PARTS);
  const [docExt, setDocExt] = useState('');                              // the picked file's own '.pdf', never typed
  const [docReading, setDocReading] = useState(false);
  const [docNote, setDocNote] = useState('');
  const [docGuess, setDocGuess] = useState<DocPart[]>([]);              // the boxes holding a guess (report 972661d6)
  // The Company box's drop-down (report 98453bac): the client master's companies, and whether 'Other…' was picked
  // by hand — the one case the typing box has to show with nothing in it yet.
  const [docCompanies, setDocCompanies] = useState<string[]>([]);
  const [docOther, setDocOther] = useState(false);
  // The Simulation check on that document (report 972661d6, 3rd opening; see recordDocSim): what the read filled the
  // four boxes with and which of the document's dates its date is (4th opening), whether this document is ticked as a
  // test, the tests recorded so far for "Results (N)", the green line once one is recorded, and whether the Results
  // list is open.
  const [docAi, setDocAi] = useState<DocParts>(NO_PARTS);
  const [docDateKind, setDocDateKind] = useState<'expiry' | 'issue' | 'none'>('none');
  const [simOn, setSimOn] = useState(false);
  const [simTotal, setSimTotal] = useState(0);
  const [simNote, setSimNote] = useState('');
  const [simList, setSimList] = useState(false);
  const [picking, setPicking] = useState('');                            // the emoji picker: '' shut, 'draft' = into the typing box, else a message id
  // Forwarding (report 96e28b88): whether messages are being ticked for one, which of them are ticked, whether the
  // picker that chooses the chats is open, and what to say at the foot once a forward has gone.
  const [pickMode, setPickMode] = useState(false);
  const [picks, setPicks] = useState<string[]>([]);
  const [fwdOpen, setFwdOpen] = useState(false);
  const [okMsg, setOkMsg] = useState('');
  // One and the same close, as the tax-ID panel has, so the few-second ask behind the picker does not re-hang
  // its Escape listener.
  const closeFwd = useCallback(() => setFwdOpen(false), []);
  const [hoverId, setHoverId] = useState('');                            // the bubble the mouse is on, or a phone last tapped: its Reply, Forward and smile show
  const [flashId, setFlashId] = useState('');                            // the message a quote has just taken the reader to
  // Changing what has just been said (report d5b1d1de): which message's ⋮ menu is open, which message is being
  // re-typed, and the words so far. `minute` is a clock that moves once a minute, so the minutes left on Edit come
  // down while the window is open.
  const [menuId, setMenuId] = useState('');
  const [menuDown, setMenuDown] = useState(false);   // …and whether it opens under its ⋮ rather than over it (MENU_ROOM)
  // A chat row's own menu (report e307a908): which row's is open and where on the screen it stands, null for none.
  // The handlers are beside `back`, further down.
  const [rowMenu, setRowMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  // A message's words in another language (report 9b579ee4): by message, the translation its bubble is showing — a
  // message showing none is simply absent.
  const [trans, setTrans] = useState<Record<string, Translation>>({});
  const [editId, setEditId] = useState('');
  const [editText, setEditText] = useState('');
  const [minute, setMinute] = useState(() => Date.now());
  const [people, setPeople] = useState<ChatPerson[] | null>(null);       // everyone a chat may be started with
  const [pane, setPane] = useState<'' | 'new' | 'group' | 'members'>('');
  const [delAsk, setDelAsk] = useState(false);                           // the Yes / No strip for deleting the open group (report a409594d)
  const [draft, setDraft] = useState('');
  // The '@' list over the typing box (report 67084d4c): where the '@' stands in what is being typed and what has
  // been typed after it, null for no list, and which of the people offered is the one Enter would put in.
  const [atList, setAtList] = useState<{ at: number; q: string } | null>(null);
  const [atPick, setAtPick] = useState(0);
  const [find, setFind] = useState('');
  // The search box over the chat list (report f57cc430): the words in it, what they found — null while nothing is
  // being searched, which is the chat list back — whether older matches went uncounted past the server's cap,
  // whether a search is out, and whether the last one could not be made.
  const [msgQ, setMsgQ] = useState('');
  const [hits, setHits] = useState<ChatHit[] | null>(null);
  const [hitMore, setHitMore] = useState(false);
  const [hitBusy, setHitBusy] = useState(false);
  const [hitFail, setHitFail] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const foot = useRef<HTMLDivElement | null>(null);
  const openRef = useRef('');   // the open chat for the poll and for loadMsgs's late-answer check
  useEffect(() => { openRef.current = openId; }, [openId]);
  const picker = useRef<HTMLInputElement | null>(null);   // the hidden file box the paperclip presses
  const [rec, setRec] = useState<MediaRecorder | null>(null);   // a voice note being recorded, if any
  const [recSecs, setRecSecs] = useState(0);
  const [notify, setNotify] = useState<boolean | null>(null);   // the notices switch; null = not known yet, so no bell
  // The banners that arrive with the browser shut (report d5b1d1de), allowed one device at a time: the button for
  // them stands beside that bell — on a phone, a banner over the chat list (PushOffer, report ec0c5a76) — and asks
  // the browser nothing until it is pressed.
  const push = useChatPush();
  // The "Install app" button beside that bell — in the app and, since report 96e28b88 opening #3, on a phone's
  // pop-up too — and the sheet it opens for a phone whose browser has no install dialog to offer. `offer.show` is
  // false once Chat IS the installed app.
  const offer = useInstallOffer();
  // The Files and Email doors beside it wear their names in the app on a desk. On a phone-shaped screen they are the icon
  // alone, in the app as in the pop-up: "Install app", both names, the title and the two icons make a row wider than the
  // phone (report 0e8dd0cb). Each keeps its name as its tooltip and for a reader who hears the page.
  const doorWords = app && !phone;
  const [howTo, setHowTo] = useState(false);
  // The sheet asked for by a pop-up before it sent this phone here (installApp: wantInstallHow). Opened on the chat
  // app's own page, which is the whole point: "Add to Home Screen" tapped here saves Chat. Read in an effect rather
  // than as this state's first value, so the one-shot note is not eaten by a paint that is thrown away.
  useEffect(() => { if (app && takeInstallHow()) setHowTo(true); }, [app]);
  const keepRec = useRef(true);   // false = the recording was discarded, so its bytes are dropped on stop
  const recTo = useRef('');       // the chat the recording was started in — where it goes, whatever is open by then
  const replyRef = useRef('');    // the message being answered, for a send that outlives a render
  const recReply = useRef('');    // …and the one the recording was started under, as recTo is its chat
  const typing = useRef<HTMLTextAreaElement | null>(null);               // the typing box, for the cursor an emoji goes at
  const editBox = useRef<HTMLTextAreaElement | null>(null);              // the box a message is being changed in (EDIT_W)
  const pickRef = useRef<HTMLDivElement | null>(null);                   // the picker itself, so a press outside shuts it
  const bubbles = useRef<Record<string, HTMLDivElement | null>>({});     // each message's own box, to be taken to by a quote
  const footAt = useRef('');      // '<chat>:<how many messages>' when the thread was last taken to its foot
  const thread = useRef<HTMLDivElement | null>(null);                    // the thread's scrolling box…
  const [threadBody, setThreadBody] = useState<HTMLDivElement | null>(null);   // …and all it holds, which grows as photos come down
  const keepFoot = useRef(false); // the thread is held at its foot while what it holds grows (report bd101132)
  const threadTop = useRef(0);    // where the thread was last scrolled to, so a scroll can tell up from down
  // How far back the open chat's thread reads (report d0f07722): where it starts, ms — 0 until the server has said,
  // which is then its own four weeks back — and the month the button at its head offers, with the moment that month
  // begins. While a press of it is on its way, `revealAt` is the start it asked for, and `holdAt` the message that was
  // at the top of the thread with where it stood, so the thread stays on that message rather than going to its foot.
  const sinceRef = useRef(0);
  const [older, setOlder] = useState<{ month: string; since: number } | null>(null);
  const [revealing, setRevealing] = useState(false);
  const revealAt = useRef(0);
  const holdAt = useRef<{ id: string; top: number } | null>(null);
  // …and which PAGE of that window the thread is showing (report f57cc430): 1, the newest hundred, everywhere but a
  // chat opened on a search hit, where it is the page the hit's own message is on. Sticky like `since` above, so the
  // few-second ask keeps the reader where the hit put them rather than pulling them back to the newest hundred.
  const pageRef = useRef(1);
  // The message a hit is taking the reader to, '' for none: the thread is put on it as its page lands (see goTo).
  const jumpTo = useRef('');
  // Searches can overlap, so only the latest may fill the list; `searchedQ` holds the words it was asked for, '' with
  // nothing searched, so the same words are never searched twice (report 0c04879b's own way round).
  const findSeq = useRef(0);
  const searchedQ = useRef('');
  const pastedRef = useRef<Shot[]>([]);   // the waiting pictures, for a send and a close that outlive a render
  const shotSeq = useRef(0);              // tells two pictures pasted in the same moment apart
  const docRef = useRef<DocHold | null>(null);   // the waiting document, for a confirm that outlives a render
  const docFor = useRef('');              // the chat it was attached in, so a read's answer lands on nothing else
  const compRef = useRef<string[]>([]);   // the company list, for a read's answer that outlives a render
  useEffect(() => { replyRef.current = replyTo; }, [replyTo]);
  useEffect(() => { pastedRef.current = pasted; }, [pasted]);
  useEffect(() => { docRef.current = docHold; }, [docHold]);
  useEffect(() => { compRef.current = docCompanies; }, [docCompanies]);

  // ── The chat list's width (report 8e638d38; LIST_W above) ─────────────────────────────────────────────────────
  // The width the list was last dragged to, and the drag itself: which pointer holds the edge — a mouse, a finger or a
  // pen, one at a time — where it was pressed, and the width the list had then and has now. Each move is a width of
  // its own, so the column and its writing follow the hand; it is stored once, on letting go, and only if it changed,
  // so a press that goes nowhere (the two of a double-click among them) stores nothing. While the edge is held nothing
  // in the window can be selected, so neither a mouse sweeping across the thread nor a finger resting on the edge
  // picks out any words.
  const [listW, setListW] = useState(readListW);
  const [grabbing, setGrabbing] = useState(false);
  const [edgeHot, setEdgeHot] = useState(false);   // a pointer is on the edge: it is drawn in the accent
  const grab = useRef<{ id: number; x: number; from: number; to: number } | null>(null);
  const edgeTouched = useRef(false);               // the edge has been pressed since this window opened
  // The person's own width (opening #2), asked for once the window knows who is signed in. It wins over this browser's
  // copy, which only drew the first paint, and becomes that copy, so the next window here opens at it. Nothing kept
  // yet: this browser's own width, if the list was ever widened here, is sent up to become the kept one. An edge
  // pressed before the answer lands has had its say and the answer is let go; no answer leaves this browser's copy.
  useEffect(() => {
    if (!me?.userId) return;
    let gone = false;
    api.chatListWidth().then(r => {
      if (gone || edgeTouched.current) return;
      if (r?.width == null) {
        const mine = readListW();
        if (mine !== LIST_W) api.chatSetListWidth(mine).catch(() => { /* the next window sends it again */ });
        return;
      }
      const w = listWidth(Number(r.width) || LIST_W);
      setListW(w);
      keepListW(w);
    }, () => { /* unreachable: this browser's copy stays */ });
    return () => { gone = true; };
  }, [me?.userId]);
  const pressEdge = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || grab.current) return;
    edgeTouched.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* the drag still follows while the pointer is on the edge */ }
    grab.current = { id: e.pointerId, x: e.clientX, from: listW, to: listW };
    setGrabbing(true);
  };
  const dragEdge = (e: React.PointerEvent) => {
    const g = grab.current;
    if (!g || e.pointerId !== g.id) return;
    g.to = listWidth(g.from + e.clientX - g.x);
    setListW(g.to);
  };
  const dropEdge = (e: React.PointerEvent) => {
    const g = grab.current;
    if (!g || e.pointerId !== g.id) return;
    grab.current = null;
    setGrabbing(false);
    if (g.to !== g.from) saveListW(g.to);
  };
  const resetEdge = () => { setListW(LIST_W); saveListW(LIST_W); };

  // ── The pictures waiting above the typing box ───────────────────────────────────────────────────────────────
  // Each one's object URL is let go of the moment the picture leaves the strip — sent, dropped by its ×, or the
  // whole strip cleared — so nothing of a screenshot is left held in the browser after it has gone.
  const dropShot = (id: string) => {
    const s = pastedRef.current.find(x => x.id === id);
    if (s) URL.revokeObjectURL(s.url);
    pastedRef.current = pastedRef.current.filter(x => x.id !== id);
    setPasted(p => p.filter(x => x.id !== id));
    setBigShot(b => (b === id ? '' : b));
    setCropShot(c => (c === id ? '' : c));
  };
  const dropShots = useCallback(() => {
    pastedRef.current.forEach(s => URL.revokeObjectURL(s.url));
    pastedRef.current = [];
    setPasted(p => (p.length ? [] : p));
    setBigShot('');
    setCropShot('');
  }, []);
  // Into the strip: each picture with an object URL to draw it from — made out here, not inside setPasted, since an
  // object URL is a thing taken and React may run what is handed to it twice. A pasted picture comes with the name
  // made for it (pastedName), a picked one with its own (report 1e817873).
  const holdShots = (items: { blob: Blob; name: string }[]) => {
    const held = items.map(x => ({ id: `s${++shotSeq.current}`, blob: x.blob, name: x.name, url: URL.createObjectURL(x.blob) }));
    pastedRef.current = [...pastedRef.current, ...held];
    setPasted(p => [...p, ...held]);
  };
  // A waiting picture cropped (report 1e817873): the cut-out takes its place in the strip — same place, same caption
  // to come — with an object URL of its own, and the old one is let go of. A picture already sent or dropped while
  // the crop window was open has nothing to take the place of, and the cut-out is simply not kept.
  const reshoot = (id: string, blob: Blob, name: string) => {
    const old = pastedRef.current.find(x => x.id === id);
    if (!old) return;
    const next: Shot = { ...old, blob, name, url: URL.createObjectURL(blob) };
    pastedRef.current = pastedRef.current.map(x => (x.id === id ? next : x));
    setPasted(p => p.map(x => (x.id === id ? next : x)));
    URL.revokeObjectURL(old.url);
  };
  // The waiting document let go of: never sent and never filed, so there is nothing to undo. The read may still
  // be running — its answer lands on nothing, since the hold it was for has gone (dropDoc below).
  const dropDoc = useCallback(() => {
    docRef.current = null;
    setDocHold(null);
    setDocParts(NO_PARTS);
    setDocGuess([]);
    setDocExt('');
    setDocNote('');
    setDocReading(false);
    setDocOther(false);   // the next document's Company box opens on the list, not on the typing box
    setDocAi(NO_PARTS);
    setDocDateKind('none');
    setSimOn(false);      // a test is ticked for one document; the next is a real one until ticked
    setSimNote('');
    setDocCrop(false);    // …and a crop window open on it goes with it (report 1e817873)
  }, []);
  // The pop-up closed with pictures still waiting: they were never sent, so they are simply let go of.
  useEffect(() => () => { pastedRef.current.forEach(s => URL.revokeObjectURL(s.url)); }, []);

  // ── The messages ticked for a forward (report 96e28b88) ─────────────────────────────────────────────────────
  // Forward on a bubble ticks that message and opens the picker at once, which is the whole of forwarding one
  // message. Closing the picker leaves the ticking on, so any number of further messages can be ticked and sent
  // together — the reporter asked for "either one or more than one message".
  const endPick = useCallback(() => { setPickMode(false); setPicks([]); setFwdOpen(false); }, []);
  const startFwd = (id: string) => {
    setPicking('');
    setErr('');
    setPickMode(true);
    setPicks([id]);
    setFwdOpen(true);
  };
  const tickMsg = (id: string) => {
    if (picks.includes(id)) { setPicks(v => v.filter(x => x !== id)); return; }
    // The server carries no more than this in one forward, so the cap is said here rather than refused there.
    if (picks.length >= FWD_MAX_MSGS) { setErr(t('chat_fwd_cap', { n: FWD_MAX_MSGS })); return; }
    setErr('');
    setPicks(v => [...v, id]);
  };
  // "Forwarded to N chat(s)" at the foot of the thread, and gone a few seconds later: a forward lands in chats
  // the reader is not looking at, so without this nothing on screen would say it had happened.
  useEffect(() => {
    if (!okMsg) return;
    const away = window.setTimeout(() => setOkMsg(''), OK_MS);
    return () => window.clearTimeout(away);
  }, [okMsg]);

  const open = useMemo(() => (convs || []).find(c => c.id === openId) || null, [convs, openId]);

  // ── Pictures (report 42100db4) ──
  // My own, for the My picture button beside the bell — asked for once as the window opens, and worn by the button —
  // and the open group's, for the Group picture button an administrator has beside Members.
  const [myPic, setMyPic] = useState('');
  useEffect(() => {
    const id = me?.userId || '';
    if (!id) return;
    let live = true;
    api.chatPicture('user', id).then(r => { if (live) setMyPic(String((r && r.url) || '')); }, () => { /* the button offers to set one */ });
    return () => { live = false; };
  }, [me?.userId]);
  const groupPic = usePic('group', open && open.kind === 'group' ? open.id : '', open && open.kind === 'group' ? (open.avatar || '') : '');

  // The Company drop-down's list (report 98453bac), asked for once — the first time the document-upload group is
  // opened, since no other chat names a document. Only by somebody the wizard's own door is open to: chat is open
  // to every member of staff while File Storage is a module like any other, and a refused call pages the admins.
  // Without the list (no such module, or the ask did not land) the Company box stays the typing box it has always
  // been, so nobody loses the box they had.
  const docGroup = !!open?.docGroup;
  const canCompanies = (me?.modules || []).includes('doc_storage');
  useEffect(() => {
    if (!docGroup || !canCompanies || compRef.current.length) return;
    api.docStorageCompanies().then((r: any) => setDocCompanies(r.companies || []), () => {});
  }, [docGroup, canCompanies]);
  // The Simulation check (report 972661d6, 3rd opening — the reporter: "Allow simulation results … for AI to learn how
  // to better label and name documents that are being uploaded to this group"): the Upload Document wizard's own tick
  // box, for administrators alone — users.role, never a person's name — and only where the Document Storage door is
  // open to them as well, since the tests are kept and counted behind it and a refused call pages the admins. The
  // count is asked for when the group is opened.
  const simOk = admin && canCompanies;
  const sim = simOk && simOn;
  useEffect(() => {
    if (!docGroup || !simOk) return;
    api.docStorageSimChecks(1, 25).then((r: any) => setSimTotal(Number(r.total) || 0), () => {});
  }, [docGroup, simOk]);

  // Escape closes the pop-up, as it closes every other dialog here — but the emoji picker first, if it is open,
  // then the pictures waiting above the typing box, then the messages ticked for a forward: Escape shuts what is
  // in front, not the window under it. (A waiting picture shown large, and the forward picker, each close
  // themselves and stop the key there — PhotoBig, DocBig, ForwardTo.)
  // In the app the window is the page, so Escape stops after those three: there is nothing to close it to.
  // The ⋮ menu on a message and the box a message is being re-typed in come first of all (report d5b1d1de):
  // Escape over a half-changed message puts the words back as they were sent and leaves the window where it is.
  const endEdit = useCallback(() => { setEditId(''); setEditText(''); }, []);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A chat row's own menu (report e307a908) is the smallest thing on screen and the nearest, so it goes first.
      if (rowMenu) { setRowMenu(null); return; }
      // The simulation checks' Results list (report 972661d6) stands over the whole window, so it goes first.
      if (simList) { setSimList(false); return; }
      // …then the strip asking whether to delete the open group (report a409594d): Escape answers No — and, once
      // Yes has been pressed, waits for the delete like everything else here.
      if (delAsk) { if (!busy) setDelAsk(false); return; }
      if (menuId) { setMenuId(''); return; }
      if (picking) { setPicking(''); return; }
      if (editId) { endEdit(); return; }
      if (pastedRef.current.length) { dropShots(); return; }
      // …then the document waiting to be named (report 13fbee78): it has been nowhere yet, so Escape simply
      // leaves it behind and the window stays where it is.
      if (docRef.current) { dropDoc(); return; }
      if (pickMode) { endPick(); return; }
      if (!busy && !app) onClose();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [app, busy, delAsk, dropDoc, dropShots, editId, endEdit, endPick, menuId, onClose, pickMode, picking, rowMenu, simList]);

  // A press anywhere but the ⋮ menu shuts it, as one shuts the emoji picker. The ⋮ itself is left alone, so
  // pressing the same one twice closes the menu rather than closing and opening it again.
  useEffect(() => {
    if (!menuId) return;
    const away = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest?.('[data-msg-menu]')) return;
      setMenuId('');
    };
    window.addEventListener('mousedown', away);
    return () => window.removeEventListener('mousedown', away);
  }, [menuId]);

  // The minutes left on Edit, counted down once a minute while the window is open — so the menu's "3 min left"
  // becomes "2 min left" on its own, and Edit goes from the menu as the last of them runs out.
  useEffect(() => {
    const tick = window.setInterval(() => setMinute(Date.now()), MINUTE_MS);
    return () => window.clearInterval(tick);
  }, []);

  // A press anywhere but the picker shuts it. The buttons that OPEN one are left alone, so pressing the same
  // smile twice shuts the picker rather than closing and opening it again.
  useEffect(() => {
    if (!picking) return;
    const away = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null;
      if (pickRef.current?.contains(el) || el?.closest?.('[data-emoji-open]')) return;
      setPicking('');
    };
    window.addEventListener('mousedown', away);
    return () => window.removeEventListener('mousedown', away);
  }, [picking]);

  // While this pop-up is on screen the notices stay quiet; closing it has every red bubble ask again (step 3).
  useEffect(() => {
    countPanels(1);
    return () => { countPanels(-1); window.dispatchEvent(new Event(CHAT_READ_EVENT)); };
  }, []);

  const loadConvs = useCallback(async () => {
    try { const r = await api.chatConversations(); setConvs(r.conversations || []); setFailed(false); return r.conversations || []; }
    catch { setConvs(c => c || []); setFailed(true); return null; }
  }, []);
  // The numbers an answer resolved, kept beside the thread. A job number is a job wherever it is written, so what
  // one chat resolved serves the next; and an answer naming nothing new leaves the list as it was, so the
  // few-second ask redraws no message.
  const mergeJobs = useCallback((got?: ChatJobs | null) => {
    const found = Object.entries(got || {});
    if (!found.length) return;
    setJobNos(j => (found.some(([no, v]) => !j[no] || j[no].id !== v.id || j[no].closed !== v.closed || j[no].client !== v.client) ? { ...j, ...got } : j));
  }, []);
  // The tax IDs an answer resolved, kept beside the thread the same way and for the same reason: a tax ID is that
  // client's wherever it is written, and an answer naming nothing new redraws no message.
  const mergeTax = useCallback((got?: ChatTaxIds | null) => {
    const found = Object.entries(got || {});
    if (!found.length) return;
    setTaxNos(v => (found.some(([no, c]) => !v[no] || v[no].clientId !== c.clientId || v[no].name !== c.name) ? { ...v, ...got } : v));
  }, []);
  // …and the passport numbers an answer resolved, kept the same way again: a passport number is that passport
  // wherever it is written, and an answer naming nothing new redraws no message.
  const mergePass = useCallback((got?: ChatPassports | null) => {
    const found = Object.entries(got || {});
    if (!found.length) return;
    setPassNos(v => (found.some(([no, d]) => !v[no] || v[no].fileId !== d.fileId || v[no].person !== d.person) ? { ...v, ...got } : v));
  }, []);
  const loadMsgs = useCallback(async (id: string) => {
    if (!id) return;
    const from = sinceRef.current;   // where the thread starts, the same on every ask until a month is brought back
    const pg = pageRef.current;      // …and which page of it: the newest hundred, or the one a search hit is on
    try {
      const r = await api.chatMessages(id, pg, from);
      // An answer for a start that has since moved — a month asked for while this ask was out, or a hit pressed in
      // this very chat — is dropped as an answer for another chat is: the ask that moved it brings the thread as it
      // now stands.
      if (openRef.current === id && sinceRef.current === from && pageRef.current === pg) {
        const rows = r.rows || [];
        sinceRef.current = Number(r.since) || from;
        // The month a press asked for has come. The thread stays on the message that was at its top (holdAt), so
        // the count it now has is put down as the one its foot is already at, and nobody is taken there.
        if (from > 0 && from === revealAt.current) {
          revealAt.current = 0;
          footAt.current = `${id}:${rows.length}`;
          setRevealing(false);
        }
        setOlder(r.olderMonth && r.olderSince ? { month: r.olderMonth, since: r.olderSince } : null);
        setMsgs(rows);
        mergeJobs(r.jobs);
        mergeTax(r.taxIds);
        mergePass(r.passports);
        // Both of these are the whole truth for the page just read, so they replace what was there rather
        // than adding to it: a reaction a colleague has just taken off has to disappear here too.
        setQuotes(r.quotes || {});
        setReacts(r.reacts || {});
      }
    } catch { if (openRef.current === id) setMsgs(m => m || []); }
  }, [mergeJobs, mergeTax, mergePass]);

  // At once, then every few seconds while the pop-up is open: the chat list, and the open thread with it.
  // The notices switch comes down with them, from the same door the cards ask, so the bell below and the cards
  // themselves never disagree (step 3).
  useEffect(() => {
    loadConvs();
    api.chatPeople().then(r => setPeople(r.people || []), () => setPeople([]));
    api.chatNotify().then(r => setNotify(r.on !== false), () => setNotify(null));
  }, [loadConvs]);
  const markRead = useCallback(async (id: string) => {
    try { await api.chatMarkRead(id); } catch { /* the next tick tries again */ }
    // Still null = the list has yet to land (a pop-up opened straight on a chat from a notice): leave it null, or
    // this would answer for it with an empty list and the chats would read as "No chats yet".
    setConvs(cs => (cs ? cs.map(c => (c.id === id ? { ...c, unread: 0 } : c)) : cs));
  }, []);
  useEffect(() => {
    const iv = setInterval(async () => {
      const was = openRef.current;   // the chat on screen as the list was asked for
      const list = await loadConvs();
      const id = openRef.current;
      if (!id) return;
      // A chat that was on screen when the list was asked for and is not in it has gone from under the reader — a
      // group an administrator has deleted (report a409594d), or one they were taken out of. Its thread would only be
      // refused now, and a refusal pages the admins, so the window goes back to the list instead of asking for it.
      if (list && id === was && !list.some(c => c.id === id)) { back(); return; }
      await loadMsgs(id);
      // The chat on screen is being read as it arrives, so it never shows itself as unread.
      if ((list || []).some(c => c.id === id && c.unread > 0)) markRead(id);
    }, POLL_MS);
    return () => clearInterval(iv);
    // back() sets nothing but state and refs, so the one this was set up with does what the newest would.
  }, [loadConvs, loadMsgs, markRead]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── The search box over the chat list (report f57cc430) ───────────────────────────────────────────────────────
  // What is searched is settled on the server (routes/chat.ts, /search): the words of every message of every chat
  // this person is in, and the names of the files, photos and voice notes those messages carry. Searches overlap as
  // the words are typed, so only the latest may fill the list.
  const runFind = useCallback(async (term: string) => {
    const tq = term.trim();
    if (tq.length < FIND_MIN) return;
    const seq = ++findSeq.current;
    searchedQ.current = tq;
    setHitBusy(true);
    setHitFail(false);
    try {
      const r = await api.chatSearch(tq);
      if (seq !== findSeq.current) return;
      setHits(r.rows || []);
      setHitMore(!!r.more);
    } catch {
      if (seq !== findSeq.current) return;
      setHits([]);
      setHitMore(false);
      setHitFail(true);
    } finally { if (seq === findSeq.current) setHitBusy(false); }
  }, []);
  // The results let go of, the box's own words left alone: an answer still on its way lands on nothing. It serves
  // both a box emptied back below two letters and the Escape that empties it.
  const dropHits = useCallback(() => {
    findSeq.current++;
    searchedQ.current = '';
    setHits(null);
    setHitMore(false);
    setHitBusy(false);
    setHitFail(false);
  }, []);
  // 300ms after the last key, as every other search box in the system searches since report 0c04879b: words a search
  // has already been made for are not searched again, and fewer than two letters bring the chat list back.
  useEffect(() => {
    const h = window.setTimeout(() => {
      const tq = msgQ.trim();
      if (tq === searchedQ.current) return;
      if (tq.length >= FIND_MIN) void runFind(tq);
      else dropHits();
    }, FIND_MS);
    return () => window.clearTimeout(h);
  }, [msgQ, runFind, dropHits]);
  // Whether the hits take the chat list's place: from the second letter, so the spinner shows where the answer will,
  // and the list is never half-replaced while somebody is still typing the first letter.
  const finding = msgQ.trim().length >= FIND_MIN;

  // Leaving a chat leaves how far back its thread was read with it (report d0f07722): the next chat opens on its own
  // four weeks, and a month still on its way for the last one lands on nothing, as its answer is dropped.
  const leaveWindow = () => {
    sinceRef.current = 0;
    revealAt.current = 0;
    holdAt.current = null;
    pageRef.current = 1;   // …and on the newest hundred of it, whatever page a hit last opened (report f57cc430)
    jumpTo.current = '';
    setOlder(null);
    setRevealing(false);
  };
  // Opening a chat reads it: the thread comes down and the watermark moves, so its unread count empties.
  // `at` is a message a search hit is opening the chat ON (report f57cc430): the window and the page it is in, as
  // the hit itself came with them, and the message to put the thread on once they land. Nothing passed = the chat
  // opens as it always did, at its newest.
  const show = async (id: string, at?: { messageId: string; since: number; page: number }) => {
    // Set before the load, not by the next render: loadMsgs drops an answer for a chat no longer open.
    openRef.current = id;
    setOpenId(id);
    setPane('');
    setDelAsk(false);   // the last group's delete question is not this chat's
    setErr('');
    setMsgs(null);
    leaveWindow();   // a chat opens on its own four weeks, whatever months were brought back in the last one
    if (at) {
      sinceRef.current = at.since;
      pageRef.current = Math.max(1, Math.floor(at.page) || 1);
      jumpTo.current = at.messageId;
    }
    // Another chat's quotes, emojis, half-made answer, pasted pictures and ticked messages are none of this
    // one's business — a forward only ever carries messages of the chat it was started in.
    setQuotes({});
    setReacts({});
    setReplyTo('');
    setPicking('');
    setAtList(null);
    setMenuId('');
    endEdit();      // a half-changed message of the chat being left is left as it was sent (report d5b1d1de)
    dropShots();
    dropDoc();      // …and a document still waiting to be named belongs to the chat it was attached in
    endPick();
    await loadMsgs(id);
    markRead(id);
  };
  // The back arrow at the head of the window, which only a phone has (report 8c5f3514): out of the thread and onto
  // the chat list, the other of the two screens. It leaves the same things behind as opening a chat does — a
  // half-written answer, the picker, a pasted picture — since what comes next is a chat, this one or another.
  const back = () => {
    openRef.current = '';
    setOpenId('');
    setDelAsk(false);   // a delete question goes with the group it was about (report a409594d)
    setMsgs(null);
    leaveWindow();
    setQuotes({});
    setReacts({});
    setReplyTo('');
    setPicking('');
    setAtList(null);
    setMenuId('');
    endEdit();
    dropShots();
    dropDoc();
    endPick();
  };

  // ── Marked unread by hand (report e307a908) ──
  // Calem: "allow messages to be marked unread if the user has not yet responded but obviously the two blue ticks
  // still show at the sender's side." A right-click on a chat's row — a press held on a phone, which has no right
  // button — opens a small menu under the pointer: Mark as unread on a chat with nothing unread, Mark as read on one
  // with something. The mark itself is the server's (routes/chat.ts, the unread door) and never moves the read
  // watermark the sender's ticks are drawn from, so those stay blue. The open chat is closed first: the window reads
  // whatever chat is open as it arrives (the few-second ask above) and would read the mark straight back off it. A
  // chat with yourself is never unread, so its row offers nothing. The red count on the chat button asks again at
  // once either way (CHAT_READ_EVENT), rather than at its next half-minute. (`rowMenu` itself is declared with the
  // window's other state above, where the Escape handler can see it.)
  const pressTimer = useRef(0);                 // the held press that is about to open the menu
  const pressAt = useRef({ x: 0, y: 0 });       // …and where it began, so a finger that moves on is a scroll, not a press
  const pressed = useRef(false);                // a held press opened the menu: the click a phone sends on release is not an open
  const openRowMenu = (c: ChatConversation, x: number, y: number) => { if (!c.self) setRowMenu({ id: c.id, x, y }); };
  const rowPress = (c: ChatConversation, e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return;      // a mouse has its right button
    window.clearTimeout(pressTimer.current);
    const { clientX: x, clientY: y } = e;
    pressAt.current = { x, y };
    pressTimer.current = window.setTimeout(() => { pressed.current = true; openRowMenu(c, x, y); }, LONG_PRESS_MS);
  };
  const rowCancel = () => window.clearTimeout(pressTimer.current);
  const rowMove = (e: React.PointerEvent) => {
    if (Math.abs(e.clientX - pressAt.current.x) > PRESS_SLACK || Math.abs(e.clientY - pressAt.current.y) > PRESS_SLACK) rowCancel();
  };
  const rowRelease = () => {
    rowCancel();
    // The click a phone sends as the finger lifts comes within a moment; after that a press is a press again.
    if (pressed.current) window.setTimeout(() => { pressed.current = false; }, PRESS_CLICK_MS);
  };
  const rowClick = (id: string) => {
    if (pressed.current) { pressed.current = false; return; }
    void show(id);
  };
  const markUnread = async (id: string) => {
    setRowMenu(null);
    if (openRef.current === id) back();
    try {
      const r = await api.chatMarkUnread(id);
      setConvs(cs => (cs ? cs.map(c => (c.id === id ? { ...c, unread: Number(r?.unread) || 0 } : c)) : cs));
      window.dispatchEvent(new Event(CHAT_READ_EVENT));
    } catch { setErr(t('chat_mark_failed')); }
  };
  const markReadByHand = async (id: string) => {
    setRowMenu(null);
    await markRead(id);
    window.dispatchEvent(new Event(CHAT_READ_EVENT));
  };
  // A press anywhere but the row menu shuts it, as one shuts the ⋮ menu on a message. The pointer's own press, not
  // the mouse's: a phone sends mouse events of its own as the finger that held the row lifts, and those would shut
  // the menu the moment it opened.
  useEffect(() => {
    if (!rowMenu) return;
    const away = (e: PointerEvent) => {
      const el = e.target as HTMLElement | null;
      if (el?.closest?.('[data-row-menu]')) return;
      setRowMenu(null);
    };
    window.addEventListener('pointerdown', away);
    return () => window.removeEventListener('pointerdown', away);
  }, [rowMenu]);

  // The newest message is the one to read, so the thread opens at its foot and goes back to it as each message
  // arrives — but only then. The few-second ask hands back the same hundred messages in a new list, and taking
  // the reader to the foot for that would undo a quote that has just taken them to an older message, and pull
  // the thread from under anybody reading back through it.
  useEffect(() => {
    const now = `${openId}:${(msgs || []).length}`;
    if (now === footAt.current) return;
    footAt.current = now;
    foot.current?.scrollIntoView({ block: 'end' });
    keepFoot.current = true;
    threadTop.current = thread.current ? thread.current.scrollTop : 0;
    holdAt.current = null;   // a month still on its way lands above a reader now at the foot, with no top to hold
  }, [msgs, openId]);
  // …and it STAYS at its foot while the thread grows after that (report bd101132: "Ensure that the most recent
  // message, when you click on a user or group, is showing"). The words come down with the messages, but each photo
  // and voice note is fetched on its own afterwards (ChatAttachment), and every one that lands made the thread taller
  // and pushed the newest message below the window. So whenever what the thread holds changes size, the thread is
  // taken back down to its foot — as long as the reader has not scrolled up since the chat was opened or a message
  // last arrived. Only the thread itself moves: scrollIntoView would move the boxes around it as well, and this can
  // run while somebody is typing.
  useLayoutEffect(() => {
    if (!threadBody) return;
    const ro = new ResizeObserver(() => {
      const el = thread.current, f = foot.current;
      if (!keepFoot.current || !el || !f) return;
      const under = belowFoot(el, f);
      if (under > 0) { el.scrollTop += under; threadTop.current = el.scrollTop; }
    });
    ro.observe(threadBody);
    return () => ro.disconnect();
  }, [threadBody]);
  // Scrolling UP lets go of it, as a quote taking the reader to an older message does (goTo): reading back is never
  // pulled down. Only a move up counts — a photo landing pushes the foot further down and never moves the thread up —
  // and only once the thread has really left its foot, so the half-pixels a scroll lands on, and the thread settling
  // back as something in it gets shorter, let go of nothing.
  const threadScrolled = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget, f = foot.current;
    const up = el.scrollTop < threadTop.current;
    threadTop.current = el.scrollTop;
    if (up && f && belowFoot(el, f) > FOOT_SLACK) keepFoot.current = false;
  };
  // The button at the head of the thread (report d0f07722): the thread reaches back to the first day of the month it
  // names, and stays on the message that was at its top rather than going to its foot. The month comes down through
  // the ordinary ask (loadMsgs), which knows it for this press's by the start it went with — and should that ask
  // fail, the few-second ask goes with the start already moved and finishes the job.
  const showOlder = () => {
    const id = openRef.current;
    if (!id || !older || revealing) return;
    const top = msgs && msgs.length ? msgs[0] : null;
    const el = top ? bubbles.current[top.id] : null, box = thread.current;
    holdAt.current = top && el && box ? { id: top.id, top: el.getBoundingClientRect().top - box.getBoundingClientRect().top } : null;
    keepFoot.current = false;   // a thread growing above the reader never pulls them down to its foot (see keepFoot)
    sinceRef.current = older.since;
    revealAt.current = older.since;
    setRevealing(true);
    loadMsgs(id);
  };
  // …and once the month has landed above that message, the thread is put back on it before the browser paints, so
  // the reader is left where they were reading and scrolls up into the month brought back.
  useLayoutEffect(() => {
    const h = holdAt.current;
    if (!h || revealAt.current) return;   // nothing held, or the month is still on its way
    holdAt.current = null;
    const el = bubbles.current[h.id], box = thread.current;
    if (!el || !box) return;
    box.scrollTop += el.getBoundingClientRect().top - box.getBoundingClientRect().top - h.top;
    threadTop.current = box.scrollTop;
  }, [msgs]);

  // Opened from a notice (step 3): that chat is the one showing, read as if it had been clicked in the list. The
  // thread comes down beside the list, so the name at its head fills in the moment the list lands.
  useEffect(() => { if (startId) show(startId); }, [startId]);   // eslint-disable-line react-hooks/exhaustive-deps

  // The bell in the header: this person's own notices, on or off, wherever they are signed in. The switch is set
  // before the server answers so the bell never lags the press; a refusal puts it back and says so.
  const flipNotify = async () => {
    if (notify === null) return;
    const want = !notify;
    setNotify(want);
    setErr('');
    try { await api.chatSetNotify(want); }
    catch { setNotify(!want); setErr(t('chat_notify_failed')); }
  };

  // A browser that refused the banners, or a subscription that could not be kept, says so at the foot of the
  // window where every other refusal here is said (report d5b1d1de). A browser whose answer was "never" cannot be
  // asked again by any page, so the words name the one way back: its own site settings.
  useEffect(() => {
    if (push.err) setErr(t(push.err === 'blocked' ? 'chat_push_blocked' : 'chat_push_failed'));
  }, [push.err]);   // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!openId || busy) return;
    // A document waiting to be named in the document-upload group goes on Send too, under the name in its box —
    // the same thing its own Confirm does, so whichever of the two is pressed does what the reader expects.
    if (docRef.current) { await confirmDoc(); return; }
    // Pictures pasted in are waiting above the typing box rather than already gone, so Send is what posts them,
    // and what is typed goes with them as their caption (sendShots). With none waiting, Send is the words alone.
    if (pastedRef.current.length) { await sendShots(); return; }
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setErr('');
    try {
      const r = await api.chatSend(openId, body, replyTo);
      setDraft('');
      setReplyTo('');
      setPicking('');
      setAtList(null);
      setMsgs(m => [...(m || []), r.message]);
      mergeJobs(r.jobs);   // a job number just typed is live at once, not at the next ask
      mergeTax(r.taxIds);  // …and so is a tax ID
      mergePass(r.passports);   // …and so is a passport number
      const quoted = r.quotes;
      if (quoted) setQuotes(q => ({ ...q, ...quoted }));   // and the quote of what it answers, with it
      loadConvs();
    } catch (e: any) {
      setErr(e?.payload?.code === 'too_long' ? t('chat_too_long') : t('chat_send_failed'));
    } finally { setBusy(false); }
  };

  // ── Changing what you have just said (report d5b1d1de) ──────────────────────────────────────────────────────
  // How long the sender may still change this message, in whole minutes, 0 once the window has run out. Counted
  // against `minute`, the once-a-minute clock above, so it comes down on its own while the window is open.
  const minsLeft = (m: ChatMessage): number =>
    Math.max(0, Math.ceil((EDIT_WINDOW_S - (minute / 1000 - m.createdAt)) / 60));
  /** …and how many SECONDS, off the live clock rather than that once-a-minute one. Only Save reads this, and it
   *  is why a change the server would refuse is never sent: a refusal of ours pages the admins on Telegram, so
   *  the window running out has to be caught here rather than there. */
  const secsLeft = (m: ChatMessage): number => Math.max(0, EDIT_WINDOW_S - (Date.now() / 1000 - m.createdAt));
  /** May the signed-in reader still change this message? Their own typed words, inside the window — the server
   *  holds the same line by its own clock, so this only decides what is OFFERED. */
  const canEdit = (m: ChatMessage): boolean => m.senderId === me.userId && m.kind === 'text' && minsLeft(m) > 0;
  const startEdit = (m: ChatMessage) => {
    setMenuId('');
    setErr('');
    setPicking('');
    setEditId(m.id);
    setEditText(m.body);
  };
  // The new words replace the old ones in the thread at once, marked edited — for the sender here and, at their
  // next few-second ask, for everybody else, because the message's own words are what was changed. No banner and
  // no notice goes out: nothing new has been said to anybody.
  const saveEdit = async (msgId: string) => {
    if (!openId || busy) return;
    const body = editText.replace(/\r\n/g, '\n').trim();
    if (!body) { setErr(t('chat_edit_empty')); return; }
    // The fifteen minutes gone while the box stood open: the box closes and nothing is sent.
    const was = (msgs || []).find(x => x.id === msgId);
    if (was && secsLeft(was) <= 0) { setErr(t('chat_edit_too_late')); endEdit(); return; }
    setBusy(true);
    setErr('');
    try {
      const r = await api.chatEdit(openId, msgId, body);
      setMsgs(list => (list || []).map(x => (x.id === msgId ? { ...x, body: r.body, editedAt: r.editedAt } : x)));
      mergeJobs(r.jobs);    // a job number just typed into it is live at once, as it is on a send
      mergeTax(r.taxIds);   // …and so is a tax ID
      mergePass(r.passports);   // …and so is a passport number
      endEdit();
      loadConvs();          // the chat list shows the last line, which may be the one just changed
    } catch (e: any) {
      const code = e?.payload?.code;
      setErr(code === 'too_late' ? t('chat_edit_too_late') : code === 'too_long' ? t('chat_too_long') : t('chat_edit_failed'));
      if (code === 'too_late') endEdit();
    } finally { setBusy(false); }
  };
  // The box as tall as the words in it (report 2fa9001f, EDIT_W), worked out afresh with every key and never shorter
  // than the rows it opens at — until the whole bubble, Save and Cancel under the box included, fills what the thread
  // shows. Past that the words scroll inside it, and its corner still drags it taller. To be measured the box goes back
  // to its rows for a moment, which can shorten the thread under a reader at its foot, so the thread is put back where
  // it stood: otherwise it would jump, and let go of its foot (see keepFoot).
  useLayoutEffect(() => {
    const el = editBox.current, list = thread.current;
    if (!el || !list) return;
    const top = list.scrollTop;
    el.style.height = 'auto';
    const least = el.offsetHeight;
    const words = el.scrollHeight + el.offsetHeight - el.clientHeight;   // every line of them, and the box's own edge
    const rest = (bubbles.current[editId]?.offsetHeight ?? least) - least;   // the bubble's padding, Save and Cancel, a quote
    const pad = getComputedStyle(list);
    const most = list.clientHeight - parseFloat(pad.paddingTop) - parseFloat(pad.paddingBottom) - rest;
    el.style.height = `${Math.max(least, Math.min(words, most))}px`;
    list.scrollTop = top;
  }, [editId, editText]);

  // ── Taking a message back altogether (report 2aafc95e) ──────────────────────────────────────────────────────
  /** May the signed-in reader take this whole message back? The same person and the same window as Edit — but
   *  ANY kind, which is the whole point of it: Edit refuses a file, a photo and a voice note, so until now
   *  nothing that carried one could be taken back at all. An ADMINISTRATOR is not held to the fifteen minutes
   *  and need not be the writer (opening #4 of the report): any message in a chat they are in, at any age. */
  const canDelete = (m: ChatMessage): boolean => admin || (m.senderId === me.userId && minsLeft(m) > 0);
  /** …and is THIS removal the administrator's rather than the writer's own second thoughts inside the window?
   *  Nothing is counting down on it, so no minutes are written on it and it is asked about in its own words. */
  const adminDelete = (m: ChatMessage): boolean => admin && !(m.senderId === me.userId && minsLeft(m) > 0);
  // It disappears for everybody as if it had never been sent, so it is asked about first. Then it leaves this
  // thread at once and everybody else's at their next few-second ask — and whatever on screen was still pointing
  // at it goes with it: the box it was being re-typed in, the answer being written to it, its tick in a forward,
  // its emoji picker.
  const delMsg = async (m: ChatMessage) => {
    if (!openId || busy) return;
    setMenuId('');
    const byAdmin = adminDelete(m);
    // The fifteen minutes gone while the menu stood open: nothing is sent at all, exactly as Save sends nothing
    // once they have — a refusal of ours pages the admins on Telegram. No clock is held against an admin, so
    // that guard is theirs to step past, and the asking says so.
    if (!byAdmin && secsLeft(m) <= 0) { setErr(t('chat_delete_too_late')); return; }
    if (!window.confirm(t(byAdmin ? 'chat_delete_confirm_admin' : 'chat_delete_confirm'))) return;
    setBusy(true);
    setErr('');
    try {
      await api.chatDelete(openId, m.id);
      setMsgs(list => (list || []).filter(x => x.id !== m.id));
      if (editId === m.id) endEdit();
      if (replyTo === m.id) setReplyTo('');
      setPicks(v => (v.includes(m.id) ? v.filter(x => x !== m.id) : v));
      setPicking(p => (p === m.id ? '' : p));
      loadConvs();   // the chat list shows the last line, which may be the one just taken back
    } catch (e: any) {
      setErr(e?.payload?.code === 'too_late' ? t('chat_delete_too_late') : t('chat_delete_failed'));
    } finally { setBusy(false); }
  };

  // ── A message read in another language (report 9b579ee4) ───────────────────────────────────────────────────────
  /** Has this message words to translate? Any message's, the reader's own or not — but not one being re-typed, whose
   *  words are a box for now. */
  const canTrans = (m: ChatMessage): boolean => editId !== m.id && hasWords(m.body);
  // The ⋮ menu opened, or shut again. It opens upwards over the messages above, as it always has, unless its message
  // is too near the top of the thread for it — there the thread would cut its top rows off, so it opens downwards.
  const flipMenu = (id: string, at: HTMLElement) => {
    if (menuId === id) { setMenuId(''); return; }
    const box = thread.current?.getBoundingClientRect();
    const b = at.getBoundingClientRect();
    setMenuDown(!!box && b.top - box.top < MENU_ROOM && box.bottom - b.bottom > b.top - box.top);
    setMenuId(id);
  };
  // The words asked for in one language and drawn under them in the bubble. A translation already made this session
  // for these very words comes back at once; otherwise the bubble shows it being made and then the translation, or
  // that it could not be made, which another press asks again. An answer that lands after the reader has asked for the
  // other language, put it away, or the words have since changed, lands on nothing.
  const translate = async (m: ChatMessage, to: ChatLang) => {
    setMenuId('');
    if (!openId) return;
    const src = m.body;
    const key = `${m.id}:${to}`;
    const kept = TRANSLATED.get(key);
    if (kept && kept.src === src) {
      const done: Translation = { to, src, from: kept.from, text: kept.text, state: 'done' };
      setTrans(v => ({ ...v, [m.id]: done }));
      return;
    }
    let from = langOfWords(src);
    const asked: Translation = { to, src, from, text: '', state: 'busy' };
    setTrans(v => ({ ...v, [m.id]: asked }));
    let text = '';
    try {
      const r = await api.chatTranslate(openId, m.id, to);
      if (r?.ok) { text = String(r.translated || '').trim(); from = r.from || from; }
    } catch { /* said in the bubble, as a translation not made */ }
    if (text) TRANSLATED.set(key, { src, text, from });
    const got: Translation = { ...asked, from, text, state: text ? 'done' : 'failed' };
    setTrans(v => (v[m.id] === asked ? { ...v, [m.id]: got } : v));
  };
  /** The translation under a bubble put away. The one made stays kept, so asking again brings it straight back. */
  const hideTrans = (id: string) => setTrans(v => {
    if (!v[id]) return v;
    const next = { ...v };
    delete next[id];
    return next;
  });

  // While a chat is open on this device, a banner for it is not wanted on this very screen (report d5b1d1de): the
  // window says so, and pushHere troubles the server only when this device has actually allowed banners — and then
  // at most once every twenty seconds, however often the thread itself asks.
  useEffect(() => {
    if (!openId || !push.on) return;
    pushHere(openId);
    const tick = window.setInterval(() => pushHere(openId), POLL_MS);
    return () => window.clearInterval(tick);
  }, [openId, push.on]);

  // ── Emojis, and answering one message in particular ─────────────────────────────────────────────────────────
  // An emoji from the picker goes in where the cursor is, so the words either side stay put, and the cursor
  // lands after it so another can follow straight away. The picker stays open: people send more than one.
  const putEmoji = (e: string) => {
    const box = typing.current;
    const from = box ? box.selectionStart : draft.length;
    const to = box ? box.selectionEnd : draft.length;
    setDraft(`${draft.slice(0, from)}${e}${draft.slice(to)}`.slice(0, MAX_BODY));
    requestAnimationFrame(() => {
      const el = typing.current;
      if (!el) return;
      el.focus();
      const pos = Math.min(from + e.length, el.value.length);
      el.setSelectionRange(pos, pos);
    });
  };

  // ── The formatting markers, and naming a colleague (report 67084d4c) ────────────────────────────────────────
  // Each of these only ever edits what is in the typing box: the message still goes to the server as the words it
  // is, markers and all, and the thread is what draws them (ChatWords above).
  /** The cursor put where the box has just been written to, once React has drawn the new words. */
  const caretTo = (from: number, to = from) => requestAnimationFrame(() => {
    const el = typing.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(Math.min(from, el.value.length), Math.min(to, el.value.length));
  });
  // Bold, Underline, Strikethrough and Italic: the marker on both sides of what is selected, which stays
  // selected, so a second kind can be put round it as well. With nothing selected the pair goes in at the cursor
  // and the cursor lands between the two, so what is typed next is inside them.
  const wrapMark = (mark: string) => {
    const box = typing.current;
    const from = box ? box.selectionStart : draft.length;
    const to = box ? box.selectionEnd : draft.length;
    const inner = draft.slice(from, to);
    setDraft(`${draft.slice(0, from)}${mark}${inner}${mark}${draft.slice(to)}`.slice(0, MAX_BODY));
    setAtList(null);
    caretTo(from + mark.length, from + mark.length + inner.length);
  };
  // Bullet points and numbered points: the marker at the head of every line the selection touches, the numbers
  // counted from one. A line already carrying one of the two swaps it rather than taking a second.
  const listMark = (kind: 'ul' | 'ol') => {
    const box = typing.current;
    const from = box ? box.selectionStart : draft.length;
    const to = box ? box.selectionEnd : from;
    const head = from > 0 ? draft.lastIndexOf('\n', from - 1) + 1 : 0;
    const ends = draft.indexOf('\n', to);
    const tail = ends < 0 ? draft.length : ends;
    const done = draft.slice(head, tail).split('\n')
      .map((ln, i) => `${kind === 'ol' ? `${i + 1}.` : '-'} ${ln.replace(/^[ \t]*(?:-[ \t]+|\d{1,3}\.[ \t]+)/, '')}`)
      .join('\n');
    setDraft(`${draft.slice(0, head)}${done}${draft.slice(tail)}`.slice(0, MAX_BODY));
    setAtList(null);
    caretTo(head + done.length);
  };
  // What is typed after an '@' narrows the list of staff it offers; choosing somebody writes "@Name " over that
  // very '@', so the words either side stay where they are. Only words are written — the thread draws the name
  // bold in that person's colour because it IS their name, not because anything was marked up for it.
  const onDraft = (v: string, caret: number) => {
    setDraft(v);
    const m = AT_TOKEN.exec(v.slice(0, caret));
    setAtList(m ? { at: caret - m[1].length - 1, q: m[1] } : null);
    setAtPick(0);
  };
  const putMention = (p: ChatPerson) => {
    const a = atList;
    if (!a) return;
    const put = `@${who(p)} `;
    setDraft(`${draft.slice(0, a.at)}${put}${draft.slice(a.at + 1 + a.q.length)}`.slice(0, MAX_BODY));
    setAtList(null);
    caretTo(a.at + put.length);
  };

  // An emoji ON a colleague's message: the caller goes on it, or comes off it if they were already on, and the
  // server answers that message's whole fresh row of chips. A refusal leaves the chips alone and the thread is
  // read again, so what is drawn is never a guess.
  const react = async (msgId: string, emoji: string) => {
    const to = openRef.current;
    if (!to) return;
    setPicking('');
    try {
      const r = await api.chatReact(to, msgId, emoji);
      if (openRef.current === to) setReacts(v => ({ ...v, [msgId]: r.reacts || {} }));
    } catch { loadMsgs(to); }
  };
  // ── The forward itself (report 96e28b88) ───────────────────────────────────────────────────────────────────
  // The ticked messages of the open chat, copied into each chat ticked in the picker, with the forwarder's own
  // comment after them if they wrote one. It goes nowhere outside this system: the picker offers chats alone.
  // A refusal leaves the ticks and the picker where they are, with the reason in the picker, so it can simply be
  // pressed again; what worked is said at the foot of the thread, since the copies land out of sight.
  const doForward = async (to: string[], comment: string) => {
    const from = openRef.current;
    if (!from || !picks.length || !to.length || busy) return;
    setBusy(true);
    setErr('');
    try {
      const r = await api.chatForward(from, picks, to, comment);
      endPick();
      setOkMsg(t('chat_fwd_done', { n: Number(r?.conversations) || to.length }));
      loadConvs();
      // Forwarded into the chat being read as well: its copies are there at once rather than at the next ask.
      if (to.includes(from)) loadMsgs(from);
    } catch (e: any) {
      const code = e?.payload?.code;
      setErr(code === 'too_long' ? t('chat_too_long')
        : code === 'too_many' ? t('chat_fwd_cap', { n: FWD_MAX_MSGS })
        : t('chat_fwd_failed'));
    } finally { setBusy(false); }
  };

  // A quote is pressable: it takes the reader to the message it quotes and marks it for a moment. One further
  // back than this page reaches has nothing on screen to be taken to, so it shows its words and stays put.
  // `smooth` glides there, which is a press inside a thread already on screen; a search hit, whose thread has just
  // been drawn with the message in it, goes straight there instead, so the reader never watches a whole month of
  // messages scroll past (report f57cc430).
  const goTo = (id: string, smooth = true) => {
    const el = bubbles.current[id];
    if (!el) return;
    keepFoot.current = false;   // let go of the foot first, or a photo landing mid-glide would pull the reader back down
    el.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
    setFlashId(id);
    window.setTimeout(() => setFlashId(f => (f === id ? '' : f)), FLASH_MS);
  };
  // …and a search hit's own message, once the page it is on has landed (report f57cc430): the thread is put on it and
  // it is marked exactly as a quote marks one, so the words that were searched for are the ones under the reader's
  // eye. One try, as the page lands — a message deleted between the search and the press is no longer there to be
  // taken to, and the thread is simply left where it opened.
  useEffect(() => {
    if (!msgs || !jumpTo.current) return;
    const id = jumpTo.current;
    jumpTo.current = '';
    if (bubbles.current[id]) goTo(id, false);
    else setErr(t('chat_original_gone'));
  }, [msgs]);   // eslint-disable-line react-hooks/exhaustive-deps
  // A press on a hit: the chat opens on the hit's own window and page, and the thread is then put on the message.
  const goToHit = (h: ChatHit) => {
    void show(h.conversationId, { messageId: h.messageId, since: h.since, page: h.page });
  };
  /**
   * What a quoted message shows: who wrote it, and its first words. The name itself is black like every other
   * word here; the writer's colour is kept for the bar down the side of the quote, which is the one coloured
   * thing left outside a name bubble (reports c05bc176, b4806261 — see PersonBubble). It comes from this
   * page's own rows when the quoted message is on the page, else from `quotes`, which the server fills for the
   * ones further back. null when it is neither — the original has gone, and the bubble says so.
   */
  const quoteOf = (id: string) => {
    if (!id) return null;
    const here = (msgs || []).find(m => m.id === id);
    const q = here ? { senderId: here.senderId, senderName: here.senderName, kind: here.kind, text: here.body } : quotes[id];
    if (!q) return null;
    // A cut through bold words — every line from Active Jobs arrives in bold (report cbb0dc7a) — would leave the
    // opening '**' standing as two asterisks, so the cut closes it again: a lone '*' of the closing pair is made
    // whole, and an opening with nothing after it is dropped.
    const cut = String(q.text || '').replace(/\s+/g, ' ').trim().slice(0, QUOTE_CHARS);
    const words = cut.split('**').length % 2 ? cut : cut.replace(/\*{0,2}$/, tail => (tail === '**' ? '' : '**'));
    return {
      here: !!here,                                      // on this page, so a press can go to it
      name: q.senderName || t('chat_gone'),
      color: hue(open?.people.find(p => p.id === q.senderId), 'var(--text-tertiary)'),
      // A photo, a voice note or a file is named as such, with its caption after it when it had one.
      text: q.kind === 'text' ? words : `${t(kindKey(q.kind))}${words ? ` · ${words}` : ''}`,
    };
  };

  // ── A file, a photo or a voice note (step 2) ────────────────────────────────────────────────────────────────
  // The bytes go up as they do to any other upload here. A picked file takes whatever is in the typing box as its
  // caption and empties it; a voice note takes none, so words half-typed are still there when the recording ends.
  // True back means it landed, which is what the waiting pasted pictures go by (sendShots) — the paperclip and the
  // microphone send one thing and have nothing to ask.
  // `fileAs` (report 13fbee78) is the document-upload group's confirmed name: the message goes as it does without
  // it, but its attachment is kept under that name (report 4eb34eff), and a copy of the file is filed into the Drop
  // Folder under that name as well. The answer says the name it really got there, or why it could not be filed,
  // and that is said at the foot of the window.
  const sendFile = async (blob: Blob, kind: 'file' | 'voice', name: string, caption: string, to = openRef.current, reply = replyRef.current, fileAs = ''): Promise<boolean> => {
    if (!to || !blob.size) return false;   // `to` is the chat it was meant for, even if another is opened while it goes up
    setBusy(true);
    setErr('');
    try {
      const r = await api.chatSendFile(to, blob, kind, name, caption, reply, fileAs);
      if (openRef.current === to) {
        if (caption) setDraft('');
        if (reply) setReplyTo('');
        setMsgs(m => [...(m || []), r.message]);
        mergeJobs(r.jobs);   // a job number in the caption, live with it
        mergeTax(r.taxIds);  // …and a tax ID in it
        mergePass(r.passports);   // …and a passport number in it
        const quoted = r.quotes;
        if (quoted) setQuotes(q => ({ ...q, ...quoted }));   // and the quote of what it answers
        // Confirmed for the Drop Folder but not filed there: the document IS in the chat, so this is a note at
        // the foot of the window and not a failure of the send.
        if (fileAs && !r.filedAs) setErr(r.fileNote === 'no_drop_folder' ? t('chat_doc_no_folder') : t('chat_doc_file_failed'));
      }
      loadConvs();
      return true;
    } catch (e: any) {
      const code = e?.payload?.code;
      setErr(code === 'too_big' ? t('chat_file_too_big')
        : code === 'executable' ? t('chat_file_refused')
        : code === 'low_disk' ? t('chat_file_no_space')
        : code === 'too_long' ? t('chat_too_long')
        : code === 'bad_name' ? t('chat_doc_bad_name')
        : t('chat_file_failed'));
      return false;
    } finally { setBusy(false); }
  };

  // ── The paperclip ───────────────────────────────────────────────────────────────────────────────────────────
  // Everywhere but the document-upload group a picked file goes straight into the thread, exactly as it always
  // has — except a picture (report 1e817873: "An image that is loaded via paperclip or copy and paste to any group or
  // chat should have a crop tool"). A PNG, JPEG, GIF or WebP waits in the strip above the typing box instead, with
  // the pasted ones, to be cropped if need be and posted by Send with whatever is typed as its caption (sendShots).
  // Anything else — a PDF, a Word file, a photo the browser cannot draw (an iPhone's HEIC on a PC) — still goes at once.
  // In that group an image or a PDF is HELD instead and sent to be read, so its name can be checked before
  // anybody else sees the document (report 13fbee78). Anything the reader cannot take — a Word file, a
  // spreadsheet, a zip — is sent as it always was, with a line saying no name could be suggested for it, and so
  // is a document whose read could not be made at all: nothing here stands between a colleague and their file.
  // A document that WAS read but makes no name the protocol takes stays held, with a line asking for one — that
  // is the "verify and/or edit" the reporter asked for, and the document is readable, so there is one to type.
  // Since report 972661d6 it stays held with every part that WAS found already in its box — off the document, or
  // guessed from the file's own name — and that line is only for a document where nothing at all was.
  // The answer lands on the very hold it was read for and on no other: `held` is compared by identity, so a
  // document dropped, replaced by another, or left behind by opening a different chat is answered to nobody.
  const sendUnnamed = async (blob: Blob, fileName: string, why: string, to = openRef.current) => {
    const ok = await sendFile(blob, 'file', fileName, draft.trim(), to);
    // Said AFTER the send, not before: a send clears the line at the foot of the window, and a send that
    // failed has its own reason to say there instead.
    if (ok && why && openRef.current === to) setErr(why);
  };
  const readDocName = async (to: string, held: DocHold) => {
    const stillHeld = () => docFor.current === to && docRef.current === held;
    // Held back for nothing: it goes into the thread as it always did, and `why` says why no name came with it.
    const sendPlain = (why: string) => { dropDoc(); void sendUnnamed(held.blob, held.fileName, why, to); };
    setDocReading(true);
    setDocNote('');
    try {
      const r = await api.chatDocName(to, held.blob, held.fileName);
      if (!stillHeld()) return;
      // The suggestion is shown as the four boxes it is made of (report 595aad91). Since report 972661d6 it arrives
      // as those four parts, and every box gets whatever part came back — even with others missing — with the parts
      // that were guessed rather than read off the document edged in orange (docGuess). A server from before then
      // sends the whole name alone, split here as ever.
      if (r.ok && (r.parts || r.name)) {
        const split = r.parts
          ? { parts: { ...NO_PARTS, ...r.parts }, ext: r.ext || extOf(held.fileName) }
          : splitDocName(r.name || '', extOf(held.fileName));
        // The company it read, lined up with the drop-down's list (report 98453bac): a company the client master
        // has shows as PICKED, under the master's own spelling of it, so the read cannot file one company under a
        // second name; anything else stays exactly as it was read and shows in the Other box.
        const hit = matchCompany(split.parts.company, compRef.current);
        // The document's own number (report 7dc8999f), judged as the Upload Document wizard judges the one its read
        // gives (aiNoFor): cleaned to the characters of the Type it came with, and kept only when it fits them whole —
        // twelve digits are no ID card. A Type that carries no number keeps none. A house book's is the house number its
        // address opens with (report 642f141b — the read's houseNo), never its registration number.
        const rule = noRuleFor(split.parts.type);
        const no = rule ? cleanNo(readNoFor(rule, split.parts.number || '', r.houseNo || ''), rule) : '';
        const filled = { ...split.parts, company: hit || split.parts.company, number: rule && fitsNo(no, rule) ? no : '' };
        setDocParts(filled);
        setDocAi(filled);   // what the read filled the boxes with: a simulation check's "AI" side (recordDocSim)
        setDocDateKind(r.dateKind || 'none');   // …and which date it put in the date box ('none' from an older server)
        setDocGuess(r.guessed || []);
        setDocOther(false);
        setDocExt(split.ext);
        return;
      }
      if (r.code === 'no_name') { setDocNote(t('chat_doc_no_name')); return; }
      // 'off' = the mark was taken off this group between opening it and picking the file, so there is nothing
      // to say; anything else is a read that could not be made.
      sendPlain(r.code === 'off' ? '' : t('chat_doc_read_failed'));
    } catch {
      if (stillHeld()) sendPlain(t('chat_doc_read_failed'));
    } finally {
      if (stillHeld()) setDocReading(false);
    }
  };
  const onPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';   // so picking the same file twice in a row still counts as a change
    if (!f) return;
    const to = openRef.current;
    if (!to) return;
    if (!open?.docGroup) {
      if (canCrop(f.type, f.name)) { holdShots([{ blob: f, name: f.name }]); return; }
      sendFile(f, 'file', f.name, draft.trim());
      return;
    }
    // A Word file, a spreadsheet, a zip: the AI cannot read it, so there is nothing to suggest and nothing to
    // hold back — it goes as it always did, and the line at the foot says why no name came with it.
    if (!canReadDoc(f)) { void sendUnnamed(f, f.name, t('chat_doc_not_readable'), to); return; }
    setErr('');
    dropShots();   // the row below the typing box is this document's now
    const held: DocHold = { blob: f, fileName: f.name };
    docFor.current = to;
    docRef.current = held;   // set here as well as in state, so the read's own guard sees it at once
    setDocHold(held);
    setDocParts(NO_PARTS);
    setDocAi(NO_PARTS);
    setDocDateKind('none');
    setSimOn(false);            // each document is a real one until it is ticked as a test
    setSimNote('');
    setDocGuess([]);
    setDocOther(false);         // this document's Company box opens on the list, whatever the last one used
    setDocExt(extOf(f.name));   // the document's own ending, kept whatever the four boxes end up saying
    void readDocName(to, held);
  };
  // The held photo cropped (report 1e817873), offered once its name has been read so no read is still running for the
  // bytes it replaces: the cut-out is what Confirm sends and files, and the four boxes keep what they already say. Its
  // ending only changes if the cut-out came out as another sort — a PNG and a JPEG never do.
  const cropDoc = (cut: Blob, name: string) => {
    const held = docRef.current;
    setDocCrop(false);
    if (!held) return;
    const next: DocHold = { blob: cut, fileName: name, cropped: true };
    docRef.current = next;
    setDocHold(next);
    if (name !== held.fileName) setDocExt(extOf(name));
  };
  // Confirm: the message is sent as any other attachment is — so the conversation keeps the document — but under
  // the name the four boxes make, so the group sees the name that was confirmed (report 4eb34eff), and a copy is
  // filed into the Drop Folder under that same name (report 595aad91). A name that does not follow the protocol —
  // a box left empty, a date that is not YYYY-MM-DD — is said so here and nothing is sent, which is what the Drop
  // Folder's own note says of a file it cannot file.
  // Ticked "Simulation check": the very same two checks, then "Confirm test" records the test (recordDocSim) instead.
  const confirmDoc = async () => {
    const held = docRef.current;
    const to = docFor.current;
    if (!held || !to || busy || docReading) return;
    if (!docPartsReady(docParts)) { setDocNote(t('chat_doc_bad_name')); return; }
    const name = docFileName(docParts, docExt);
    if (!protocolOk(name)) { setDocNote(t('chat_doc_bad_name')); return; }
    if (sim) { await recordDocSim(held, name); return; }
    const ok = await sendFile(held.blob, 'file', name, draft.trim(), to, replyRef.current, name);
    if (ok) dropDoc();
  };
  // "Confirm test" (report 972661d6, 3rd opening): an admin's test of the read, recorded in the Upload Document
  // wizard's own list of simulation checks (server routes/docStorage.ts ds_sim_checks), marked as made in the chat.
  // Its AI side is what the read filled the four boxes with (docAi), its final side the boxes as they stand now, and
  // `changed` names each of the four that differs, spaces aside, as the wizard compares them. Those corrections are
  // what the next read in this group is shown (server routes/chat.ts doc-name). The date box holds whichever date the
  // document gave, so the test goes with the kind the read named for it (docDateKind, 4th opening — the reporter: "I
  // want AI to learn from these results"): an expiry or issue date corrected here is a lesson like the other three
  // boxes, and a date off the file's own name or today's ('none') shows under Results but teaches nothing, since the
  // read never gave it. Nothing is posted to the group and nothing is filed in the Drop Folder; the box clears and a
  // green line says the test was recorded. A failure keeps the box as it was.
  const recordDocSim = async (held: DocHold, name: string) => {
    const same = (x: string, y: string) => x.replace(/\s+/g, ' ').trim() === y.replace(/\s+/g, ' ').trim();
    const side = (p: DocParts, n: string) => ({ type: p.type.trim(), person: p.person.trim(), company: p.company.trim(), date: p.date.trim(), name: n });
    const aiName = docFileName(docAi, docExt);
    setBusy(true);
    setDocNote('');
    try {
      const r: any = await api.docStorageSimCheckAdd([{
        file: held.fileName,
        dateKind: docDateKind,
        ai: side(docAi, docPartsReady(docAi) && protocolOk(aiName) ? aiName : ''),
        final: side(docParts, name),
        changed: DOC_PART_KEYS.filter(k => !same(docAi[k], docParts[k])),
      }], 'chat');
      setSimTotal(Number(r.total) || 0);
      if (docRef.current !== held) return;   // left behind while it was recorded: whatever is held now stays
      dropDoc();
      setSimNote(t('chat_sim_recorded'));
    } catch (e: any) {
      if (docRef.current === held) setDocNote(e?.message || t('ds_sim_save_err'));
    } finally { setBusy(false); }
  };

  // ── A screenshot pasted straight in, SHOWN before it is posted (report 32621dcf, Calem: "Allow images
  // and screenshots to be copied and pasted directly into the chat window", then "Show the screenshot or
  // copy and paste image in the chat window before posting it directly to the chat") ──────────────────
  // A photo could only be sent through the paperclip's file chooser, which means saving a screenshot to
  // the computer first. ⌘V / Ctrl+V in an open chat takes it where it stands — but it is no longer posted
  // by the paste itself, which gave nobody a chance to see what had been pasted before the whole chat saw
  // it. A pasted picture now WAITS: it goes into a strip above the typing box as a thumbnail with an × to
  // drop it, pressable to be seen large, and nothing leaves this computer until Send is pressed. Send
  // then sends each one through the very same door a picked file uses — kind 'file', so the server decides
  // from what the bytes really are that it is a photo — under a name made for it (pastedName), with
  // whatever is typed as the first one's caption and the quote strip on the first one, exactly as a picked
  // file carries them (sendShots). Pasted words are none of this: nothing is taken, so the browser drops
  // them into the typing box as it always has.
  // The items have to be read here and now — a clipboard's items are gone the moment the handler returns —
  // so each picture's bytes are taken at once and held, with an object URL to draw it from.
  const takeShots = (dt: DataTransfer | null): boolean => {
    if (!openRef.current || busy || rec || pane === 'members') return false;
    const shots: Blob[] = [];
    const its = dt?.items;
    if (its) for (let i = 0; i < its.length; i++) {
      const it = its[i];
      if (it.kind !== 'file' || !String(it.type || '').startsWith('image/')) continue;
      const f = it.getAsFile();
      if (f?.size) shots.push(f);
    }
    // Some browsers hand a pasted picture over in `files` alone, so that is looked at when the items held none.
    const fl = dt?.files;
    if (!shots.length && fl) for (let i = 0; i < fl.length; i++) {
      const f = fl[i];
      if (f.size && String(f.type || '').startsWith('image/')) shots.push(f);
    }
    if (!shots.length) return false;
    holdShots(shots.map(b => ({ blob: b, name: pastedName(b.type) })));
    return true;
  };
  // Send with pictures waiting: each goes up in turn and leaves the strip as it lands, so what is still on
  // screen is what has still to go. The first carries the caption and the quote; the rest are pictures alone,
  // as a second pasted picture answers nothing of its own. A refusal stops the run there and leaves what has
  // not gone in the strip — the error line says why — so a screenshot is never lost to a send that failed.
  const sendShots = async () => {
    const to = openRef.current;
    let caption = draft.trim();
    let reply = replyRef.current;
    for (const s of pastedRef.current.slice()) {
      const ok = await sendFile(s.blob, 'file', s.name, caption, to, reply);
      if (!ok) return;
      dropShot(s.id);
      caption = '';
      reply = '';
    }
  };
  // The newest of the above, so the listener below is hung on the document once per open chat.
  const shotsRef = useRef(takeShots);
  shotsRef.current = takeShots;
  // Two ways in: the typing box's own paste (below, where it is drawn) and this one, which catches a
  // paste made with the cursor nowhere in particular — on the thread, on a bubble, on the chat list —
  // and is hung only while a chat is open. It leaves every box alone: the typing box has its own
  // handler, and a name half-typed into the group's name or the person search keeps the ordinary paste.
  useEffect(() => {
    if (!openId) return;
    const onPaste = (e: ClipboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      if (shotsRef.current(e.clipboardData)) e.preventDefault();
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [openId]);

  // The microphone: press once to record, again to send. The browser asks for permission the first time, and
  // nothing is kept if the answer is no. Opus in a WebM wrapper — what every browser that can record, records.
  const startRec = async () => {
    if (busy || rec) return;
    const dev = navigator.mediaDevices;
    if (!dev?.getUserMedia || typeof MediaRecorder === 'undefined') { setErr(t('chat_no_mic')); return; }
    setErr('');
    let stream: MediaStream;
    try { stream = await dev.getUserMedia({ audio: true }); }
    catch { setErr(t('chat_mic_refused')); return; }
    const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4']
      .find(x => MediaRecorder.isTypeSupported?.(x)) || '';
    let mr: MediaRecorder;
    try { mr = type ? new MediaRecorder(stream, { mimeType: type }) : new MediaRecorder(stream); }
    catch { stream.getTracks().forEach(tr => tr.stop()); setErr(t('chat_no_mic')); return; }
    const parts: Blob[] = [];
    keepRec.current = true;
    recTo.current = openRef.current;
    recReply.current = replyRef.current;   // a voice note answers whatever the quote strip was showing when it started
    setPicking('');                        // the picker gives way to the recording, as the typing box does
    setAtList(null);                        // and so does the '@' list, which stands over that same box
    dropShots();                           // and so do any pasted pictures: the row is the recording's now
    mr.ondataavailable = e => { if (e.data?.size) parts.push(e.data); };
    mr.onstop = () => {
      stream.getTracks().forEach(tr => tr.stop());   // the microphone light goes out with the recording
      setRec(null);
      setRecSecs(0);
      if (!keepRec.current || !parts.length) return;
      const mime = mr.mimeType || type || 'audio/webm';
      const ext = mime.indexOf('ogg') >= 0 ? 'ogg' : mime.indexOf('mp4') >= 0 ? 'm4a' : 'webm';
      sendFile(new Blob(parts, { type: mime }), 'voice', `voice-note.${ext}`, '', recTo.current, recReply.current);
    };
    mr.start();
    setRec(mr);
  };
  const stopRec = (keep: boolean) => {
    keepRec.current = keep;
    try { rec?.stop(); } catch { setRec(null); }
  };
  // The seconds beside the microphone while it listens, and its own five-minute end.
  useEffect(() => {
    if (!rec) return;
    const iv = setInterval(() => setRecSecs(s => s + 1), 1000);
    const cap = setTimeout(() => { keepRec.current = true; try { rec.stop(); } catch { /* already stopped */ } }, MAX_REC_MS);
    return () => {
      clearInterval(iv);
      clearTimeout(cap);
      // The pop-up was closed with the microphone still on: turn it off and keep nothing.
      if (rec.state === 'recording') { keepRec.current = false; try { rec.stop(); } catch { /* already stopped */ } }
    };
  }, [rec]);

  const startDirect = async (userId: string) => {
    setBusy(true);
    setErr('');
    try {
      const r = await api.chatOpenDirect(userId);
      await loadConvs();
      setPane('');
      setFind('');
      await show(r.conversation.id);
    } catch { setErr(t('chat_start_failed')); } finally { setBusy(false); }
  };
  const createGroup = async () => {
    const nm = groupName.trim();
    if (!nm || !picked.length || busy) return;
    setBusy(true);
    setErr('');
    try {
      const r = await api.chatCreateGroup(nm, picked);
      await loadConvs();
      setPane('');
      setGroupName('');
      setPicked([]);
      await show(r.conversation.id);
    } catch (e: any) {
      setErr(e?.payload?.code === 'no_members' ? t('chat_need_members') : t('chat_group_failed'));
    } finally { setBusy(false); }
  };
  const rename = async () => {
    if (!open || open.kind !== 'group') return;
    const nm = (window.prompt(t('chat_rename_q'), open.name) || '').trim();
    if (!nm || nm === open.name) return;
    setBusy(true);
    setErr('');
    try { await api.chatRenameGroup(open.id, nm); await loadConvs(); }
    catch { setErr(t('chat_rename_failed')); } finally { setBusy(false); }
  };
  const addMember = async (userId: string) => {
    if (!open) return;
    setBusy(true);
    setErr('');
    try { await api.chatAddMember(open.id, userId); await loadConvs(); }
    catch { setErr(t('chat_member_failed')); } finally { setBusy(false); }
  };
  const removeMember = async (p: ChatPerson) => {
    if (!open || !window.confirm(t('chat_remove_confirm', { name: who(p) }))) return;
    setBusy(true);
    setErr('');
    try { await api.chatRemoveMember(open.id, p.id); await loadConvs(); }
    catch { setErr(t('chat_member_failed')); } finally { setBusy(false); }
  };
  // Deleting the open group (report a409594d): an administrator's, as its name and its people are, and only once Yes
  // has been pressed in the strip under the head of the thread — never the browser's own box. Once it has gone the
  // thread is closed and the list asked for again, so the group leaves the list at once.
  const deleteGroup = async () => {
    if (!open || open.kind !== 'group' || busy) return;
    setBusy(true);
    setErr('');
    try {
      await api.chatDeleteGroup(open.id);
      back();   // the thread closes, and the Yes / No strip with it
      await loadConvs();
    } catch { setErr(t('chat_delete_group_failed')); } finally { setBusy(false); }
  };
  // Pictures (report 42100db4): my own, set, changed or taken away by me — and the open group's, by an administrator, after
  // which the list is asked for again so the row and the head of the thread show it at once. What failed is said on the
  // window's own error line.
  const saveMyPic = async (url: string) => {
    const r = await api.chatSetPicture('me', '', url);
    setMyPic(url);
    keepPic('user', me?.userId || '', r.avatar, url);
  };
  const dropMyPic = async () => {
    await api.chatDropPicture('me', '');
    setMyPic('');
    keepPic('user', me?.userId || '', '', '');
  };
  const saveGroupPic = async (url: string) => {
    if (!open || open.kind !== 'group') return;
    const r = await api.chatSetPicture('group', open.id, url);
    keepPic('group', open.id, r.avatar, url);
    await loadConvs();
  };
  const dropGroupPic = async () => {
    if (!open || open.kind !== 'group') return;
    await api.chatDropPicture('group', open.id);
    keepPic('group', open.id, '', '');
    await loadConvs();
  };

  // The New chat / New group lists: everyone on staff, narrowed by what is typed in the box. The signed-in person's
  // own entry (report e307a908, `self`) is for a chat with yourself alone: it stands in the New chat list and in no
  // other — a group's members are colleagues, and so is whoever the '@' offers.
  const others = useMemo(() => (people || []).filter(p => !p.self), [people]);
  const matches = useMemo(() => {
    const q = find.trim().toLowerCase();
    const list = pane === 'new' ? (people || []) : others;
    return q ? list.filter(p => `${p.name} ${p.nickname} ${p.email}`.toLowerCase().includes(q)) : list;
  }, [people, others, find, pane]);
  // Whom the '@' is offering: the same staff list, narrowed the same way by what has been typed after it, a
  // handful at a time. Nothing is offered while the emoji picker holds that place above the typing box.
  const atMatches = useMemo(() => {
    if (!atList) return [];
    const q = atList.q.trim().toLowerCase();
    return (q ? others.filter(p => `${p.name} ${p.nickname} ${p.email}`.toLowerCase().includes(q)) : others).slice(0, AT_MAX);
  }, [others, atList]);
  const atOpen = !!atList && !!atMatches.length && !picking;

  // ── Which names a message's words are drawn for, and in what colour (report 67084d4c) ───────────────────────
  // Everybody on staff — the list the '@' offers (`others`, everyone but the caller) — and the people of the chat
  // being read, which is where the signed-in reader's own name comes from. Both the name and the nickname count,
  // since either is what a colleague is called here. One regex, the longest name first so "Ann Smith" is drawn as
  // the one person and not as "Ann" with a stray "Smith" after it; and their colours beside it. A name of one
  // letter is left out — it would mark every stray letter of a sentence.
  const mentions = useMemo<Mentions>(() => {
    const colors: Record<string, string> = {};
    const names: string[] = [];
    const add = (nm: string, color: string) => {
      const k = String(nm || '').trim().toLowerCase();
      if (k.length < 2 || colors[k] !== undefined) return;
      colors[k] = color || '';
      names.push(String(nm).trim());
    };
    [...others, ...(open?.people || [])].forEach(p => {
      add(p.name, p.color);
      add(p.nickname, p.color);
      // Neither a name nor a nickname on the account: their address is what the window calls them (who), so it
      // is what the '@' list puts in, and it has to be drawn as a name too or that mention would be plain words.
      if (!String(p.name || '').trim() && !String(p.nickname || '').trim()) add(p.email, p.color);
    });
    if (!names.length) return null;
    const alt = names.sort((a, b) => b.length - a.length)
      .map(n => n.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&')).join('|');
    return { re: new RegExp(`@?(?:${alt})`, 'gi'), colors };
  }, [others, open]);

  // What a key does in the typing box: the '@' list first while it is up — ↑ and ↓ choose, Enter or Tab puts the
  // name in, Escape leaves the list alone (and stops there, or the window's own Escape would close the whole
  // pop-up) — then ⌘/Ctrl+B, U and I, and then Enter sends as it always has.
  const onTypeKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const n = atMatches.length;
    if (atOpen && n) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAtPick(i => (i + 1) % n); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAtPick(i => (i + n - 1) % n); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); putMention(atMatches[Math.min(atPick, n - 1)]); return; }
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setAtList(null); return; }
    }
    if ((e.metaKey || e.ctrlKey) && !e.altKey) {
      const k = e.key.toLowerCase();
      const mark = k === 'b' ? '**' : k === 'u' ? '__' : k === 'i' ? '_' : '';
      if (mark) { e.preventDefault(); wrapMark(mark); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
  };

  const btn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px 10px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' };
  // Every box typed into here — the message, a group's name, the search for a colleague. 16px on a phone and not
  // 12.5: an iPhone ZOOMS the whole page in whenever a box smaller than that is tapped, and leaves it zoomed, which
  // would put half the chat off the side of the screen the moment anybody started typing (report 8c5f3514).
  const box: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontSize: phone ? 16 : 12.5, boxSizing: 'border-box' };
  // The paperclip, the smile, the microphone and Send: the same square as the pop-up's other buttons, icon
  // only. Send takes this shape too and fills it with the accent, so the composer's row is squares throughout.
  const icon: React.CSSProperties = { ...btn, width: 42, height: 42, padding: 0, flexShrink: 0, color: 'var(--accent-text)' };
  // The two that appear beside a bubble when the mouse is on it: Reply and the smile. No edge and no fill of
  // their own — they sit on the thread's grey, so they never read as part of what was said.
  const act: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, padding: 0, borderRadius: 7, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', flexShrink: 0 };
  // The six formatting buttons over the typing box (report 67084d4c): the same borderless square as those two,
  // a little larger, so the row reads as a toolbar and not as six more buttons of the row below it.
  const tool: React.CSSProperties = { ...act, width: 28, height: 28, color: 'var(--text-secondary)' };
  const heading: React.CSSProperties = { fontSize: 10.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '10px 12px 6px' };
  // `k` grows a row with the chat list's widened column (report 8e638d38); the thread's own lists — the people a group
  // may add, the '@' list — leave it at 1.
  const row = (on: boolean, k = 1): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 8 * k, width: '100%', padding: `${8 * k}px ${10 * k}px`, borderRadius: 9 * k, border: '1px solid', borderColor: on ? 'var(--accent)' : 'transparent', background: on ? 'var(--bg-active)' : 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 12.5 * k, color: 'var(--text-primary)', minWidth: 0 });
  // The chat list's own measure (report 8e638d38): its width over its own 290px — 1 until the edge is dragged, 1.15 at
  // the widest, and always 1 on a phone — and any size in the column multiplied by it, so sz(10, 10, 8) is
  // '10px 10px 8px' until the list is widened. New chat, New group and Create group are the pop-up's ordinary button,
  // and the two boxes over the people to pick from its ordinary box, grown by it; a phone's boxes keep their 16px.
  const listScale = phone ? 1 : listW / LIST_W;
  const sz = (...n: number[]): string => n.map(v => `${v * listScale}px`).join(' ');
  // Whether any chat in the list has a picture (report 42100db4): the icon of a chat without one is then as wide as a picture.
  const anyPic = (convs || []).some(c => !!c.avatar);
  // …and the same for the people the New chat list offers (report e307a908), which draws their pictures as the list does.
  const anyPersonPic = (people || []).some(p => !!p.avatar);
  // The pixels a phone adds to the names and words (report 999f2fe7, PHONE_UP); 0 on a wider screen.
  const phoneUp = phone ? PHONE_UP : 0;
  const listBtn: React.CSSProperties = { ...btn, gap: sz(6), padding: sz(7, 10), fontSize: sz(12) };
  const listBox: React.CSSProperties = phone ? box : { ...box, padding: sz(7, 10), borderRadius: sz(9), fontSize: sz(12.5) };
  // One of the four boxes a document's name is typed in (report 595aad91), labelled above as the Upload Document
  // wizard labels the very same four, with the red star on the three that have to be filled. A press of Enter in
  // any of them is Confirm, as it was when the name was one box, and a keystroke in any of them takes the note
  // under them away: it was about what the boxes used to hold. A box holding a guess (report 972661d6) is edged in
  // orange and asks to be checked, until a keystroke in it makes what it holds the sender's own. `mode` brings up a
  // phone's number keys, for the Number box beside Type (docTypeBox).
  const unguess = (part: DocPart) => setDocGuess(g => (g.includes(part) ? g.filter(x => x !== part) : g));
  const docBox = (part: DocPart, label: string, need: boolean, value: string, ph: string, set: (v: string) => void, mode?: 'numeric' | 'text') => (
    <label style={{ display: 'block', minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>
        {label}{need && <span style={{ color: '#DC2626' }}> *</span>}
      </span>
      <input value={value} placeholder={ph} autoComplete="off" inputMode={mode}
        onChange={e => { set(e.target.value); setDocNote(''); unguess(part); }}
        onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); void confirmDoc(); } }}
        style={docGuess.includes(part) ? { ...box, ...DOC_GUESS_EDGE } : box} />
      {docGuess.includes(part) && <span style={DOC_GUESS_HINT}>{t('chat_doc_guess')}</span>}
    </label>
  );
  // The first of the four, the Type — with the document's own Number box beside it for a type that carries one
  // (report 7dc8999f): the Upload Document wizard's Number box (report c8ad7a51), by its very rules (utils/docNumber.ts),
  // so a title deed, a house, car or motorbike book, a company's papers or a building permit is named here as it is
  // there. It is never required, and never edged as a guess. It shows the number as the Type now in the box takes it,
  // so a Type changed by hand re-cleans it, and one that carries no number hides it and leaves it out of the name. The
  // Type keeps its place in the same grid either way, so the box being typed in never loses the cursor as the Number
  // box comes and goes. A phone's 16px boxes need more room than the wizard's 13px widths give.
  const docTypeBox = () => {
    const rule = noRuleFor(docParts.type);
    const noW = rule ? Math.round(noBoxW(rule) * (phone ? 16 / 13 : 1)) : 0;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: rule ? `minmax(0, 1fr) ${noW}px` : 'minmax(0, 1fr)', columnGap: 6, alignItems: 'start', minWidth: 0 }}>
        {docBox('type', t('chat_doc_type'), true, docParts.type, t('chat_doc_type_ph'), v => setDocParts(p => ({ ...p, type: v })))}
        {rule && docBox('number', t('ds_wiz_doc_no'), false, retypeNo(docParts.number, docParts.type), t('ds_wiz_deed_no_ph'),
          v => setDocParts(p => ({ ...p, number: retypeNo(v, p.type) })), rule.numeric ? 'numeric' : 'text')}
      </div>
    );
  };
  const setCompany = (v: string) => setDocParts(p => ({ ...p, company: v }));
  // The third of those four boxes, which is PICKED and not typed (report 98453bac) — and SEARCHED, since the
  // client master runs to 130-odd companies (opening #3): the picker above (DocCompanyPicker) offers the master's
  // own companies, '— No company —' first — a document may belong to a person alone — and 'Other…' last, which
  // brings the typing box back under it for a company the master has not got yet. The box stands on the master's
  // entry whenever it holds one of them (the AI's read included), and on 'Other…' for anything else, so what is
  // shown and what the file will be named are never two different companies. With no list to show — the File
  // Storage module is somebody else's, or the ask did not land — this is the plain box it has always been.
  const docCompanyBox = () => {
    if (!docCompanies.length) return docBox('company', t('chat_doc_company'), false, docParts.company, t('chat_doc_company_ph'), setCompany);
    const held = docParts.company.trim();
    const listed = docCompanies.find(c => c.toLowerCase() === held.toLowerCase()) || '';
    const other = docOther || (!!held && !listed);
    // A guessed company (report 972661d6) is edged in orange on whichever box shows it — the picker, or the Other
    // box under it — until a company is picked or typed there. A search typed into the picker and left is no change.
    const guess = docGuess.includes('company');
    return (
      <div style={{ minWidth: 0 }}>
        {/* The caption the other three carry, on its own rather than wrapped round the box: a press on a label
            is a press on the box it names, which would open the list again as a company was being picked out
            of it. The picker names itself to a screen reader instead (aria-label). */}
        <span style={{ display: 'block', fontSize: 10, color: 'var(--text-tertiary)', marginBottom: 2 }}>
          {t('chat_doc_company')}
        </span>
        {/* 'Other…' keeps whatever is held, so a company picked by mistake can be corrected by hand rather than
            typed again from nothing; '— No company —' is the entry that empties the box. */}
        <DocCompanyPicker value={listed} other={other} companies={docCompanies} guess={guess && !other}
          onPick={v => { setDocNote(''); setDocOther(false); setCompany(v); unguess('company'); }}
          onOther={() => { setDocNote(''); setDocOther(true); }}
          onType={() => setDocNote('')}
          onEnter={() => void confirmDoc()} />
        {other && (
          <input value={docParts.company} placeholder={t('chat_doc_company_ph')} autoComplete="off"
            aria-label={t('chat_doc_company')}
            onChange={e => { setCompany(e.target.value); setDocNote(''); unguess('company'); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); void confirmDoc(); } }}
            style={{ ...box, marginTop: 4, ...(guess ? DOC_GUESS_EDGE : null) }} />
        )}
        {guess && <span style={DOC_GUESS_HINT}>{t('chat_doc_guess')}</span>}
      </div>
    );
  };
  // The simulation checks' "Results (N)" (report 972661d6, 3rd opening): beside the tick box, and beside the green line
  // once a test is recorded. It opens the Upload Document wizard's own Results list (SimResults) over the window.
  const simLink = (
    <button type="button" onClick={() => setSimList(true)}
      style={{ flexShrink: 0, border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent)', fontSize: 11.5, fontWeight: 600, textDecoration: 'underline' }}>
      {t('ds_sim_results', { n: simTotal })}
    </button>
  );
  // Nothing to send: no words AND no pasted picture waiting. A picture alone is a message, so Send is live for it
  // with the typing box empty — the words, when there are any, become its caption. A document waiting to be named
  // is a message too, and Send is dead while its name is still being read for (report 13fbee78).
  const sendOff = busy || docReading || (!draft.trim() && !pasted.length && !docHold);

  if (!me) return null;
  return (
    // Over the whole page, above every dialog a page of its own opens, since this button sits on the menu and may be
    // pressed whatever is in front.
    // In the app (report 8c5f3514) there is no page under it to show through: no dark, no margin, no rounded corner
    // and no press-outside, and it is as tall as the screen can actually show (100dvh, which on a phone shrinks and
    // grows with the browser's own address bar instead of hiding the typing box under it).
    // The app's viewport reaches the screen's very edges (report ec0c5a76), so its sides keep clear of an iPhone's
    // notch when the phone is turned on its side — env(safe-area-inset-left/right) is nothing at all otherwise.
    <div style={{ position: 'fixed', inset: 0, background: app ? 'var(--bg-surface)' : 'rgba(0,0,0,0.45)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: app ? '0 env(safe-area-inset-right) 0 env(safe-area-inset-left)' : 20 }}
      onMouseDown={e => { if (!app && e.target === e.currentTarget && !busy) onClose(); }}>
      {/* The pop-up's box is 1081 × 713 — the 940 × 620 it was, 15% larger on both sides (report f692486f). The two
          caps are left as they were: a screen narrower or shorter than the box still gets 96vw / 88vh, so a small
          laptop sees the whole window and nothing is cut off.
          Nothing in it can be selected while the chat list's edge is being dragged (report 8e638d38; see grab). */}
      <div role="dialog" aria-modal={app ? undefined : true} aria-label={t('chat_title')}
        style={{ display: 'flex', flexDirection: 'column', width: app ? '100%' : 1081, maxWidth: app ? '100%' : '96vw', height: app ? '100dvh' : 713, maxHeight: app ? '100dvh' : '88vh', background: 'var(--bg-surface)', borderRadius: app ? 0 : 14, boxShadow: app ? undefined : 'var(--shadow-md)', overflow: 'hidden', userSelect: grabbing ? 'none' : undefined, WebkitUserSelect: grabbing ? 'none' : undefined }}>
        {/* Not while a message is being written: Update reloads the page, and the typing box keeps nothing through that.
            The bar comes back once the message has gone or been cleared. */}
        {!draft.trim() && !pasted.length && !docHold && updateBar}
        {/* ── the pop-up's own top row ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 14px', borderBottom: '1px solid var(--border-strong)', flexShrink: 0 }}>
          {/* On a phone a chat has the whole screen, so the window's own icon becomes the way back to the list —
              here rather than at the head of the thread, so it is there even while the thread is still coming
              down (report 8c5f3514). */}
          {phone && openId ? (
            <button onClick={back} title={t('chat_back')} aria-label={t('chat_back')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0 }}>
              <i className="ti ti-arrow-left" style={{ fontSize: 19 }} aria-hidden />
            </button>
          ) : (
            <i className="ti ti-message-circle" style={{ fontSize: 19, color: 'var(--accent)' }} aria-hidden />
          )}
          <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' }}>{t('chat_title')}</div>
          {/* "Install app" (report 8c5f3514), in the app — and on a phone-shaped screen in the pop-up as well
              (report 96e28b88, opening #3: "Allow users with an iPhone to download a home screen chat app rather
              than redirect them to the firm's site"). A phone only ever meets the chat as this pop-up over an
              ordinary page, so until now there was nothing anywhere that offered to install it, and a Share → "Add
              to Home Screen" on that page saved the page — the portal. The desktop pop-up is left exactly as it was,
              which is the earlier decision: the test is the width of the screen, nothing else.
              Drawn in the app's green, not as one more grey icon: the whole of the report is that a reader could not
              FIND this. In the app it carries its words; in the pop-up the icon alone, because that top row also
              holds the back arrow, the two switches and the ×, and on a phone the words would push them off the side
              — the label stays on the button for a long press and for a reader who hears the page.
              The press: the browser's own install dialog where the browser gave this page one to open. Where it gave
              none — every iPhone, and any page with no service worker of ours answering for it — the phone is taken
              to the chat app's own address first, as a real page load so that /chat.webmanifest and the
              apple-mobile-web-app-* meta are in the document from its first paint, and the instructions open there
              (the effect above). In the app there is nowhere to go: the sheet opens where it stands.
              Gone once Chat is the installed app. */}
          {(app || phone) && offer.show && (
            <button onClick={() => {
              offer.install().then(opened => {
                if (opened) return;                        // the browser's own dialog is up; nothing more to say
                if (app) { setHowTo(true); return; }       // already the chat page — the sheet belongs here
                wantInstallHow();                          // …and on the other side of this load, open it
                window.location.assign(CHAT_PATH);
              });
            }}
              title={t('chat_install_tip')} aria-label={t('chat_install')}
              style={{ ...btn, flexShrink: 0, gap: 5, padding: app ? '5px 9px' : 0, width: app ? undefined : 28, height: app ? undefined : 28, color: 'var(--accent-text)', background: 'var(--accent-bg)', border: '1px solid var(--accent)' }}>
              <i className="ti ti-download" style={{ fontSize: app ? 14 : 16 }} aria-hidden />{app ? t('chat_install') : null}
            </button>
          )}
          {/* The Files app's door, beside Chat's (report ac3c43b4: "make it a downloadable app like you did with chat")
              — on the same screens, the same shape, for whoever may open the Filing page (doc_storage). "Add to Home
              Screen" saves the page it is tapped on, so the press takes the phone to the Files app's own address with a
              real page load, and the instructions open there (FilesApp). Not inside an installed app, which has no
              browser to install anything from. In the top menu's Document colours, as the app's ring is (report
              89771ce1); Chat's own button above has no coloured section to map to and keeps the accent. */}
          {(app || phone) && !runningAsApp() && !!me?.modules.includes('doc_storage') && (
            <button onClick={() => { wantInstallHow('files'); window.location.assign(FILES_PATH); }}
              title={t('files_install_tip')} aria-label={t('files_install')}
              style={{ ...btn, flexShrink: 0, gap: 5, padding: doorWords ? '5px 9px' : 0, width: doorWords ? undefined : 28, height: doorWords ? undefined : 28, color: DOC_GROUP.color, background: DOC_GROUP.tint, border: `1px solid ${DOC_GROUP.color}` }}>
              <i className="ti ti-folder" style={{ fontSize: doorWords ? 14 : 16 }} aria-hidden />{doorWords ? t('files_title') : null}
            </button>
          )}
          {/* The Email app's door, beside those two (report 0e8dd0cb: "Create an email app the same way that we have done
              for chat and files, downloadable to the phone") — on the same screens, the same shape, for whoever may open
              the Email page (the email module). The press goes as Files' does: a real page load to the app's own address,
              and the instructions open there (EmailApp). In the top menu's Email purple, as the app's ring is
              (report 89771ce1). */}
          {(app || phone) && !runningAsApp() && !!me?.modules.includes('email') && (
            <button onClick={() => { wantInstallHow('email'); window.location.assign(EMAIL_PATH); }}
              title={t('emailapp_install_tip')} aria-label={t('emailapp_install')}
              style={{ ...btn, flexShrink: 0, gap: 5, padding: doorWords ? '5px 9px' : 0, width: doorWords ? undefined : 28, height: doorWords ? undefined : 28, color: EMAIL_CAL_GROUP.color, background: EMAIL_CAL_GROUP.tint, border: `1px solid ${EMAIL_CAL_GROUP.color}` }}>
              <i className="ti ti-mail" style={{ fontSize: doorWords ? 14 : 16 }} aria-hidden />{doorWords ? t('tab_email') : null}
            </button>
          )}
          {/* "Notify me on this device" (report d5b1d1de), beside the bell: the banners that arrive with the
              browser shut. One press per device, which is what the reporter asked for — the office computer and
              the phone each allow it for themselves — and the browser's own "Allow notifications?" opens on THIS
              press and never on load, because a browser asked out of nowhere refuses for good. Drawn in the
              accent colour once this device has them, grey while it has not; no button at all where banners are
              impossible (an old browser, a page over plain http://, or a server whose package is not installed
              yet). Pressing it again gives them up on this device alone and leaves the cards exactly as they are.
              Not on a phone-shaped screen (report ec0c5a76, opening #2): a bare icon there went unread, so the
              chat list carries a "Turn on notifications" banner in its place (PushOffer). */}
          {!phone && push.show && push.on !== null && (
            <button onClick={() => { if (!push.busy) void (push.on ? push.stop() : push.allow()); }}
              aria-pressed={push.on} aria-label={t('chat_push')}
              title={t(push.on ? 'chat_push_tip_on' : 'chat_push_tip_off')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: push.on ? 'var(--accent)' : 'var(--text-tertiary)', cursor: push.busy ? 'wait' : 'pointer' }}>
              <i className={`ti ti-${push.busy ? 'loader' : 'device-mobile'}`} style={{ fontSize: 16 }} aria-hidden />
            </button>
          )}
          {/* My picture (report 42100db4), left of the bell: the picture colleagues see for me in their chat list and at
              the head of our chat. With none it opens the file picker; with one it wears it, and opens Change / Remove. */}
          {!!me?.userId && (
            <PicButton pic={myPic} label={t('chat_pic_mine')} title={t('chat_pic_mine_tip')} save={saveMyPic} drop={dropMyPic} onError={setErr} showLabel={false}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)' }} />
          )}
          {/* The notices' switch (step 3), beside the ×: the bell rings while the cards are on, and is crossed out
              while they are off. Nothing is shown until the server has said which, so it never starts on the wrong one. */}
          {notify !== null && (
            <button onClick={flipNotify} aria-pressed={notify} aria-label={t('chat_notify')}
              title={t(notify ? 'chat_notify_tip_on' : 'chat_notify_tip_off')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: notify ? 'var(--accent)' : 'var(--text-tertiary)', cursor: 'pointer' }}>
              <i className={`ti ${notify ? 'ti-bell-ringing' : 'ti-bell-off'}`} style={{ fontSize: 16 }} aria-hidden />
            </button>
          )}
          {/* Full screen ⤢ / Reduce ⤡ (report 14dc81ac): the one chat in its two frames, swapped from either side.
              In the window, ⤢ opens the chat's own page — /chat, the address a phone's installed Chat opens — in a
              tab of its own and lets this window go, so the chat is in one place and not two; a chat tab already
              open is the tab that comes forward, rather than a second one opening beside it (openChatTab, the
              one-tab machinery of report c2846304). On that page ⤡ is the way back down, handed in by ChatApp only
              where there is a window to go back to — never in the app a phone installed, which has none.
              Not on a phone-shaped screen: the window there already fills the screen, and the door to the chat's
              own address beside it is "Install app". */}
          {app && onReduce && (
            <button onClick={onReduce} title={t('chat_reduce_tip')} aria-label={t('chat_reduce')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
              <i className="ti ti-arrows-minimize" style={{ fontSize: 16 }} aria-hidden />
            </button>
          )}
          {!app && !phone && (
            <button onClick={() => { if (!busy && openChatTab(CHAT_PATH)) onClose(); }}
              title={t('chat_full_tip')} aria-label={t('chat_full')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: busy ? 'wait' : 'pointer' }}>
              <i className="ti ti-arrows-maximize" style={{ fontSize: 16 }} aria-hidden />
            </button>
          )}
          {/* No × in the app: the window is the page, and closing it would leave an empty screen behind. */}
          {!app && (
            <button onClick={onClose} title={t('chat_close')} aria-label={t('chat_close')}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer' }}>
              <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
            </button>
          )}
        </div>

        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {/* ── the chats, down the left — and on a phone the first of the two screens, which is the whole of it
                 until a chat is chosen (report 8c5f3514) ──
                 As wide as this person last dragged it, with every size in it grown to match (sz; report 8e638d38). */}
          {(!phone || !openId) && (
          <aside style={{ width: phone ? '100%' : listW, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: phone ? undefined : '1px solid var(--border-strong)', minHeight: 0, position: phone ? undefined : 'relative' }}>
            {/* The phone's way to the banners that arrive with Chat shut (report ec0c5a76), in place of the top
                row's icon: draws nothing once they are on, once put away, or where they cannot be had at all. */}
            {phone && <PushOffer push={push} app={app} />}
            <div style={{ display: 'flex', gap: sz(6), padding: sz(10, 10, 8), flexShrink: 0 }}>
              <button onClick={() => { setPane(p => (p === 'new' ? '' : 'new')); setFind(''); setErr(''); }} style={{ ...listBtn, flex: 1 }}>
                <i className="ti ti-user-plus" style={{ fontSize: sz(14) }} aria-hidden />{t('chat_new')}
              </button>
              {/* The reporter: admins alone make a group and add or remove people. The server says so too. */}
              {admin && (
                <button onClick={() => { setPane(p => (p === 'group' ? '' : 'group')); setFind(''); setGroupName(''); setPicked([]); setErr(''); }} style={{ ...listBtn, flex: 1 }}>
                  <i className="ti ti-users-plus" style={{ fontSize: sz(14) }} aria-hidden />{t('chat_new_group')}
                </button>
              )}
            </div>

            {/* ── the search box (report f57cc430) ──
                Every search box in the system is Filing's (report 48c2197c): 13.5px words, 7px above and below them,
                the magnifier 14.5px and 16px in with the words 36px in, clear of it, and 16px clear of the right-hand
                end — with the page's own name inside, which here is Chat. It takes the whole column rather than
                Filing's fixed 220px, since the column itself is dragged wider (sz grows every size in it with it), and
                it keeps this window's own box (listBox) for its edge, its colours and a phone's 16px words, which no
                box here goes under (see `box`). It stands over the chats and searches them as it is typed; Escape
                empties it and brings the list back without closing the window. Not while the people picker is open —
                that pane has a search box of its own, and two boxes in one column would read as one search. */}
            {pane !== 'new' && pane !== 'group' && (
              <div style={{ padding: sz(0, 10, 8), flexShrink: 0 }}>
                <div style={{ position: 'relative' }}>
                  {/* The magnifier turns while a search is out, where every other busy icon in the system turns, so
                      the words already on screen can stay as they are rather than flickering away under a spinner.
                      The turning is the icon's own: the span beside it holds the place, since `spin` is a transform
                      and would take the one that centres it on the box's line. */}
                  <span aria-hidden style={{ position: 'absolute', left: sz(16), top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', color: 'var(--text-tertiary)', pointerEvents: 'none' }}>
                    <i className={`ti ti-${hitBusy ? 'loader-2' : 'search'}`}
                      style={{ fontSize: sz(14.5), animation: hitBusy ? 'spin 0.9s linear infinite' : undefined }} />
                  </span>
                  <input value={msgQ} onChange={e => setMsgQ(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Escape' && msgQ) { e.stopPropagation(); setMsgQ(''); dropHits(); } }}
                    placeholder={t('chat_find')} aria-label={t('chat_find')}
                    style={{ ...listBox, padding: sz(7, 16, 7, 36), fontSize: phone ? 16 : sz(13.5) }} />
                </div>
              </div>
            )}

            {pane === 'new' || pane === 'group' ? (
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: sz(0, 10, 10) }}>
                {pane === 'group' && (
                  <input value={groupName} onChange={e => setGroupName(e.target.value)} maxLength={60} placeholder={t('chat_group_name')}
                    style={{ ...listBox, marginBottom: sz(6) }} />
                )}
                <input autoFocus value={find} onChange={e => setFind(e.target.value)} placeholder={t('chat_find_person')} style={listBox} />
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', marginTop: sz(6) }}>
                  {people === null ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: sz(12) }}>
                      <i className="ti ti-loader" style={{ fontSize: sz(18), color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
                    </div>
                  ) : !matches.length ? (
                    <div style={{ fontSize: sz(12), lineHeight: 1.5, color: 'var(--text-tertiary)', padding: sz(6, 2) }}>{t('chat_no_people')}</div>
                  ) : matches.map(p => {
                    const on = pane === 'group' && picked.includes(p.id);
                    // The signed-in person's own entry (report e307a908): a chat with yourself, named "You" in their own
                    // bubble with what such a chat is for beside it. Only the New chat list has it (matches).
                    const you = pane === 'new' && !!p.self;
                    return (
                      <button key={p.id} disabled={busy}
                        onClick={() => (pane === 'group'
                          ? setPicked(v => (v.includes(p.id) ? v.filter(x => x !== p.id) : [...v, p.id]))
                          : startDirect(p.id))}
                        style={{ ...row(on, listScale), cursor: busy ? 'not-allowed' : 'pointer' }}>
                        {/* In the New chat list, the person's own picture (report e307a908) as the chat list draws it
                            (report 42100db4) — and with none the icon it always had, as wide as a picture while anybody
                            in the list has one, so every name still starts at one edge. A group's list keeps its tick box. */}
                        {pane === 'group'
                          ? <i className={`ti ti-${on ? 'square-check' : 'square'}`} style={{ fontSize: sz(15), flexShrink: 0, color: on ? 'var(--accent)' : 'var(--text-tertiary)' }} aria-hidden />
                          : <ChatAvatar kind="user" id={p.id} v={p.avatar} size={sz(PIC_ROW)}
                              fallback={<i className="ti ti-user" style={{ fontSize: sz(15), flexShrink: 0, color: 'var(--text-tertiary)', width: anyPersonPic ? sz(PIC_ROW) : undefined, textAlign: 'center' }} aria-hidden />} />}
                        {/* The name sits in the person's own bubble here as it does on their messages, rather
                            than being coloured text on the line (reports c05bc176, b4806261). */}
                        <PersonBubble p={you ? { ...p, nickname: t('chat_you') } : p} scale={listScale} />
                        {you && <span style={{ flex: 1, minWidth: 0, fontSize: sz(11), color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('chat_you_hint')}</span>}
                      </button>
                    );
                  })}
                </div>
                {pane === 'group' && (
                  <button onClick={createGroup} disabled={busy || !groupName.trim() || !picked.length}
                    style={{ ...listBtn, marginTop: sz(8), border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, opacity: busy || !groupName.trim() || !picked.length ? 0.5 : 1 }}>
                    <i className="ti ti-check" style={{ fontSize: sz(14) }} aria-hidden />{t('chat_create_group', { n: picked.length })}
                  </button>
                )}
              </div>
            ) : finding ? (
              /* ── what the search found, in the chats' own place (report f57cc430) ──
                 One hit a row, the newest first: the chat it was said in, then who said it and when, then the words
                 round the match — the message's own, or the name of the file it carried when that is what matched, as
                 a quote of one names it (quoteOf). The chat's name is drawn exactly as its row in the list is: a
                 group's black, a one-to-one chat's in that colleague's own colour (report 2ad9e77c), and the writer
                 in their own bubble (PersonBubble), with their colour taken from the chat itself where the list has
                 landed. A press opens that chat ON the message (goToHit). */
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: sz(0, 8, 10) }}>
                {hits === null ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: sz(14) }}>
                    <i className="ti ti-loader" style={{ fontSize: sz(20), color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
                  </div>
                ) : hitFail ? (
                  <div style={{ fontSize: sz(12), lineHeight: 1.5, color: '#B42318', padding: sz(6, 4) }}>{t('chat_find_failed')}</div>
                ) : !hits.length ? (
                  <div style={{ fontSize: sz(12), lineHeight: 1.5, color: 'var(--text-tertiary)', padding: sz(6, 4) }}>{t('chat_find_none')}</div>
                ) : (
                  <>
                    {hits.map(h => {
                      const conv = (convs || []).find(c => c.id === h.conversationId);
                      const name = h.kind === 'group' ? (h.name || t('chat_group')) : h.self ? t('chat_you') : (who(h.other) || t('chat_gone'));
                      const writer = conv?.people.find(p => p.id === h.senderId);
                      const words = plainWords(h.excerpt);
                      return (
                        <button key={h.messageId} onClick={() => goToHit(h)} title={t('chat_find_tip')}
                          style={{ ...row(false, listScale), flexDirection: 'column', alignItems: 'stretch', gap: sz(2), marginBottom: sz(2) }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: sz(6), minWidth: 0 }}>
                            <i className={`ti ti-${h.kind === 'group' ? 'users' : 'user'}`} style={{ fontSize: sz(15), flexShrink: 0, color: 'var(--text-tertiary)' }} aria-hidden />
                            <span style={{ flex: 1, minWidth: 0, fontSize: sz(12.5 + phoneUp), fontWeight: 700, color: h.kind === 'group' ? 'var(--text-primary)' : hue(h.other), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: sz(6), minWidth: 0 }}>
                            <PersonBubble p={writer} name={h.senderName} scale={listScale} style={{ maxWidth: sz(ROW_BUBBLE_MAX) }} />
                            <span style={{ flex: 1, minWidth: 0, fontSize: sz(11 + phoneUp), color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmtDateTime(h.createdAt)}</span>
                          </span>
                          {/* Two lines of it at most, as a notice card shows a message (ChatToast): the rest is read
                              in the chat itself, which is one press away. */}
                          <span style={{ fontSize: sz(11.5 + phoneUp), lineHeight: 1.4, color: 'var(--text-primary)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', wordBreak: 'break-word' }}>
                            {h.msgKind === 'text' ? words : `${t(kindKey(h.msgKind))}${words ? ` · ${words}` : ''}`}
                          </span>
                        </button>
                      );
                    })}
                    {/* The server hands back the newest matches and no more, so a search that reached its cap says
                        so: there are older ones these are not showing, and more words will narrow them. */}
                    {hitMore && (
                      <div style={{ fontSize: sz(11.5), lineHeight: 1.5, color: 'var(--text-tertiary)', padding: sz(6, 4) }}>{t('chat_find_more', { n: hits.length })}</div>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: sz(0, 8, 10) }}>
                {convs === null ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: sz(14) }}>
                    <i className="ti ti-loader" style={{ fontSize: sz(20), color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
                  </div>
                ) : failed && !convs.length ? (
                  <div style={{ fontSize: sz(12), lineHeight: 1.5, color: '#B42318', padding: sz(6, 4) }}>{t('chat_list_failed')}</div>
                ) : !convs.length ? (
                  <div style={{ fontSize: sz(12), lineHeight: 1.5, color: 'var(--text-tertiary)', padding: sz(6, 4) }}>{t('chat_none')}</div>
                ) : convs.map(c => {
                  // Who spoke last in this chat, for the bubble at the end of its row (report b4806261): the
                  // last message's writer, found among the chat's people for their colour. A chat nobody has
                  // written in yet has no bubble, and neither has one whose last writer has left the system
                  // with no name on the message — PersonBubble draws nothing when there is nobody to name.
                  const last = c.last;
                  const spoke = last ? c.people.find(p => p.id === last.senderId) : undefined;
                  // …and the last message when it is MY OWN and the chat is a one-to-one, so the row can carry
                  // the same tick the thread's own bubble does and the list and the thread never disagree
                  // (report 29486dfd). Null on a group's row, and null on somebody else's message: neither
                  // wears a tick.
                  const myLast = last && c.kind !== 'group' && last.senderId === me.userId ? last : null;
                  return (
                    // A right-click, or a press held on a phone, opens the row's menu (report e307a908; rowPress and the
                    // block beside it). The row keeps its text out of a phone's long-press selection for the same reason.
                    <button key={c.id} onClick={() => rowClick(c.id)} title={c.self ? undefined : t('chat_row_tip')}
                      onContextMenu={e => { e.preventDefault(); openRowMenu(c, e.clientX, e.clientY); }}
                      onPointerDown={e => rowPress(c, e)} onPointerMove={rowMove} onPointerUp={rowRelease} onPointerCancel={rowCancel} onPointerLeave={rowCancel}
                      style={{ ...row(c.id === openId, listScale), alignItems: 'flex-start', marginBottom: sz(2), userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}>
                      {/* The chat's picture (report 42100db4) — a group's own, the other person's in a one-to-one — and
                          with none the icon it always had, as wide as a picture while any row has one, so that every
                          name still starts at one edge. */}
                      <ChatAvatar kind={c.kind === 'group' ? 'group' : 'user'} id={c.kind === 'group' ? c.id : (c.other?.id || '')} v={c.avatar} size={sz(PIC_ROW)}
                        fallback={<i className={`ti ti-${c.kind === 'group' ? 'users' : 'user'}`} style={{ fontSize: sz(15), flexShrink: 0, marginTop: sz(1), color: 'var(--text-tertiary)', width: anyPic ? sz(PIC_ROW) : undefined, textAlign: 'center' }} aria-hidden />} />
                      {/* A group an administrator holds at the top in Admin → Chat (report f4c8e029): the rows
                          arrive in that order already, so this says WHY this one is first and nothing here
                          sorts. Not pinned = no pin, and the row is exactly as it was. */}
                      {c.pinned && <i className="ti ti-pin" title={t('chat_pinned')} style={{ fontSize: sz(13), flexShrink: 0, marginTop: sz(2), color: 'var(--text-secondary)' }} aria-hidden />}
                      <span style={{ flex: 1, minWidth: 0 }}>
                        {/* A one-to-one chat's name IS a colleague's name, so it is drawn in that colleague's own
                            colour (report 2ad9e77c). A group's name — 'Automation Requests' — is nobody's name
                            and stays black. The last thing said in the chat stays black either way: nothing a
                            person WRITES is coloured (reports c05bc176, b4806261), and who wrote it is the
                            bubble at the end of the row.
                            On a phone both lines are a pixel larger (report 999f2fe7) — the name, which took the
                            row's own 12.5, now carries that size itself. */}
                        <span style={{ display: 'block', fontSize: sz(12.5 + phoneUp), fontWeight: 700, color: c.kind === 'group' ? 'var(--text-primary)' : hue(c.other), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleOf(c)}</span>
                        <span style={{ display: 'block', fontSize: sz(11.5 + phoneUp), color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {/* The tick goes in front of the words, as WhatsApp's own list does, so it stays in
                              sight when a long last message runs out of room and ends in "…". */}
                          {myLast && <ChatTicks read={myLast.read} scale={listScale} style={{ marginRight: sz(3) }} />}
                          {snippet(last)}
                        </span>
                      </span>
                      {/* The end of the row says both things about a chat at a glance (report b4806261): who wrote
                          last, in their bubble, and how many of their messages have not been read, in the red
                          circle the chat button's own count uses. The circle is there only while something is
                          unread, as it was; the bubble stands whether anything is unread or not. */}
                      {last && <PersonBubble p={spoke} name={last.senderName} scale={listScale} style={{ marginTop: sz(1), maxWidth: sz(ROW_BUBBLE_MAX) }} />}
                      {c.unread > 0 && <ChatCount n={c.unread} scale={listScale} style={{ marginTop: sz(1) }} />}
                    </button>
                  );
                })}
              </div>
            )}
            {/* A row's own menu (report e307a908), just under the pointer that opened it: Mark as unread, or Mark as
                read on a chat with something unread. Fixed to the screen, as a photo shown large is, so neither
                column clips it, and kept inside the window's edges. Set a little off the pointer so the click a
                phone sends as the finger lifts lands on the row — which swallows it — and not on the menu. */}
            {rowMenu && (() => {
              const c = (convs || []).find(x => x.id === rowMenu.id);
              if (!c) return null;
              const left = Math.max(8, Math.min(rowMenu.x + 10, window.innerWidth - ROW_MENU_W - 8));
              const top = Math.max(8, Math.min(rowMenu.y + 14, window.innerHeight - ROW_MENU_H - 8));
              const unread = c.unread > 0;
              return (
                <div data-row-menu="1" role="menu" style={{ position: 'fixed', left, top, zIndex: 10300, minWidth: ROW_MENU_W, padding: 4, borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-md)' }}>
                  <button role="menuitem" onClick={() => void (unread ? markReadByHand(c.id) : markUnread(c.id))}
                    style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', padding: '6px 8px', borderRadius: 7, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 12.5, color: 'var(--text-primary)' }}>
                    <i className={`ti ti-${unread ? 'mail-opened' : 'mail'}`} style={{ fontSize: 14, color: 'var(--text-tertiary)' }} aria-hidden />
                    {t(unread ? 'chat_mark_read' : 'chat_mark_unread')}
                  </button>
                </div>
              );
            })()}
            {/* Something that went wrong on THIS screen — a chat that would not start, a group that would not be
                made. The thread has its own line for it at the foot of the window, which on a phone is the other
                screen and not on this one, so the phone says it here (report 8c5f3514). */}
            {phone && err && <div role="alert" style={{ padding: '7px 12px', fontSize: 12, color: '#B42318', borderTop: '1px solid var(--border)' }}>{err}</div>}
            {/* The list's right-hand edge, to drag it wider (report 8e638d38) — with a mouse, a finger or a pen, so it
                answers pointer events and a finger on it never scrolls the page instead (touch-action). A strip
                astride the line, wider than the line so a finger can find it — 7px each side, inside the room both
                columns keep clear at their edges — with a short bar at its middle, so an iPad, which has no mouse to
                change the cursor, still shows there is something to take hold of. The whole line turns the accent
                while a pointer is on it or holding it. A double-click puts the list back to 290px. The mouse's own
                press is kept off it, as the formatting buttons' is, so it starts no selection and the typing box
                keeps its cursor. */}
            {!phone && (
              <div role="separator" aria-orientation="vertical" aria-label={t('chat_list_resize')} title={t('chat_list_resize')}
                onMouseDown={e => e.preventDefault()}
                onPointerDown={pressEdge} onPointerMove={dragEdge} onPointerUp={dropEdge} onPointerCancel={dropEdge}
                onPointerEnter={() => setEdgeHot(true)} onPointerLeave={() => setEdgeHot(false)} onDoubleClick={resetEdge}
                style={{ position: 'absolute', top: 0, bottom: 0, right: -8, width: 15, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'col-resize', touchAction: 'none' }}>
                <span style={{ width: 3, height: edgeHot || grabbing ? '100%' : 28, borderRadius: 2, background: edgeHot || grabbing ? 'var(--accent)' : 'var(--border-strong)', transition: 'background 0.15s' }} aria-hidden />
              </div>
            )}
          </aside>
          )}

          {/* ── the thread, on the right — and on a phone the second screen, in place of the list ── */}
          {(!phone || !!openId) && (
          <section style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {!open ? (
              // A pop-up opened from a notice has its chat before the list has landed (step 3): the spinner holds
              // this side until it does, rather than telling the reader to choose a chat they already chose.
              openId && convs === null ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                  <i className="ti ti-loader" style={{ fontSize: 20, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center', fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-tertiary)' }}>
                  {t('chat_pick')}
                </div>
              )
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                  {/* The chat's picture (report 42100db4), the list row's own — and nothing at all while there is none. */}
                  <ChatAvatar kind={open.kind === 'group' ? 'group' : 'user'} id={open.kind === 'group' ? open.id : (open.other?.id || '')} v={open.avatar} size={PIC_HEAD} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* The name at the head of the thread, like the one in the list: the colleague's own colour in
                        a one-to-one chat (report 2ad9e77c), black for a group, whose name is nobody's. The
                        address under it, and every message below, stay as they were. */}
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: open.kind === 'group' ? 'var(--text-primary)' : hue(open.other), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleOf(open)}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {/* Under "You", what a chat with yourself is for (report e307a908), where a colleague's address goes. */}
                      {open.kind === 'group' ? open.people.map(who).join(' · ') : open.self ? t('chat_you_hint') : (open.other?.email || '')}
                    </div>
                  </div>
                  {/* A group's name and its people are an admin's to change (the reporter's answer). */}
                  {admin && open.kind === 'group' && (
                    <>
                      <button onClick={rename} disabled={busy} title={t('chat_rename')} style={btn}>
                        <i className="ti ti-pencil" style={{ fontSize: 14 }} aria-hidden />{t('chat_rename')}
                      </button>
                      <button onClick={() => { setPane(p => (p === 'members' ? '' : 'members')); setFind(''); setErr(''); }} title={t('chat_members')} style={btn}>
                        <i className="ti ti-users" style={{ fontSize: 14 }} aria-hidden />{t('chat_members')}
                      </button>
                      {/* …and so is its picture (report 42100db4: "Only admin can set group pictures"). On a phone the
                          picture alone, as the bin beside it. */}
                      <PicButton key={open.id} pic={groupPic} label={t('chat_pic_group')} title={t('chat_pic_group')} save={saveGroupPic} drop={dropGroupPic}
                        onError={setErr} showLabel={!phone} style={btn} />
                      {/* …and so is the group itself, to delete (report a409594d): red, as every delete in the window is,
                          and it only opens the Yes / No strip below — nothing goes until Yes. On a phone the bin alone,
                          so the three buttons still leave the group's name some room. */}
                      <button onClick={() => { setDelAsk(v => !v); setErr(''); }} disabled={busy} title={t('chat_delete_group')} aria-label={t('chat_delete_group')} aria-expanded={delAsk}
                        style={{ ...btn, color: '#E24B4A' }}>
                        <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden />{!phone && t('chat_delete_group')}
                      </button>
                    </>
                  )}
                </div>

                {/* Whether to delete the group, asked in the window itself (report a409594d): what happens, then Yes and
                    No. Yes deletes it for everybody; No — or Escape — puts the strip away and changes nothing. */}
                {delAsk && admin && open.kind === 'group' && (
                  <div role="alertdialog" aria-label={t('chat_delete_group')}
                    style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '9px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                    <span style={{ flex: '1 1 220px', minWidth: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-primary)' }}>
                      {t('chat_delete_group_q', { name: titleOf(open) })}
                    </span>
                    <button onClick={() => void deleteGroup()} disabled={busy}
                      style={{ ...btn, color: '#fff', background: '#E24B4A', border: '1px solid #E24B4A', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                      <i className={`ti ${busy ? 'ti-loader' : 'ti-trash'}`} style={{ fontSize: 14, animation: busy ? 'spin 1s linear infinite' : undefined }} aria-hidden />
                      {t('chat_delete_group_yes')}
                    </button>
                    <button onClick={() => setDelAsk(false)} disabled={busy} style={btn}>{t('chat_delete_group_no')}</button>
                  </div>
                )}

                {pane === 'members' && admin && open.kind === 'group' ? (
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 12px 12px' }}>
                    <div style={heading}>{t('chat_in_group')}</div>
                    {open.people.map(p => (
                      <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', fontSize: 12.5, color: 'var(--text-primary)' }}>
                        <i className="ti ti-user" style={{ fontSize: 14, color: 'var(--text-tertiary)' }} aria-hidden />
                        {/* Each member in their own bubble, as everywhere else in the window (reports c05bc176,
                            b4806261); the × that takes them out of the group keeps the room at the end of the line. */}
                        <span style={{ flex: 1, minWidth: 0 }}><PersonBubble p={p} /></span>
                        {open.people.length > 1 && (
                          <button onClick={() => removeMember(p)} disabled={busy} title={t('chat_remove')} aria-label={`${t('chat_remove')}: ${who(p)}`}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, padding: 0, borderRadius: 6, border: 'none', background: 'transparent', color: '#E24B4A', cursor: busy ? 'not-allowed' : 'pointer' }}>
                            <i className="ti ti-user-minus" style={{ fontSize: 14 }} aria-hidden />
                          </button>
                        )}
                      </div>
                    ))}
                    <div style={heading}>{t('chat_add_member')}</div>
                    <input value={find} onChange={e => setFind(e.target.value)} placeholder={t('chat_find_person')} style={{ ...box, margin: '0 10px 6px', width: 'calc(100% - 20px)' }} />
                    {matches.filter(p => !open.people.some(x => x.id === p.id)).map(p => (
                      <button key={p.id} onClick={() => addMember(p.id)} disabled={busy} style={{ ...row(false), cursor: busy ? 'not-allowed' : 'pointer' }}>
                        <i className="ti ti-user-plus" style={{ fontSize: 15, flexShrink: 0, color: 'var(--accent)' }} aria-hidden />
                        <PersonBubble p={p} />
                      </button>
                    ))}
                  </div>
                ) : (
                  <>
                    <div ref={thread} onScroll={threadScrolled}
                      style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', background: 'var(--c-gray-50)' }}>
                      {/* All the thread holds, in one box as tall as what is in it, so a photo or a voice note that
                          lands late is seen to grow it (report bd101132; see keepFoot). */}
                      <div ref={setThreadBody} style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                      {/* What is older than the thread's four weeks (report d0f07722): a press brings back the month
                          named, the next press the one before it that anybody wrote in, and the thread stays on the
                          message that was at its top (showOlder). Gone once nothing older is left — and while the
                          thread already holds as many messages as it shows, as a month more could add none. */}
                      {msgs && older && (
                        <button onClick={showOlder} disabled={revealing}
                          style={{ ...btn, alignSelf: 'center', flexShrink: 0, opacity: revealing ? 0.7 : 1, cursor: revealing ? 'default' : 'pointer' }}>
                          <i className={`ti ${revealing ? 'ti-loader' : 'ti-history'}`} style={{ fontSize: 14, animation: revealing ? 'spin 1s linear infinite' : undefined }} aria-hidden />
                          {t('chat_show_month', monthWords(older.month, i18n.language || 'en'))}
                        </button>
                      )}
                      {msgs === null ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 14 }}>
                          <i className="ti ti-loader" style={{ fontSize: 20, color: 'var(--text-tertiary)', animation: 'spin 1s linear infinite' }} aria-hidden />
                        </div>
                      ) : !msgs.length ? (
                        <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-tertiary)' }}>{t('chat_say_hello')}</div>
                      ) : msgs.map(m => {
                        const own = m.senderId === me.userId;
                        // Who wrote it, from the chat's people — which list every member, the signed-in one too.
                        const writer = open.people.find(p => p.id === m.senderId);
                        const quoted = m.replyTo ? quoteOf(m.replyTo) : null;   // the message this one answers, if it answers one
                        const chips = Object.entries(reacts[m.id] || {});       // the emojis on it, if any
                        const ticked = pickMode && picks.includes(m.id);        // ticked to be forwarded (report 96e28b88)
                        // Its words in another language, if the reader asked (report 9b579ee4) — and only while they are
                        // still the words it was asked for, and not being re-typed.
                        const tr = trans[m.id] && trans[m.id].src === m.body && editId !== m.id ? trans[m.id] : null;
                        // Reply, Forward, the smile and the ⋮ show with the mouse — and stay while a picker or the
                        // ⋮ menu of this very message is open, or opening one would make it vanish under the hand.
                        // A phone has no mouse to bring them, so there a tap does (report 871f8cb5: a document just
                        // sent to the document-upload group could not be deleted in the Chat app — the ⋮ that Delete
                        // sits in never showed, and a tap on the document only saved it). A tap on the message or
                        // anywhere beside it shows them on that message, and a tap on another moves them there. A tap
                        // on something that does a thing of its own — a number in the words, a button, the file, the
                        // photo, the voice note — is left to do just that and nothing more.
                        const acts = hoverId === m.id || picking === m.id || menuId === m.id;
                        // A job's message in the Active Jobs group is answered by the job's lead resource alone, with Reply
                        // (report 8e70cd7c, opening #3): anybody else's reply would never reach the job, so they are offered
                        // none — a lock stands where Reply was and names who answers it.
                        const replyLock = !m.replyOnly || m.replyOnly.ids.includes(me.userId) ? ''
                          : m.replyOnly.name ? t('chat_reply_lead_only', { name: m.replyOnly.name }) : t('chat_reply_no_lead');
                        // Being changed (report 2fa9001f, EDIT_W): the bubble holds the box at twice its old width, with
                        // its own 11px padding and 1px edge either side round that, and is let out past its 78% to do
                        // it — but never past the thread, less the tick box and its gap while messages are ticked for a
                        // forward. Every other bubble keeps its 78% and no least width at all.
                        const editMin = editId !== m.id ? undefined
                          : `min(${(phone ? EDIT_W_PHONE : EDIT_W) + 24}px, ${pickMode ? '100% - 28px' : '100%'})`;
                        return (
                          <div key={m.id} onMouseEnter={() => setHoverId(m.id)} onMouseLeave={() => setHoverId(h => (h === m.id ? '' : h))}
                            onClick={e => { if (!(e.target as HTMLElement | null)?.closest?.('a, button, audio, textarea, [role="button"]')) setHoverId(m.id); }}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: own ? 'flex-end' : 'flex-start', maxWidth: '100%' }}>
                            {/* The bubble and, when the mouse is on it, the three things that can be done to it —
                                Reply, Forward and an emoji. The row is the full width of the thread so the bubble's
                                78% is of that, as it always was; the buttons sit in what is left of the row, on the
                                free side of the bubble. */}
                            <div style={{ display: 'flex', flexDirection: own ? 'row-reverse' : 'row', alignItems: 'center', gap: 4, width: '100%' }}>
                            {/* The words are black — every message's, mine and everybody else's, in a one-to-one and
                                in a group (reports c05bc176, b4806261, Calem: "Remove the user colors from the chat
                                window and instead change all text to black"). They were drawn in the writer's own
                                colour (report 32621dcf); that colour is now the bubble their name sits in at the
                                start of the line. A caption and a file's name take the black from here; the fill, the
                                edge and the time stay as they were. On a phone the words, a caption and a file's name
                                are all a pixel larger, from this one size (report 999f2fe7, PHONE_UP) — and on every
                                screen a pixel larger again (report d4dbcbe8, TEXT_UP): 13.5 on a desk where they were
                                12.5, and 14.5 on a phone. This is the size the WORDS are drawn at; an emoji among
                                them is drawn against it rather than at it, and a message that is nothing but emoji
                                far larger again (report ffe39a2e, EMOJI_EM) — the padding and the fill here are
                                untouched, and the bubble simply grows around the taller line. */}
                            {/* A message ticked for a forward wears the same accent ring a quote's press leaves on
                                the message it takes the reader to, so what is going is plain at a glance. */}
                            <div ref={el => { bubbles.current[m.id] = el; }}
                              style={{ maxWidth: editMin ? '100%' : '78%', minWidth: editMin, padding: '7px 11px', borderRadius: 12, border: '1px solid var(--border)', background: own ? 'var(--bg-active)' : 'var(--bg-surface)', fontSize: 12.5 + TEXT_UP + phoneUp, lineHeight: 1.55, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', boxShadow: flashId === m.id || ticked ? '0 0 0 2px var(--accent)' : undefined }}>
                              {/* Forwarded into this chat from another (report 96e28b88): who wrote the words
                                  first, above them. The copy itself is an ordinary message of whoever forwarded
                                  it, so their own bubble is at the start of the line as on anything else they
                                  send; this line says where it came from. A forward of a forward names the first
                                  writer, not the colleague who passed it on (server rootOf). */}
                              {m.fwdFrom && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, margin: '0 0 3px', fontSize: 10.5, fontStyle: 'italic', color: 'var(--text-tertiary)', whiteSpace: 'normal' }}>
                                  <i className="ti ti-corner-up-right" style={{ fontSize: 12, flexShrink: 0 }} aria-hidden />
                                  {t('chat_fwd_from', { name: m.fwdFrom })}
                                </div>
                              )}
                              {/* A message that answers another carries the quote of it above its own words: who wrote
                                  that one and its first words, with that person's colour down the side of the quote —
                                  the bar keeps it, the name is black like the rest (reports c05bc176, b4806261).
                                  Pressing the quote goes to the original and marks it for a moment; one further back
                                  than this page reaches shows its words and stays put, and one that has gone says so.
                                  The quoted words go through ChatWords as every other words here do, so a job or
                                  document number inside a quote is live too — those take their own press (each stops
                                  it going further), so pressing a number opens it and pressing anything else goes to
                                  the original. */}
                              {m.replyTo && (
                                <div role={quoted?.here ? 'button' : undefined} onClick={() => quoted?.here && goTo(m.replyTo)}
                                  title={quoted?.here ? t('chat_reply') : undefined}
                                  style={{ margin: '0 0 4px', padding: '3px 0 3px 7px', borderLeft: '3px solid', borderLeftColor: quoted ? quoted.color : 'var(--border-strong)', borderRadius: 3, whiteSpace: 'normal', cursor: quoted?.here ? 'pointer' : 'default' }}>
                                  <span style={{ display: 'block', fontSize: 10.5, fontWeight: 800, color: quoted ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                    {quoted ? quoted.name : t('chat_gone')}
                                  </span>
                                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {quoted ? <ChatWords text={quoted.text} jobs={jobNos} taxIds={taxNos} passports={passNos} onTaxId={setTaxOpen} onJob={onJob} mentions={mentions} inline /> : t('chat_original_gone')}
                                  </span>
                                </div>
                              )}
                              {/* Text is the words themselves; the other three kinds are the thing they carry, with
                                  whatever caption was sent under it. Either way the words go through ChatWords, so a
                                  job or document number in them is live (report 32621dcf).
                                  Who wrote it stands in their own bubble at the start of that line of words, on every
                                  message and in a one-to-one as much as in a group (reports c05bc176, b4806261,
                                  Calem: "add the user's bubble and color to the beginning of the line of text"). It
                                  takes the place of the coloured name a group used to put above the words, which only
                                  ever showed on somebody else's message. A photo, a voice note or a file sent with no
                                  caption has no line of words for the bubble to begin, so it stands above the thing
                                  itself — nothing sent is ever left without its sender. On a phone its name is a
                                  pixel larger, as the words are (report 999f2fe7), and on every screen a pixel larger
                                  again, with them (report d4dbcbe8, TEXT_UP). */}
                              {/* Being changed (report d5b1d1de): the words of the bubble become a box with the
                                  words as they were sent in it, and two buttons. The lines above it — who it was
                                  forwarded from, the message it answers — are left exactly as they are: an edit
                                  changes the words and nothing else about the message.
                                  Enter saves, Shift+Enter is a new line and Escape puts the words back as they
                                  were sent, which is how the typing box below already behaves.
                                  The box is as wide as the bubble lets it be (editMin) and as tall as its words
                                  (report 2fa9001f): the rows here are only the least it is — a line more than the
                                  words have, up to six — and the height past them is set as they are typed. */}
                              {editId === m.id ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, whiteSpace: 'normal' }}>
                                  <textarea ref={editBox} value={editText} autoFocus rows={Math.min(6, editText.split('\n').length + 1)}
                                    maxLength={MAX_BODY} aria-label={t('chat_edit')}
                                    onChange={e => setEditText(e.target.value)}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void saveEdit(m.id); }
                                    }}
                                    style={{ ...box, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }} />
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ flex: 1, minWidth: 0, fontSize: 10.5, color: 'var(--text-tertiary)' }}>{t('chat_edit_left', { n: minsLeft(m) })}</span>
                                    <button onClick={endEdit} style={{ ...btn, padding: '4px 8px', fontSize: 11.5 }}>{t('chat_edit_cancel')}</button>
                                    <button onClick={() => void saveEdit(m.id)} disabled={busy || !editText.trim()}
                                      style={{ ...btn, padding: '4px 8px', fontSize: 11.5, fontWeight: 700, border: 'none', background: 'var(--accent)', color: '#fff', opacity: busy || !editText.trim() ? 0.5 : 1, cursor: busy || !editText.trim() ? 'not-allowed' : 'pointer' }}>
                                      {t('chat_edit_save')}
                                    </button>
                                  </div>
                                </div>
                              ) : m.kind === 'text' ? (
                                <>
                                  <PersonBubble p={writer} name={m.senderName} up={TEXT_UP + phoneUp} style={{ marginRight: 5 }} />
                                  <ChatWords text={m.body} jobs={jobNos} taxIds={taxNos} passports={passNos} onTaxId={setTaxOpen} onJob={onJob} mentions={mentions} />
                                </>
                              ) : (
                                <>
                                  {!m.body && <div style={{ marginBottom: 3 }}><PersonBubble p={writer} name={m.senderName} up={TEXT_UP + phoneUp} /></div>}
                                  <ChatAttachment convId={open.id} m={m} />
                                  {m.body && (
                                    <div style={{ marginTop: 4 }}>
                                      <PersonBubble p={writer} name={m.senderName} up={TEXT_UP + phoneUp} style={{ marginRight: 5 }} />
                                      <ChatWords text={m.body} jobs={jobNos} taxIds={taxNos} passports={passNos} onTaxId={setTaxOpen} onJob={onJob} mentions={mentions} />
                                    </div>
                                  )}
                                </>
                              )}
                              {/* The words in another language (report 9b579ee4), for this reader alone: under a thin
                                  line, which language they were translated from, then the translation — through
                                  ChatWords like the words above it, so a job or document number in it is just as
                                  live — and an × to put it away. A turning wheel stands there while it is being made,
                                  and should it not come, the line says so. */}
                              {tr && (
                                <div style={{ marginTop: 6, paddingTop: 5, borderTop: '1px solid var(--border)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: tr.state === 'failed' ? '#B42318' : 'var(--text-tertiary)', whiteSpace: 'normal' }}>
                                    <i className={`ti ti-${tr.state === 'busy' ? 'loader' : 'language'}`} style={{ fontSize: 12, flexShrink: 0, animation: tr.state === 'busy' ? 'spin 1s linear infinite' : undefined }} aria-hidden />
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                      {tr.state === 'done' ? t('chat_translated_from', { lang: t(`chat_translate_to_${tr.from}`) })
                                        : tr.state === 'failed' ? t('chat_translate_failed') : ''}
                                    </span>
                                    <button onClick={() => hideTrans(m.id)} title={t('chat_translate_hide')} aria-label={t('chat_translate_hide')}
                                      style={{ ...act, width: 18, height: 18, borderRadius: 5 }}>
                                      <i className="ti ti-x" style={{ fontSize: 12 }} aria-hidden />
                                    </button>
                                  </div>
                                  {tr.state === 'done' && (
                                    <div style={{ marginTop: 2 }}>
                                      <ChatWords text={tr.text} jobs={jobNos} taxIds={taxNos} passports={passNos} onTaxId={setTaxOpen} onJob={onJob} mentions={mentions} />
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* A document attached in the document-upload group, whose copy went to the Drop
                                  Folder under the confirmed name (report 13fbee78). Quiet, under the thing itself,
                                  and there for every reader of the thread — so anybody can see the document was
                                  filed, and under which name, without opening the library. */}
                              {m.filedAs && (
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4, margin: '4px 0 0', fontSize: 10.5, fontStyle: 'italic', color: 'var(--text-tertiary)', whiteSpace: 'normal' }}>
                                  <i className="ti ti-folder-check" style={{ fontSize: 12, flexShrink: 0, marginTop: 1 }} aria-hidden />
                                  {t('chat_doc_filed', { name: m.filedAs })}
                                </div>
                              )}
                            </div>
                            {/* Answer this message, forward it into another chat, or put an emoji on it. They appear
                                with the mouse and are out of the way otherwise, so a thread at rest reads exactly
                                as it did. While messages are being ticked for a forward (report 96e28b88) the three
                                give way to this message's own tick box, which is always there to be pressed — the
                                bubble itself is left alone, so a photo still opens and a number in it is still a
                                link while the ticking is on. */}
                            {pickMode ? (
                              <button onClick={() => tickMsg(m.id)} aria-pressed={ticked}
                                title={t('chat_fwd_tick')} aria-label={t('chat_fwd_tick')}
                                style={{ ...act, color: ticked ? 'var(--accent)' : 'var(--text-tertiary)' }}>
                                <i className={`ti ti-${ticked ? 'square-check' : 'square'}`} style={{ fontSize: 16 }} aria-hidden />
                              </button>
                            ) : editId === m.id ? null : (
                            <span style={{ display: 'flex', gap: 2, flexShrink: 0, opacity: acts ? 1 : 0, pointerEvents: acts ? 'auto' : 'none', transition: 'opacity .12s' }}>
                              {replyLock ? (
                                <span role="img" title={replyLock} aria-label={replyLock} style={{ ...act, cursor: 'help' }}>
                                  <i className="ti ti-lock" style={{ fontSize: 14 }} aria-hidden />
                                </span>
                              ) : (
                                <button onClick={() => { setReplyTo(m.id); setPicking(''); typing.current?.focus(); }}
                                  title={t('chat_reply')} aria-label={t('chat_reply')} style={act}>
                                  <i className="ti ti-corner-up-left" style={{ fontSize: 14 }} aria-hidden />
                                </button>
                              )}
                              <button onClick={() => startFwd(m.id)} title={t('chat_fwd')} aria-label={t('chat_fwd')} style={act}>
                                <i className="ti ti-corner-up-right" style={{ fontSize: 14 }} aria-hidden />
                              </button>
                              <button data-emoji-open="1" onClick={() => setPicking(p => (p === m.id ? '' : m.id))}
                                title={t('chat_react')} aria-label={t('chat_react')}
                                style={{ ...act, fontSize: 13, lineHeight: 1, opacity: picking === m.id ? 1 : 0.75 }}>
                                <span aria-hidden>{SMILE}</span>
                              </button>
                              {/* The message's own ⋮ menu (report d5b1d1de). It is there only while there is
                                  something in it: Edit, on the reader's OWN typed message and only for the
                                  fifteen minutes the words may still be changed, with the minutes still left
                                  written on it — and beside it Delete, which takes the whole message back,
                                  whatever kind it is (report 2aafc95e). The last minute gone, the ⋮ goes too —
                                  the server would refuse a later change in any case, so nothing offers one.
                                  For an administrator it stays on every message instead, Delete alone of those two
                                  in it: they are held to no clock and need not have written the thing.
                                  Translate (report 9b579ee4) is in it on every message that has words, anybody's
                                  and of any age, so the ⋮ now stands beside most messages — and a phone reaches it
                                  through the same tap that shows Reply and Forward. */}
                              {(canTrans(m) || canEdit(m) || canDelete(m)) && (
                                <span data-msg-menu="1" style={{ position: 'relative', display: 'flex' }}>
                                  <button onClick={e => flipMenu(m.id, e.currentTarget)}
                                    title={t('chat_more')} aria-label={t('chat_more')} aria-expanded={menuId === m.id}
                                    style={{ ...act, color: menuId === m.id ? 'var(--accent)' : 'var(--text-tertiary)' }}>
                                    <i className="ti ti-dots-vertical" style={{ fontSize: 15 }} aria-hidden />
                                  </button>
                                  {menuId === m.id && (
                                    <div role="menu" style={{ position: 'absolute', ...(menuDown ? { top: '100%', marginTop: 4 } : { bottom: '100%', marginBottom: 4 }), left: own ? 0 : 'auto', right: own ? 'auto' : 0, zIndex: 3, minWidth: 150, padding: 4, borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-md)' }}>
                                      {/* Translate: a quiet heading and, under it, the two of English, Thai and
                                          Chinese the words are not already in — either one press away. What comes
                                          back is drawn under the words in the bubble. */}
                                      {canTrans(m) && (
                                        <>
                                          <div role="presentation" style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '5px 8px 3px', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>
                                            <i className="ti ti-language" style={{ fontSize: 14 }} aria-hidden />
                                            {t('chat_translate')}
                                          </div>
                                          {CHAT_LANGS.filter(l => l !== langOfWords(m.body)).map(l => (
                                            <button key={l} role="menuitem" onClick={() => void translate(m, l)}
                                              aria-label={`${t('chat_translate')}: ${t(`chat_translate_to_${l}`)}`}
                                              style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '6px 8px 6px 29px', borderRadius: 7, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 12.5, color: 'var(--text-primary)' }}>
                                              {t(`chat_translate_to_${l}`)}
                                            </button>
                                          ))}
                                          {(canEdit(m) || canDelete(m)) && <div role="separator" style={{ height: 1, margin: '4px 6px', background: 'var(--border)' }} />}
                                        </>
                                      )}
                                      {canEdit(m) && (
                                        <button role="menuitem" onClick={() => startEdit(m)}
                                          style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', padding: '6px 8px', borderRadius: 7, border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontSize: 12.5, color: 'var(--text-primary)' }}>
                                          <i className="ti ti-pencil" style={{ fontSize: 14, color: 'var(--text-tertiary)' }} aria-hidden />
                                          <span style={{ flex: 1, minWidth: 0 }}>{t('chat_edit')}</span>
                                          <span style={{ flexShrink: 0, fontSize: 11, color: 'var(--text-tertiary)' }}>{t('chat_edit_left', { n: minsLeft(m) })}</span>
                                        </button>
                                      )}
                                      {/* …and Delete, which takes the whole bubble back — a file, a photo and a
                                          voice note included, which is what it was asked for (report 2aafc95e).
                                          Red, because nothing undoes it, and it asks before it goes. Both rows
                                          run out together, so the minutes left are written once: on Edit where
                                          there is one, and here on the kinds Edit will not touch — and on
                                          neither when it is an admin taking the message back, because nothing
                                          is counting down on that. */}
                                      {canDelete(m) && (
                                        <button role="menuitem" onClick={() => void delMsg(m)} disabled={busy}
                                          style={{ display: 'flex', alignItems: 'center', gap: 7, width: '100%', padding: '6px 8px', borderRadius: 7, border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', textAlign: 'left', fontSize: 12.5, color: '#E24B4A', opacity: busy ? 0.5 : 1 }}>
                                          <i className="ti ti-trash" style={{ fontSize: 14 }} aria-hidden />
                                          <span style={{ flex: 1, minWidth: 0 }}>{t('chat_delete')}</span>
                                          {!canEdit(m) && !adminDelete(m) && (
                                            <span style={{ flexShrink: 0, fontSize: 11, color: 'var(--text-tertiary)' }}>{t('chat_edit_left', { n: minsLeft(m) })}</span>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </span>
                              )}
                            </span>
                            )}
                            </div>
                            {/* The emojis people have put on it, under the bubble: each one, how many are on it, and
                                mine marked. Pressing one puts me on it, or takes me back off. The emoji itself is
                                15% larger than it was (report ffe39a2e, CHIP_EMOJI); the count beside it, the pill
                                round them and its height are untouched. */}
                            {chips.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, maxWidth: '78%', margin: '3px 3px 0' }}>
                                {chips.map(([e, ids]) => {
                                  const mineToo = ids.includes(me.userId);
                                  return (
                                    <button key={e} onClick={() => react(m.id, e)}
                                      title={ids.map(id => who(open.people.find(p => p.id === id))).filter(Boolean).join(' · ')}
                                      style={{ display: 'flex', alignItems: 'center', gap: 3, height: 20, padding: '0 6px', borderRadius: 999, border: '1px solid', borderColor: mineToo ? 'var(--accent)' : 'var(--border-strong)', background: mineToo ? 'var(--accent-bg)' : 'var(--bg-surface)', cursor: 'pointer', fontSize: 11, lineHeight: 1, color: mineToo ? 'var(--accent-text)' : 'var(--text-secondary)' }}>
                                      <span style={{ fontSize: CHIP_EMOJI }}>{e}</span>
                                      <span style={{ fontWeight: 700 }}>{ids.length}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            {/* Every time here is the office's own clock, day first, to the minute — and beside it,
                                on a message whose sender changed its words afterwards, that it was edited (report
                                d5b1d1de). It shows for EVERY reader, not only for the person who changed it, and
                                the time it was changed is on it.
                                At the end of that same line, on the reader's OWN message in a one-to-one chat, the
                                one grey tick or two blue ones (report 29486dfd): sent, and read by the person on the
                                other side. A group's messages have no tick — many readers, no single answer — and
                                neither has anything somebody else wrote. */}
                            <div title={m.editedAt > 0 ? t('chat_edited_at', { when: fmtDateTime(m.editedAt) }) : undefined}
                              style={{ fontSize: 10.5, color: 'var(--text-tertiary)', margin: '2px 3px 0' }}>
                              {fmtDateTime(m.createdAt)}
                              {m.editedAt > 0 && ` · ${t('chat_edited')}`}
                              {own && open.kind === 'direct' && <ChatTicks read={m.read} style={{ marginLeft: 4 }} />}
                            </div>
                          </div>
                        );
                      })}
                      <div ref={foot} />
                      </div>
                    </div>
                    {/* The foot of the thread: what is being answered, the pasted pictures, the pickers, the
                        formatting row, and the typing box with its three buttons. On a phone it also keeps itself
                        clear of the strip at the very bottom of the screen the home bar sits on — env(safe-area-
                        inset-bottom) is nothing at all on a screen that has no such strip (report 8c5f3514). In the
                        app it does so at any width too: its viewport reaches the screen's edges (report ec0c5a76),
                        and a phone turned on its side is wider than a phone but still has the home bar. */}
                    <div style={{ position: 'relative', padding: phone || app ? '10px 12px calc(10px + env(safe-area-inset-bottom))' : '10px 12px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
                      {/* The messages ticked for a forward (report 96e28b88): how many there are, how to add more,
                          the button that opens the picker of chats again, and an × to forward nothing after all.
                          It stands in the same strip the message being answered stands in, above it, and goes as
                          the forward lands or is given up. */}
                      {pickMode && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px', padding: '6px 9px', borderRadius: 9, borderLeft: '3px solid var(--accent)', background: 'var(--c-gray-100)', minWidth: 0 }}>
                          <i className="ti ti-corner-up-right" style={{ fontSize: 15, flexShrink: 0, color: 'var(--text-tertiary)' }} aria-hidden />
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ display: 'block', fontSize: 11, fontWeight: 800, color: 'var(--text-primary)' }}>{t('chat_fwd_n', { n: picks.length })}</span>
                            <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('chat_fwd_pick')}</span>
                          </span>
                          <button onClick={() => setFwdOpen(true)} disabled={busy || !picks.length} title={t('chat_fwd_go')}
                            style={{ ...btn, flexShrink: 0, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, opacity: busy || !picks.length ? 0.5 : 1, cursor: busy || !picks.length ? 'not-allowed' : 'pointer' }}>
                            <i className="ti ti-corner-up-right" style={{ fontSize: 14 }} aria-hidden />{t('chat_fwd_go')}
                          </button>
                          <button onClick={endPick} title={t('chat_fwd_cancel')} aria-label={t('chat_fwd_cancel')} style={act}>
                            <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden />
                          </button>
                        </div>
                      )}
                      {/* The message being answered, above the typing box: who wrote it, its first words, and an × to
                          answer nobody after all — their colour down the side of the strip, the name itself black
                          (reports c05bc176, b4806261). The words are plain text here, so their formatting markers
                          come off as the chat list's do (plainWords; report cbb0dc7a). It goes as the message is
                          sent. While the microphone is on there is no strip to show — the recording has the row — but
                          the message it answers is remembered and goes up with the voice note. */}
                      {replyTo && !rec && (() => {
                        const q = quoteOf(replyTo);
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px', padding: '6px 9px', borderRadius: 9, borderLeft: '3px solid', borderLeftColor: q ? q.color : 'var(--border-strong)', background: 'var(--c-gray-100)', minWidth: 0 }}>
                            <i className="ti ti-corner-up-left" style={{ fontSize: 15, flexShrink: 0, color: 'var(--text-tertiary)' }} aria-hidden />
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', fontSize: 11, fontWeight: 800, color: q ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                {t('chat_replying_to', { name: q ? q.name : t('chat_gone') })}
                              </span>
                              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {q ? plainWords(q.text) : t('chat_original_gone')}
                              </span>
                            </span>
                            <button onClick={() => setReplyTo('')} title={t('chat_cancel_reply')} aria-label={t('chat_cancel_reply')} style={act}>
                              <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden />
                            </button>
                          </div>
                        );
                      })()}
                      {/* The pictures pasted in and not yet posted, in the same place and the same strip as the
                          message being answered, under the quote when there is one: one row of thumbnails, each
                          with an × to drop it and pressable to be seen large (PhotoBig, the very overlay a photo
                          already in the chat opens). They go as Send posts them, and Escape drops the lot.
                          A picture picked with the paperclip waits here as well, and each one that can be cropped
                          has Crop under its × (report 1e817873) — dead while a send is going up, which posts the
                          picture as it stood when Send was pressed. */}
                      {pasted.length > 0 && (
                        <div style={{ margin: '0 0 8px', padding: '6px 9px', borderRadius: 9, borderLeft: '3px solid var(--accent)', background: 'var(--c-gray-100)', minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', marginBottom: 5 }}>
                            <i className="ti ti-photo" style={{ fontSize: 14, flexShrink: 0 }} aria-hidden />
                            {t('chat_pasted')}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflowX: 'auto' }}>
                            {pasted.map(s => (
                              <div key={s.id} style={{ position: 'relative', flexShrink: 0 }}>
                                <button onClick={() => setBigShot(s.id)} title={t('chat_preview')}
                                  style={{ display: 'block', width: SHOT_THUMB, height: SHOT_THUMB, padding: 0, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', overflow: 'hidden', cursor: 'zoom-in' }}>
                                  <img src={s.url} alt={s.name} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }} />
                                </button>
                                <button onClick={() => dropShot(s.id)} title={t('chat_pasted_drop')} aria-label={t('chat_pasted_drop')}
                                  style={{ ...SHOT_DOT, top: 3 }}>
                                  <i className="ti ti-x" aria-hidden />
                                </button>
                                {canCrop(s.blob.type, s.name) && (
                                  <button onClick={() => setCropShot(s.id)} disabled={busy} title={t('chat_crop')} aria-label={t('chat_crop')}
                                    style={{ ...SHOT_DOT, bottom: 3, opacity: busy ? 0.5 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
                                    <i className="ti ti-crop" aria-hidden />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* The document waiting to be named, in the document-upload group (report 13fbee78): the file
                          picked, the name the AI suggests for it in four boxes to be checked or changed (report
                          595aad91), and Confirm — which sends the message as any other attachment is sent AND files
                          a copy into the Drop Folder under the name those boxes make. It stands in the same strip,
                          in the same place, as the pasted pictures above. The × leaves it behind; nothing has been
                          sent or filed until Confirm. */}
                      {docHold && (
                        <div style={{ margin: '0 0 8px', padding: '6px 9px', borderRadius: 9, borderLeft: '3px solid var(--accent)', background: 'var(--c-gray-100)', minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', marginBottom: 5 }}>
                            <i className="ti ti-file-upload" style={{ fontSize: 14, flexShrink: 0 }} aria-hidden />
                            <span style={{ flex: 1, minWidth: 0 }}>{t('chat_doc_title')}</span>
                            <button onClick={dropDoc} title={t('chat_doc_drop')} aria-label={t('chat_doc_drop')} style={act}>
                              <i className="ti ti-x" style={{ fontSize: 14 }} aria-hidden />
                            </button>
                          </div>
                          {/* The file's name, and "cropped" beside it once Crop has cut it down (report 1e817873). */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-tertiary)', marginBottom: 5, minWidth: 0 }}>
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{docHold.fileName}</span>
                            {docHold.cropped && (
                              <span style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: 700, color: 'var(--accent-text)' }}>
                                <i className="ti ti-crop" style={{ fontSize: 12 }} aria-hidden />{t('chat_crop_done')}
                              </span>
                            )}
                          </div>
                          {docReading ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-secondary)' }}>
                              <i className="ti ti-loader" style={{ fontSize: 13, animation: 'spin 1s linear infinite' }} aria-hidden />
                              {t('chat_doc_reading')}
                            </div>
                          ) : (
                            <>
                              {/* The name as its four parts (report 595aad91): the document's type, the client in
                                  brackets, their company when there is one, and the date. Two to a row here and one
                                  to a row on a phone, where the thread is the whole screen's width. */}
                              <div style={{ display: 'grid', gridTemplateColumns: phone ? '1fr' : '1fr 1fr', gap: 6, marginBottom: 5 }}>
                                {docTypeBox()}
                                {docBox('person', t('chat_doc_person'), true, docParts.person, t('ds_wiz_person_ph'),
                                  v => setDocParts(p => ({ ...p, person: v })))}
                                {docCompanyBox()}
                                {docBox('date', t('chat_doc_date'), true, docParts.date, todayISO(),
                                  v => setDocParts(p => ({ ...p, date: v })))}
                              </div>
                              {/* An administrator's Simulation check (report 972661d6, 3rd opening), once the read has
                                  landed — so a test is never a document a failed read sends on to the group. Ticked,
                                  Confirm reads "Confirm test" and records how the read did instead (recordDocSim). */}
                              {simOk && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 5 }}>
                                  <label title={t('chat_sim_check_tip')} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', color: sim ? 'var(--accent)' : 'var(--text-secondary)' }}>
                                    <input type="checkbox" checked={simOn} disabled={busy} onChange={() => { setSimOn(v => !v); setDocNote(''); }} />
                                    {t('ds_sim_check')}
                                  </label>
                                  {simLink}
                                </div>
                              )}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ flex: 1, minWidth: 0, fontSize: 11, lineHeight: 1.45, color: docNote ? '#B45309' : 'var(--text-tertiary)', whiteSpace: 'normal' }}>
                                  {docNote || t(sim ? 'chat_sim_hint' : 'chat_doc_check')}
                                </span>
                                {/* Crop, for a photo, before Confirm (report 1e817873): the cut-out is what Confirm sends
                                    and files, under the name the boxes make. A PDF has no Crop. */}
                                {canCrop(docHold.blob.type, docHold.fileName) && (
                                  <button onClick={() => setDocCrop(true)} disabled={busy} title={t('chat_crop')}
                                    style={{ ...btn, flexShrink: 0, opacity: busy ? 0.5 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
                                    <i className="ti ti-crop" style={{ fontSize: 14 }} aria-hidden />{t('chat_crop')}
                                  </button>
                                )}
                                {/* Dead until the three required boxes are filled — the company's may stay empty. */}
                                <button onClick={() => void confirmDoc()} disabled={busy || !docPartsReady(docParts)}
                                  style={{ ...btn, flexShrink: 0, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, opacity: busy || !docPartsReady(docParts) ? 0.5 : 1, cursor: busy || !docPartsReady(docParts) ? 'not-allowed' : 'pointer' }}>
                                  <i className="ti ti-check" style={{ fontSize: 14 }} aria-hidden />{sim ? t(busy ? 'ds_sim_recording' : 'ds_sim_confirm') : t('chat_doc_confirm')}
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      {/* A test just recorded (report 972661d6): the box has cleared, nothing went to the group or the
                          Drop Folder, and this says so, with the Results beside it. The next document picked, or
                          another chat opened, takes it away. */}
                      {simOk && !docHold && simNote && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px', padding: '6px 9px', borderRadius: 9, borderLeft: '3px solid #15803D', background: 'var(--c-gray-100)', minWidth: 0 }}>
                          <i className="ti ti-circle-check" style={{ fontSize: 15, flexShrink: 0, color: '#15803D' }} aria-hidden />
                          <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, fontWeight: 700, color: '#15803D' }}>{simNote}</span>
                          {simLink}
                        </div>
                      )}
                      {/* One of those pictures, shown large over the Chat window — the same as a photo already sent,
                          less Save: it is on this computer already and on no other. Crop instead (report 1e817873),
                          so a picture opened to be looked at can be cut down there and then. */}
                      {bigShot && (() => {
                        const s = pasted.find(x => x.id === bigShot);
                        return s ? <PhotoBig url={s.url} name={s.name} onClose={() => setBigShot('')}
                          onCrop={canCrop(s.blob.type, s.name) && !busy ? () => setCropShot(s.id) : undefined} /> : null;
                      })()}
                      {/* The crop window (report 1e817873), over the large picture when it was opened from there. Keep
                          puts the cut-out in the place of the picture it was cut from (reshoot; cropDoc for the
                          document-upload group's photo); Cancel, × and Esc leave the picture as it was. */}
                      {cropShot && (() => {
                        const s = pasted.find(x => x.id === cropShot);
                        return s ? (
                          <ImageCrop key={s.id} blob={s.blob} name={s.name} onCancel={() => setCropShot('')}
                            onCrop={(cut, name) => { reshoot(s.id, cut, name); setCropShot(''); }} />
                        ) : null;
                      })()}
                      {docCrop && docHold && (
                        <ImageCrop blob={docHold.blob} name={docHold.fileName} onCancel={() => setDocCrop(false)} onCrop={cropDoc} />
                      )}
                      {/* The emoji picker, over the thread just above the typing box — the one picker, whether it was
                          opened by the smile here (its emoji goes into what is being typed) or by the smile on a
                          bubble (it goes onto that message). Escape or a press outside shuts it, and while the
                          microphone is on it is not offered at all, as the typing box is not. */}
                      {picking && !rec && (
                        <div ref={pickRef} role="dialog" aria-label={t('chat_emoji')}
                          style={{ position: 'absolute', left: 12, bottom: 'calc(100% + 6px)', zIndex: 2, width: 304, padding: 7, borderRadius: 12, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-md)', display: 'grid', gridTemplateColumns: `repeat(${EMOJI_COLS}, 1fr)`, gap: 2, boxSizing: 'border-box' }}>
                          {EMOJI.map(e => (
                            <button key={e} onClick={() => (picking === 'draft' ? putEmoji(e) : react(picking, e))} title={e}
                              style={{ height: 32, padding: 0, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>{e}</button>
                          ))}
                        </div>
                      )}
                      {/* The staff the '@' being typed offers (report 67084d4c), in the same place the emoji picker
                          stands: each in their own bubble, the one Enter would put in marked. A press keeps the
                          mouse off the box itself (mousedown is stopped), so the cursor stays where the name has to
                          go and the box keeps the focus; a press anywhere else leaves the box, and the list goes
                          with it. It is tall enough for the eight it offers, so there is nothing in it to scroll. */}
                      {atOpen && !rec && (
                        <div role="listbox" aria-label={t('chat_mention')}
                          style={{ position: 'absolute', left: 12, bottom: 'calc(100% + 6px)', zIndex: 3, width: 262, maxHeight: 300, overflowY: 'auto', padding: 6, borderRadius: 12, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-md)', boxSizing: 'border-box' }}>
                          <div style={{ ...heading, padding: '2px 4px 5px' }}>{t('chat_mention')}</div>
                          {atMatches.map((p, i) => (
                            <button key={p.id} role="option" aria-selected={i === atPick}
                              onMouseDown={e => e.preventDefault()} onClick={() => putMention(p)} onMouseEnter={() => setAtPick(i)}
                              style={{ ...row(i === atPick), marginBottom: 2 }}>
                              <i className="ti ti-at" style={{ fontSize: 14, flexShrink: 0, color: 'var(--text-tertiary)' }} aria-hidden />
                              <PersonBubble p={p} />
                            </button>
                          ))}
                        </div>
                      )}
                      {/* ── How a message may be written (report 67084d4c) ──────────────────────────────────────
                          Bold, Underline, Strikethrough, Italic, bullet points and numbered points: each button
                          writes its markers round what is selected in the box below (wrapMark, listMark), and the
                          thread draws them. The mouse is kept off the box, so what was selected stays selected.
                          While the microphone is on there is no box to write in, so there is no toolbar either. */}
                      {!rec && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 2, margin: '0 0 6px' }}>
                          {TOOLS.map(x => (
                            <button key={x.icon} onMouseDown={e => e.preventDefault()}
                              onClick={() => (x.mark ? wrapMark(x.mark) : listMark(x.list || 'ul'))}
                              title={t(x.key)} aria-label={t(x.key)} style={tool}>
                              <i className={`ti ti-${x.icon}`} style={{ fontSize: 15 }} aria-hidden />
                            </button>
                          ))}
                        </div>
                      )}
                      {/* One row on a screen with the room for it — the three buttons, the typing box and Send —
                          and on a phone two, the buttons above the box, so the box is the width of the screen
                          instead of the hundred-odd pixels left over beside them (report 8c5f3514). The parts
                          themselves, and their sizes, are the same either way. */}
                      <div style={{ display: 'flex', flexDirection: phone ? 'column' : 'row', alignItems: phone ? 'stretch' : 'flex-end', gap: 8 }}>
                      {/* The box the paperclip presses. Anything may be picked — a photo among it — and the server
                          decides which of the three kinds it becomes from what it really is. On a phone that is the
                          camera, the photo roll and the files, which is what the phone itself offers for it. */}
                      <input ref={picker} type="file" onChange={onPicked} style={{ display: 'none' }} tabIndex={-1} aria-hidden />
                      {rec ? (
                        // While the microphone is on, the typing box gives way to the recording: throw it away on
                        // the left, how long it has been running in the middle, send it on the right. All three stay
                        // on one row, a phone included — there is no box here for a second row to make room for.
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                          <button onClick={() => stopRec(false)} title={t('chat_discard')} aria-label={t('chat_discard')}
                            style={{ ...icon, color: '#E24B4A' }}>
                            <i className="ti ti-trash" style={{ fontSize: 16 }} aria-hidden />
                          </button>
                          <div style={{ ...box, flex: 1, height: 42, display: 'flex', alignItems: 'center', gap: 8, width: 'auto' }}>
                            <span style={{ width: 9, height: 9, borderRadius: 999, background: '#DC2626', flexShrink: 0 }} aria-hidden />
                            <span style={{ fontWeight: 700 }}>{t('chat_recording')}</span>
                            <span style={{ color: 'var(--text-tertiary)' }}>{mmss(recSecs)}</span>
                          </div>
                          <button onClick={() => stopRec(true)} title={t('chat_stop_send')} aria-label={t('chat_stop_send')}
                            style={{ ...icon, border: 'none', background: 'var(--accent)', color: '#fff' }}>
                            <i className="ti ti-send" style={{ fontSize: 15 }} aria-hidden />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <button onClick={() => picker.current?.click()} disabled={busy} title={t('chat_attach')} aria-label={t('chat_attach')}
                            style={{ ...icon, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1 }}>
                            <i className={`ti ti-${busy ? 'loader' : 'paperclip'}`} style={{ fontSize: 16, animation: busy ? 'spin 1s linear infinite' : undefined }} aria-hidden />
                          </button>
                          {/* Beside the paperclip: the emojis, for the message being typed. */}
                          <button data-emoji-open="1" onClick={() => setPicking(p => (p === 'draft' ? '' : 'draft'))}
                            aria-pressed={picking === 'draft'} title={t('chat_emoji')} aria-label={t('chat_emoji')}
                            style={{ ...icon, fontSize: 17, lineHeight: 1, borderColor: picking === 'draft' ? 'var(--accent)' : 'var(--border-strong)' }}>
                            <span aria-hidden>{SMILE}</span>
                          </button>
                          <button onClick={startRec} disabled={busy} title={t('chat_record')} aria-label={t('chat_record')}
                            style={{ ...icon, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1 }}>
                            <i className="ti ti-microphone" style={{ fontSize: 16 }} aria-hidden />
                          </button>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flex: 1, minWidth: 0 }}>
                          {/* Enter sends; Shift+Enter starts a new line, as every other box here does. A picture
                              pasted in here waits in the strip above (takeShots) and what is typed becomes its
                              caption; pasted words fall in as ever. An '@' typed here offers the staff list above
                              the box, and ⌘/Ctrl+B, U and I write the formatting markers (onTypeKey).
                              What is typed is a pixel larger than the window's other boxes, as the words in the
                              thread are (report d4dbcbe8, TEXT_UP): 13.5, and 17 on a phone — still past the 16 an
                              iPhone needs not to zoom (see box). */}
                          <textarea ref={typing} value={draft} onChange={e => onDraft(e.target.value, e.target.selectionStart)} rows={2} maxLength={MAX_BODY} placeholder={t('chat_write')}
                            onKeyDown={onTypeKey} onBlur={() => setAtList(null)}
                            onPaste={e => { if (takeShots(e.clipboardData)) e.preventDefault(); }}
                            style={{ ...box, flex: 1, minWidth: 0, minHeight: 42, maxHeight: 120, resize: 'vertical', fontSize: (phone ? 16 : 12.5) + TEXT_UP, lineHeight: 1.5, fontFamily: 'inherit' }} />
                          {/* Send is the paper plane and nothing else (report c59f9828) — the same square as the
                              paperclip beside it, in the accent, with the word it used to print kept as its tooltip. */}
                          <button onClick={send} disabled={sendOff} title={t('chat_send')} aria-label={t('chat_send')}
                            style={{ ...icon, border: 'none', background: 'var(--accent)', color: '#fff', opacity: sendOff ? 0.5 : 1, cursor: sendOff ? 'not-allowed' : 'pointer' }}>
                            <i className={`ti ti-${busy ? 'loader' : 'send'}`} style={{ fontSize: 15 }} aria-hidden />
                          </button>
                          </div>
                        </>
                      )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            {err && <div role="alert" style={{ padding: '7px 14px', fontSize: 12, color: '#B42318', borderTop: '1px solid var(--border)' }}>{err}</div>}
            {/* …and what has just worked, in the same place for a few seconds: a forward's copies land in chats
                the reader is not looking at, so this is what says it happened (report 96e28b88). */}
            {!err && okMsg && (
              <div style={{ padding: '7px 14px', fontSize: 12, color: 'var(--accent-text)', borderTop: '1px solid var(--border)' }}>{okMsg}</div>
            )}
          </section>
          )}
        </div>
        {/* A tax ID pressed in a message: that client's company documents, over this whole window (TaxDocs). It is
            a child of the window, so a press on its own dark closes it and leaves the chat where it was. */}
        {taxOpen && <TaxDocs taxId={taxOpen} onClose={closeTax} />}
        {/* Where the ticked messages are going (report 96e28b88): the chats this person is in, over the whole
            window. Closing it leaves them ticked, so more can be added and the picker opened again. */}
        {fwdOpen && (
          <ForwardTo convs={convs || []} count={picks.length} busy={busy} err={err}
            onSend={doForward} onClose={closeFwd} />
        )}
        {/* What to tap to install, where the browser offered the page no dialog of its own (report 8c5f3514). */}
        {howTo && <InstallHow onClose={() => setHowTo(false)} />}
        {/* The simulation checks' Results (report 972661d6): the wizard's own list, the chat's tests in it with the rest.
            A child of this window, so it stands over it; Escape and a press on its dark close it. */}
        {simList && simOk && <SimResults onClose={() => setSimList(false)} />}
      </div>
    </div>
  );
}

/** The chat button for the main Portal's top bar (report 32621dcf): a square beside the rocket, in the same shape, with
 *  the red unread count on its top-right corner and the pop-up it opens. It is no tab and changes no page. */
export function ChatTopButton() {
  const { t } = useTranslation();
  const { unread, refresh } = useChatUnread();
  const [open, setOpen] = useState(false);
  // Reduce ⤡ on the full-screen chat page hands the window back to the tab it was expanded from (report 14dc81ac):
  // where that tab is this one, it opens again here, over the page the reader left it on.
  useChatBack(() => setOpen(true));
  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button onClick={() => setOpen(true)} title={t('chat_btn_tip')} aria-label={t('chat_title')}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, padding: 0, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', cursor: 'pointer', flexShrink: 0 }}>
        <i className="ti ti-message-circle" style={{ fontSize: 17, lineHeight: 1, color: 'var(--accent)' }} aria-hidden />
      </button>
      {unread > 0 && <ChatCount n={unread} style={{ position: 'absolute', top: -7, right: -6, zIndex: 1, pointerEvents: 'none' }} />}
      {open && <ChatPanel onClose={() => { setOpen(false); refresh(); }} />}
    </div>
  );
}

/** The unread count, the same red circle the top menu's counts use (Portal: badgeSpan). `scale` grows it in the chat
 *  list's widened column (report 8e638d38), as PersonBubble's; 1 everywhere else. */
export function ChatCount({ n, scale = 1, style }: { n: number; scale?: number; style?: React.CSSProperties }) {
  return (
    <span style={{ minWidth: 20 * scale, height: 20 * scale, padding: `0 ${3 * scale}px`, borderRadius: 999, background: '#DC2626', color: '#fff', fontSize: 11.5 * scale, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, boxSizing: 'border-box', flexShrink: 0, boxShadow: '0 0 0 1.5px var(--bg-surface)', ...style }}>
      <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{n > 99 ? '99+' : n}</span>
    </span>
  );
}
